<script lang="ts">
  import { tick, onMount } from "svelte";
  import { fade, fly } from "svelte/transition";
  import {
    figmaRenderNodeStyleAtScale,
  } from "./lib/figma-preview";
  import {
    clampNumber,
    cloneAssets,
    createArtFlowDocument,
    createDefaultProjectConfig,
    createDefaultStageStates,
    createFinalOverlay,
    createProjectConfigFromPreset,
    createReferenceSet,
    createWireframeDocument,
    buildReferenceExport,
    defaultOutputRoot,
    defaultStatusGroupFor,
    defaultNineSliceFor,
    EXTRACTION_TARGETS,
    normalizeArtFlowDocument,
    normalizeAsset,
    normalizeFinalOverlay,
    normalizeReferenceSet,
    normalizeScreenStatus,
    normalizeStatusGroup,
    projectPresets,
    sanitizeId,
    screenPrefix,
    uniqueIdInSet,
    zeroInsets,
    type ArtAsset,
    type AssetExtractionTarget,
    type AssetType,
    type FigmaInfo,
    type FinalOverlay,
    type FinalOverlayFit,
    type NineSlice,
    type ProjectConfig,
    type ProjectPreset,
    type Rect,
    type ReferenceImage,
    type ReferenceSet,
    type ScreenStatus,
    type SemanticTree,
    type SemanticTreeNode,
    type StageState,
    type StatusGroup,
    type TextExportPolicy,
    type UiScreen,
    type WireframeDocument,
    type WorkflowStageId,
  } from "./lib/document";

  type ModalMode = "none" | "addAsset" | "deleteAsset" | "deleteScreen" | "fileConflict" | "gameSync" | "referenceAssets" | "help" | "buildReport" | "importMenu" | "figmaImport" | "projectSettings" | "memberAdmin" | "extractRefine";
  type BuildIssueSeverity = "error" | "warning";
  type ResizeHandle = "n" | "e" | "s" | "w" | "ne" | "nw" | "se" | "sw";
  type MarqueeAssetType = "panel" | "button" | "icon";
  type ImageGenerationMode = "screen-mockup" | "button-atlas" | "status-atlas";
  type CanvasViewMode = "wireframe" | "final" | "overlay";

  interface PipelineStage {
    id: WorkflowStageId;
    title: string;
    desc: string;
    state: StageState;
    output: string;
  }

  interface BuildIssue {
    severity: BuildIssueSeverity;
    screenId: string;
    screenName: string;
    assetId: string;
    field: string;
    message: string;
  }

  interface FigmaFrame {
    id: string;
    name: string;
    type: string;
    page: string;
    depth: number;
    parentId?: string;
    parentIds?: string[];
    path?: string[];
    childCount: number;
    text?: string;
    style?: {
      fill?: string;
      stroke?: string;
      strokeWeight?: number;
      radius?: number;
      opacity?: number;
      fontFamily?: string;
      fontSize?: number;
      fontWeight?: number;
      textAlign?: string;
      textVerticalAlign?: string;
      lineHeight?: number;
      gradient?: string | { css?: string };
      gradients?: Array<{ css?: string }>;
      imageRef?: string;
      image?: { imageRef?: string; scaleMode?: string };
      scaleMode?: string;
      effects?: Array<{ type?: string; radius?: number; spread?: number; color?: string; offset?: { x: number; y: number } }>;
      effectsCss?: string;
      rotation?: number;
      clipsContent?: boolean;
      blendMode?: string;
    };
    imageRef?: string;
    scaleMode?: string;
    gradient?: string | { css?: string };
    gradients?: Array<{ css?: string }>;
    effects?: Array<{ type?: string; radius?: number; spread?: number; color?: string; offset?: { x: number; y: number } }>;
    rotation?: number;
    clipsContent?: boolean;
    blendMode?: string;
    renderUrl?: string;
    box: { x: number; y: number; w: number; h: number } | null;
  }

  interface FigmaParseResult {
    ok: boolean;
    name?: string;
    lastModified?: string;
    pages?: Array<{ id: string; name: string; childCount: number }>;
    frames?: FigmaFrame[];
    error?: string;
  }

  interface FigmaLayerTreeRow {
    frame: FigmaFrame;
    depth: number;
  }

  interface FigmaImportSnapshot {
    version: 1;
    savedAt: string;
    url: string;
    parseWholeFile: boolean;
    showParsedOverlay: boolean;
    result: FigmaParseResult | null;
    frames: FigmaFrame[];
    selectedFrameIds: string[];
    previewFrameId: string;
    renderImages: Record<string, string>;
    renderError: string;
    canvasZoom: number;
    canvasPan: { x: number; y: number };
  }

  interface AiProvider {
    id: string;
    label: string;
    available: boolean;
    command: string;
    script?: string;
    env?: string[];
    role: string;
  }

  interface AiProviderResult {
    ok: boolean;
    wireframe: AiProvider[];
    image: AiProvider[];
    error?: string;
  }

  type ApiTaskKey = "wireframe" | "image" | "export";

  interface DryRunEntry {
    path: string;
    kind: string;
    action: "write" | "overwrite" | "skip";
    exists: boolean;
    size?: number;
    reason?: string;
    assetId?: string;
  }

  interface DryRunPlan {
    outputRoot: string;
    entries: DryRunEntry[];
    summary: {
      total: number;
      write: number;
      overwrite: number;
      skip: number;
    };
  }

  interface GameSyncEntry {
    path: string;
    targetPath?: string;
    kind: string;
    action: "write" | "overwrite" | "skip" | "written";
    exists?: boolean;
    size?: number;
    reason?: string;
    assetId?: string;
  }

  interface GameSyncPlan {
    stagingRoot?: string;
    gameRoot?: string;
    outputRoot: string;
    entries: GameSyncEntry[];
    summary: {
      total: number;
      write?: number;
      overwrite?: number;
      skip?: number;
      written?: number;
      skipped?: number;
    };
  }

  interface ApiTaskResponse {
    ok: boolean;
    files?: string[];
    command?: string;
    message?: string;
    error?: string;
    prompt?: string;
    provider?: string;
    model?: string;
    screen?: Partial<UiScreen>;
    canvas?: { width?: number; height?: number };
    nodes?: any[];
    assets?: any[];
    plan?: DryRunPlan;
    geometryReport?: any;
  }

  // AI 元素提取(/api/extract/*)的单元素结果。
  interface ExtractEntry {
    assetId: string;
    ok: boolean;
    error?: string;
    cropMode?: string;
    confidence?: number;
    pipeline?: string;
    reason?: string;
    detectedRect?: { left: number; top: number; width: number; height: number };
    target?: string;
    textContent?: string;
    cutoutDataUrl?: string;
    maskDataUrl?: string;
  }

  // C：提取目标类型(前景整体/本体去文字/仅文字层/背后背景)的展示标签。
  const extractionTargetLabels: Record<AssetExtractionTarget, string> = {
    "foreground": "前景整体",
    "body-no-text": "本体去文字",
    "text-layer": "仅文字层",
    "background-behind": "背后背景",
  };

  interface ExtractScreenResponse {
    ok: boolean;
    entries?: ExtractEntry[];
    notes?: string[];
    message?: string;
    error?: string;
  }

  interface ApiTaskStatus {
    loading: boolean;
    files: string[];
    command: string;
    message: string;
    error: string;
    prompt: string;
    provider: string;
    model: string;
    plan: DryRunPlan | null;
    geometryReport: any;
  }

  async function readApiTaskResponse(response: Response, url: string): Promise<ApiTaskResponse> {
    const contentType = response.headers.get("content-type") ?? "";
    const raw = await response.text();
    if (!contentType.includes("application/json")) {
      const prefix = raw.trim().slice(0, 80) || response.statusText;
      throw new Error(`${url} 返回的不是 JSON：${prefix}`);
    }
    try {
      return JSON.parse(raw) as ApiTaskResponse;
    } catch (_error) {
      throw new Error(`${url} 返回了无效 JSON`);
    }
  }

  const FIGMA_BOARD_SCALE = 0.36;

  interface AuthUser {
    id: string;
    username: string;
    displayName: string;
    role: string;
  }

  interface CloudProject {
    id: string;
    name: string;
    ownerUserId?: string;
    updatedAt: string;
    updatedBy: string;
    members: string[];
    hasDocument: boolean;
    document?: any;
  }

  interface AdminUser {
    id: string;
    username: string;
    displayName: string;
    role: string;
  }

  const defaultFigmaUrl = "";
  const figmaTokenStorageKey = "art-workbench-figma-token";
  const figmaUrlStorageKey = "art-workbench-figma-url";
  const figmaImportStorageKey = "art-workbench-figma-import-state";
  const workspaceDraftStorageKey = "art-workbench-local-draft";

  let figmaUrl = defaultFigmaUrl;
  let selectedAssetId = "";
  let currentView: "screen" | "atlas" | "game" = "screen";
  let activeStage: WorkflowStageId = "wireframe";
  let projectName = "Untitled Project";
  let canvasWidth = 600;
  let canvasHeight = 1200;
  let outputRoot = defaultOutputRoot;
  let projectConfig: ProjectConfig = createDefaultProjectConfig();
  let finalOverlay: FinalOverlay = createFinalOverlay();
  let toast = "";
  let modalMode: ModalMode = "none";
  let snapToGrid = true;
  let canvasZoom = 1;
  let canvasAutoCenter = true;
  let canvasViewMode: CanvasViewMode = "overlay";
  let canvasRenderKey = 0;
  let hydratingScreen = false;
  let canvasWrapElement: HTMLDivElement | null = null;
  let phoneFrameElement: HTMLDivElement | null = null;
  let dragAssetId = "";
  let dragLast = { x: 0, y: 0 };
  let ignoreCanvasClickUntil = 0;
  let resizeDrag: {
    assetId: string;
    handle: ResizeHandle;
    startRect: Rect;
    startSourceRect: Rect;
    startTargetSize: { w: number; h: number };
    pointer: { x: number; y: number };
  } | null = null;
  let paletteDragType: AssetType | "" = "";
  let paletteDragStart = { x: 0, y: 0 };
  let paletteDragMoved = false;
  let suppressPaletteClick = false;
  let marqueeMode = false;
  let marqueeAssetType: MarqueeAssetType = "panel";
  let marqueeDrag: { start: { x: number; y: number }; current: { x: number; y: number } } | null = null;
  let styleRef = "";
  let figmaToken = "";
  let layoutRef = "";
  let promptPack = "";
  let imageGenerationMode: ImageGenerationMode = "screen-mockup";
  let projectReferences: ReferenceSet = createReferenceSet();
  let selectedScreenId = "";
  let selectedScreenBatchIds: string[] = [];
  let pendingDeleteScreenIds: string[] = [];
  let newScreenName = "";
  let batchGeneratingWireframes = false;
  let wireframeImportError = "";
  let newAsset: ArtAsset = createEmptyAsset();
  let figmaParsing = false;
  let figmaResult: FigmaParseResult | null = null;
  let figmaFrames: FigmaFrame[] = [];
  let figmaSelectedFrameIds: string[] = [];
  let figmaPreviewFrameId = "";
  let figmaParseWholeFile = true;
  let figmaShowParsedOverlay = true;
  let figmaCanvasZoom = 1;
  let figmaCanvasPan = { x: 96, y: 64 };
  let figmaCanvasPanning = false;
  let figmaCanvasLastPointer = { x: 0, y: 0 };
  let figmaRenderImages: Record<string, string> = {};
  let figmaRenderLoading = false;
  let figmaRenderError = "";
  let figmaParseEpoch = 0;
  let figmaPersistenceReady = false;
  let workspacePersistenceReady = false;
  let aiProviders: AiProviderResult | null = null;
  let aiProviderLoading = true;
  let aiProviderError = "";
  let authUser: AuthUser | null = null;
  let authLoading = true;
  let authMode: "login" | "register" = "login";
  let loginUsername = "admin";
  let loginPassword = "";
  let loginError = "";
  let registerUsername = "";
  let registerDisplayName = "";
  let registerPassword = "";
  let registerConfirmPassword = "";
  let cloudProjects: CloudProject[] = [];
  let selectedCloudProjectId = "";
  let cloudStatus = "";
  let cloudLoading = false;
  let adminUsers: AdminUser[] = [];
  let adminUserLoading = false;
  let adminUserError = "";
  let newUserName = "";
  let newUserDisplayName = "";
  let newUserPassword = "";
  let newUserRole = "member";
  let selectedProjectMemberIds: string[] = [];
  let userMenuOpen = false;
  let newProjectTitle = "";
  let apiTaskEpoch = 0;
  let apiTasks: Record<ApiTaskKey, ApiTaskStatus> = {
    wireframe: createApiTaskStatus(),
    image: createApiTaskStatus(),
    export: createApiTaskStatus(),
  };
  let pendingDryRunPlan: DryRunPlan | null = null;
  let fileConflictConfirmed = false;
  let gameSyncPlan: GameSyncPlan | null = null;
  let gameSyncLoading = false;
  let gameSyncError = "";
  let gameSyncMessage = "";
  let gameSyncDone = false;
  // B2 参考资产管理
  let referenceScope: "project" | "screen" = "project";
  let newReferenceUrl = "";
  let newReferenceLabel = "";
  let newReferenceRole: ReferenceImage["role"] = "reference";
  let sliceZipLoading = false;
  let sliceThumbnails: Record<string, string> = {};
  let sliceThumbTimer: ReturnType<typeof setTimeout> | null = null;
  // AI 元素提取(切图阶段)：结果只存内存，服务端有缓存，重提取很快。
  let extractEntries: Record<string, ExtractEntry> = {};
  // A：自动整屏提取的防重入 key(界面 + 成稿签名)。
  let autoExtractedKey = "";
  let extractNotes: string[] = [];
  let extractMessage = "";
  let extractError = "";
  let extractScreenLoading = false;
  let extractSingleLoadingId = "";
  let extractHealthOk = false;
  // 用户点过「采用」的 mask，导出(zip/write-staging)时通过 extractionMasks 附带，优先级最高。
  let adoptedExtractionMasks: Record<string, string> = {};
  // 点修正模式：点坐标统一存成稿图像素坐标(成稿像素 = 画布坐标 × 成稿宽/画布宽)。
  let pointEditAssetId = "";
  let pointEditPolarity: "positive" | "negative" = "positive";
  let pointEditPositive: Array<[number, number]> = [];
  let pointEditNegative: Array<[number, number]> = [];
  let finalOverlayNaturalSize: { w: number; h: number } | null = null;
  let finalOverlayNaturalSrc = "";
  let backgroundPreviewUrl = "";
  let backgroundPreviewPipeline = "";
  let backgroundPreviewLoading = false;
  // 提示词二次处理(唯一计费操作)。
  let refineTargetAssetId = "";
  let refinePromptText = "";
  let refineLoading = false;

  let stages: PipelineStage[] = createDefaultStages();

  let uiScreens: UiScreen[] = [];

  let assets: ArtAsset[] = [];

  $: figmaInfo = parseFigmaUrl(figmaUrl);
  $: selectedAsset = assets.find((asset) => asset.assetId === selectedAssetId) ?? null;
  $: selectedScreen = uiScreens.find((screen) => screen.id === selectedScreenId) ?? null;
  $: pendingDeleteScreens = uiScreens.filter((screen) => pendingDeleteScreenIds.includes(screen.id));
  $: modeVisibleAssets = canvasViewMode === "wireframe"
    ? assets
    : (canvasViewMode === "final" && activeStage !== "slice")
      ? []
      : assets.filter((asset) => !asset.hidden);
  // 切图阶段显示全部非隐藏元素；图集分组改由右侧「图集分组」面板按 atlas 聚合呈现，
  // 不再用 currentView 粗暴过滤画布（旧逻辑只留 atlas 含 physical 的元素，会让典型项目画布空掉）。
  $: visibleAssets = modeVisibleAssets;
  $: atlasGroups = buildAtlasGroups(assets);
  // AI 提取面板的元素卡片：当前界面所有参与导出的非隐藏元素 + 各自提取结果(可能还没有)。
  $: extractCardRows = activeStage === "slice"
    ? assets
        .filter((asset) => !asset.hidden && (asset.exportEnabled ?? true))
        .map((asset) => ({ asset, entry: extractEntries[asset.assetId] ?? null }))
    : [];
  // 切换界面后清掉指向已不存在元素的点修正状态。
  $: if (pointEditAssetId && !assets.some((asset) => asset.assetId === pointEditAssetId)) {
    pointEditAssetId = "";
    pointEditPositive = [];
    pointEditNegative = [];
  }
  // 成稿图换了之后，缓存的成稿像素尺寸失效。
  $: if (finalOverlayNaturalSize && finalOverlay.src !== finalOverlayNaturalSrc) {
    finalOverlayNaturalSize = null;
    finalOverlayNaturalSrc = "";
  }
  // B2：当前作用域（项目级/界面级）的参考集，供管理模态读取。
  $: activeReferenceSet = referenceScope === "screen" && selectedScreen
    ? normalizeReferenceSet(selectedScreen.references, projectReferences)
    : projectReferences;
  // 切图阶段：按每个元素的 rect 从成稿图裁出缩略图，列在画布下方的切片预览条。
  $: sliceThumbSignature = activeStage === "slice" && finalOverlay.src
    ? `${finalOverlay.src.slice(0, 48)}:${finalOverlay.src.length}:${canvasWidth}x${canvasHeight}:${assets
        .filter((asset) => !asset.hidden)
        .map((asset) => `${asset.assetId},${Math.round(asset.rect.x)},${Math.round(asset.rect.y)},${Math.round(asset.rect.w)},${Math.round(asset.rect.h)}`)
        .join("|")}`
    : "";
  $: if (sliceThumbSignature) {
    if (sliceThumbTimer) clearTimeout(sliceThumbTimer);
    sliceThumbTimer = setTimeout(() => { void regenerateSliceThumbnails(); }, 250);
  }
  // A：进入切图阶段且有成稿时，自动用线框 rect 先验整屏语义提取。
  // key 含界面与成稿签名：成稿重新生成后自动重跑；服务端按成稿哈希+rect 缓存，重入秒回。
  $: autoExtractSignature = activeStage === "slice" && hasFinalOverlay && assets.length
    ? `${selectedScreenId}:${finalOverlay.src.length}:${finalOverlay.src.slice(0, 48)}`
    : "";
  $: if (autoExtractSignature && autoExtractSignature !== autoExtractedKey) {
    autoExtractedKey = autoExtractSignature;
    void runScreenExtraction();
  }

  async function regenerateSliceThumbnails(): Promise<void> {
    const src = finalOverlay.src;
    const list = assets.filter((asset) => !asset.hidden);
    if (!src || !list.length) {
      sliceThumbnails = {};
      return;
    }
    const image = await new Promise<HTMLImageElement | null>((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });
    if (!image || !image.naturalWidth) {
      sliceThumbnails = {};
      return;
    }
    const sx = image.naturalWidth / Math.max(1, canvasWidth);
    const sy = image.naturalHeight / Math.max(1, canvasHeight);
    const next: Record<string, string> = {};
    for (const asset of list) {
      const w = Math.max(1, Math.round(asset.rect.w * sx));
      const h = Math.max(1, Math.round(asset.rect.h * sy));
      const left = Math.max(0, Math.min(image.naturalWidth - 1, Math.round(asset.rect.x * sx)));
      const top = Math.max(0, Math.min(image.naturalHeight - 1, Math.round(asset.rect.y * sy)));
      const canvas = document.createElement("canvas");
      canvas.width = Math.min(w, image.naturalWidth - left);
      canvas.height = Math.min(h, image.naturalHeight - top);
      const ctx = canvas.getContext("2d");
      if (!ctx || canvas.width <= 0 || canvas.height <= 0) continue;
      ctx.drawImage(image, left, top, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
      try {
        next[asset.assetId] = canvas.toDataURL("image/png");
      } catch (_error) {
        // 跨域污染无法导出，跳过
      }
    }
    sliceThumbnails = next;
  }

  interface AtlasGroup {
    atlas: string;
    items: ArtAsset[];
    area: number;
    maxW: number;
    maxH: number;
  }

  // 按 atlas 字段聚合非隐藏元素，给切图阶段的「图集分组」面板用：每组资源数、占用面积、最大切片尺寸。
  function buildAtlasGroups(list: ArtAsset[]): AtlasGroup[] {
    const groups = new Map<string, AtlasGroup>();
    for (const asset of list) {
      if (asset.hidden) continue;
      const key = (asset.atlas ?? "").trim() || "(未分组)";
      let group = groups.get(key);
      if (!group) {
        group = { atlas: key, items: [], area: 0, maxW: 0, maxH: 0 };
        groups.set(key, group);
      }
      const w = Math.round(asset.targetSize?.w ?? asset.rect.w);
      const h = Math.round(asset.targetSize?.h ?? asset.rect.h);
      group.items.push(asset);
      group.area += Math.max(0, w) * Math.max(0, h);
      group.maxW = Math.max(group.maxW, w);
      group.maxH = Math.max(group.maxH, h);
    }
    return [...groups.values()].sort((a, b) => b.items.length - a.items.length || a.atlas.localeCompare(b.atlas));
  }

  $: layerRows = assets.map((asset, index) => ({
    asset,
    index,
    depth: assetLayerDepth(asset),
  }));
  $: buildIssues = validateAssets(assets, canvasWidth, canvasHeight, selectedScreenId, selectedScreen?.name ?? "当前界面");
  $: blockingBuildIssues = buildIssues.filter((issue) => issue.severity === "error");
  $: warningBuildIssues = buildIssues.filter((issue) => issue.severity === "warning");
  $: blockingBuildSummary = buildBlockingIssueSummary(blockingBuildIssues);
  $: activeStageInfo = stages.find((stage) => stage.id === activeStage) ?? stages[0];
  $: dryRunConflictEntries = pendingDryRunPlan?.entries.filter((entry) => entry.action === "overwrite") ?? [];
  $: currentApiTaskStatus = activeStage === "wireframe"
    ? apiTasks.wireframe
    : activeStage === "style"
      ? apiTasks.image
      : activeStage === "slice"
        ? apiTasks.export
        : createApiTaskStatus();
  $: marqueeRect = marqueeDrag ? rectFromPoints(marqueeDrag.start, marqueeDrag.current) : null;
  $: figmaTopFrames = figmaFrames.filter((frame) => frame.depth === 1 && frame.box);
  $: figmaPreviewFrame = figmaFrames.find((frame) => frame.id === figmaPreviewFrameId)
    ?? figmaTopFrames[0]
    ?? figmaFrames.find((frame) => frame.box);
  $: figmaActivePage = figmaPreviewFrame?.page ?? figmaTopFrames[0]?.page ?? "";
  $: figmaPageTopFrames = figmaActivePage ? figmaTopFrames.filter((frame) => frame.page === figmaActivePage) : figmaTopFrames;
  $: figmaLayerRoot = figmaTopFrames.find((frame) => frame.id === figmaPreviewFrame?.id)
    ?? figmaPageTopFrames[0]
    ?? figmaTopFrames[0];
  $: figmaLayerRows = figmaLayerRoot ? figmaLayerTreeRows(figmaLayerRoot) : [];
  $: figmaParsedReady = Boolean(figmaResult?.ok && figmaFrames.length);
  $: figmaImportCanvasBounds = figmaSelectionBounds(figmaPageTopFrames);
  $: figmaCanvasWorldStyleValue = figmaCanvasWorldStyle(figmaCanvasPan.x, figmaCanvasPan.y, figmaCanvasZoom, figmaImportCanvasBounds.w, figmaImportCanvasBounds.h);
  $: canvasPreviewBaseHeightValue = 720;
  $: canvasPreviewBaseWidthValue = Math.max(180, canvasPreviewBaseHeightValue * (canvasWidth / Math.max(1, canvasHeight)));
  $: canvasDisplayWidthValue = canvasPreviewBaseWidthValue * canvasZoom;
  $: canvasDisplayHeightValue = canvasPreviewBaseHeightValue * canvasZoom;
  $: phoneFrameCss = `width:${canvasDisplayWidthValue}px;height:${canvasDisplayHeightValue}px;`;
  $: hasFinalOverlay = Boolean(finalOverlay.src);
  $: wireframeReferenceSrc = selectedScreen ? resolveWireframeReferenceSrc(selectedScreen) : "";
  $: hasWireframeReference = Boolean(wireframeReferenceSrc);
  $: showWireframeReferenceInCanvas = Boolean(canvasViewMode === "wireframe" && wireframeReferenceSrc);
  $: showFinalOverlayInCanvas = hasFinalOverlay && canvasViewMode !== "wireframe";
  $: showCanvasGuides = Boolean(selectedScreen && canvasViewMode !== "final" && !showWireframeReferenceInCanvas);
  $: wireframeReferenceStyle = [
    "opacity:1",
    "object-fit:fill",
    "width:100%",
    "height:100%",
    "left:50%",
    "top:50%",
    "transform:translate(-50%, -50%)",
  ].join(";");
  $: finalOverlayStyle = [
    `opacity:${finalOverlay.opacity}`,
    `object-fit:${finalOverlay.fit}`,
    `width:${finalOverlay.scale * 100}%`,
    `height:${finalOverlay.scale * 100}%`,
    "left:50%",
    "top:50%",
    `transform:translate(-50%, -50%) translate(${finalOverlay.offsetX * (canvasDisplayWidthValue / Math.max(1, canvasWidth))}px, ${finalOverlay.offsetY * (canvasDisplayHeightValue / Math.max(1, canvasHeight))}px)`,
  ].join(";");
  $: screenDebugSummary = selectedScreen
    ? [
        `screen=${selectedScreen.id}`,
        `assets=${assets.length}`,
        `first=${assets[0]?.assetId ?? "-"}`,
        `overlay=${finalOverlay.src ? `${finalOverlay.src.slice(0, 18)}...` : "-"}`,
      ].join(" · ")
    : "screen=-";
  $: imagePromptPreview = selectedScreen ? createImagePromptPreview() : "";

  onMount(() => {
    figmaToken = window.localStorage.getItem(figmaTokenStorageKey) ?? "";
    restoreWorkspaceDraft();
    restoreFigmaImportState();
    figmaPersistenceReady = true;
    workspacePersistenceReady = true;
    void rehydrateScreenImagesFromIdb();
    void loadCurrentUser();
  });

  function switchAuthMode(nextMode: "login" | "register"): void {
    authMode = nextMode;
    loginError = "";
  }

  // 成稿/参考图是 ~1MB+ 的 data: URL，塞进 localStorage 会超配额导致整份草稿写入静默失败、刷新丢图。
  // 因此大图改存 IndexedDB（配额大），localStorage 草稿里只保留剥离 data: URL 后的精简 JSON。
  const imageDbName = "art-workbench-images";
  const imageStoreName = "screen-images";
  let lastIdbImageSignature = "";

  function openImageDb(): Promise<IDBDatabase | null> {
    return new Promise((resolve) => {
      try {
        const request = window.indexedDB.open(imageDbName, 1);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(imageStoreName)) db.createObjectStore(imageStoreName);
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
      } catch (_error) {
        resolve(null);
      }
    });
  }

  async function saveScreenImagesToIdb(entries: Array<{ id: string; overlay: string; reference: string }>): Promise<void> {
    const db = await openImageDb();
    if (!db) return;
    await new Promise<void>((resolve) => {
      const tx = db.transaction(imageStoreName, "readwrite");
      const store = tx.objectStore(imageStoreName);
      const keep = new Set(entries.map((entry) => entry.id));
      const keysReq = store.getAllKeys();
      keysReq.onsuccess = () => {
        for (const key of keysReq.result as IDBValidKey[]) {
          if (!keep.has(String(key))) store.delete(key);
        }
        for (const entry of entries) store.put({ overlay: entry.overlay, reference: entry.reference }, entry.id);
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
    db.close();
  }

  async function loadScreenImagesFromIdb(): Promise<Record<string, { overlay: string; reference: string }>> {
    const db = await openImageDb();
    if (!db) return {};
    const result = await new Promise<Record<string, { overlay: string; reference: string }>>((resolve) => {
      const out: Record<string, { overlay: string; reference: string }> = {};
      const tx = db.transaction(imageStoreName, "readonly");
      const store = tx.objectStore(imageStoreName);
      const keysReq = store.getAllKeys();
      const valsReq = store.getAll();
      tx.oncomplete = () => {
        const keys = keysReq.result as IDBValidKey[];
        const vals = valsReq.result as Array<{ overlay: string; reference: string }>;
        keys.forEach((key, index) => { out[String(key)] = vals[index] ?? { overlay: "", reference: "" }; });
        resolve(out);
      };
      tx.onerror = () => resolve({});
    });
    db.close();
    return result;
  }

  function stripDataUrls(value: any): any {
    if (typeof value === "string") return value.startsWith("data:") ? "" : value;
    if (Array.isArray(value)) return value.map(stripDataUrls);
    if (value && typeof value === "object") {
      const out: Record<string, any> = {};
      for (const key of Object.keys(value)) out[key] = stripDataUrls(value[key]);
      return out;
    }
    return value;
  }

  function persistWorkspaceDraft(): void {
    if (!workspacePersistenceReady) return;
    try {
      const manifest = createManifest();
      const imageEntries = ((manifest as any).screens ?? [])
        .map((screen: any) => {
          const overlaySrc = String(screen?.finalOverlay?.src ?? "");
          const referenceSrc = String(screen?.referenceImageUrl ?? "");
          return {
            id: String(screen?.id ?? ""),
            overlay: overlaySrc.startsWith("data:") ? overlaySrc : "",
            reference: referenceSrc.startsWith("data:") ? referenceSrc : "",
          };
        })
        .filter((entry: { id: string; overlay: string; reference: string }) => entry.id && (entry.overlay || entry.reference));
      window.localStorage.setItem(workspaceDraftStorageKey, JSON.stringify({
        version: 1,
        savedAt: new Date().toISOString(),
        selectedScreenId,
        currentView,
        activeStage,
        document: stripDataUrls(manifest),
      }));
      if (imageEntries.length) {
        // 仅在图片内容变化时才写 IDB，避免每次拖动元素都搬运 1MB+ 数据
        const signature = imageEntries.map((entry) => `${entry.id}:${entry.overlay.length}:${entry.reference.length}`).join("|");
        if (signature !== lastIdbImageSignature) {
          lastIdbImageSignature = signature;
          void saveScreenImagesToIdb(imageEntries);
        }
      }
    } catch (error) {
      console.warn("workspace draft persist failed", error);
    }
  }

  async function rehydrateScreenImagesFromIdb(): Promise<void> {
    const images = await loadScreenImagesFromIdb();
    if (!Object.keys(images).length) return;
    let changed = false;
    uiScreens = uiScreens.map((screen) => {
      const stored = images[screen.id];
      if (!stored) return screen;
      const overlay = stored.overlay || screen.finalOverlay?.src || "";
      const reference = stored.reference || screen.referenceImageUrl || "";
      if (!overlay && !reference) return screen;
      changed = true;
      return {
        ...screen,
        referenceImageUrl: reference || screen.referenceImageUrl,
        finalOverlay: normalizeFinalOverlay(
          screen.finalOverlay ? { ...screen.finalOverlay, src: overlay } : { src: overlay } as any,
          reference,
        ),
      };
    });
    if (changed) hydrateWorkspaceFromSelectedScreen();
  }

  function restoreWorkspaceDraft(): void {
    const raw = window.localStorage.getItem(workspaceDraftStorageKey);
    if (!raw) return;
    try {
      const snapshot = JSON.parse(raw) as { document?: any; selectedScreenId?: string; currentView?: "screen" | "atlas" | "game"; activeStage?: WorkflowStageId };
      if (!snapshot.document) return;
      applyManifestDocument(snapshot.document, snapshot.selectedScreenId);
      if (snapshot.currentView) currentView = snapshot.currentView;
      if (snapshot.activeStage) setWorkflowStage(snapshot.activeStage);
    } catch (error) {
      console.warn("workspace draft restore failed", error);
      window.localStorage.removeItem(workspaceDraftStorageKey);
    }
  }

  function saveFigmaToken(): void {
    const token = figmaToken.trim();
    if (!token) {
      window.localStorage.removeItem(figmaTokenStorageKey);
      showToast("Figma Token 已清除");
      return;
    }
    window.localStorage.setItem(figmaTokenStorageKey, token);
    figmaToken = token;
    persistFigmaImportState();
    showToast("Figma Token 已保存到本机浏览器");
  }

  function clearFigmaToken(): void {
    figmaToken = "";
    window.localStorage.removeItem(figmaTokenStorageKey);
    showToast("Figma Token 已清除");
  }

  function updateFigmaTokenInput(): void {
    if (!figmaPersistenceReady) return;
    const token = figmaToken.trim();
    if (token) {
      window.localStorage.setItem(figmaTokenStorageKey, token);
    } else {
      window.localStorage.removeItem(figmaTokenStorageKey);
    }
  }

  function createDefaultStages(): PipelineStage[] {
    return [
      { id: "wireframe", title: "1. 线框设计", desc: "生成或导入结构线框，像素级调整元素", output: "可编辑线框", state: "active" },
      { id: "style", title: "2. 游戏风格", desc: "按线框源图生成同尺寸、同布局的成稿原图", output: "成稿源图", state: "todo" },
      { id: "slice", title: "3. 切图整理", desc: "从成稿源图按同一套线框/语义合同切图并生成组装清单", output: "sliced assets / atlas plan", state: "todo" },
    ];
  }

  function stageStatesFromStages(): Record<WorkflowStageId, StageState> {
    return stages.reduce((states, stage) => {
      states[stage.id] = stage.state;
      return states;
    }, createDefaultStageStates());
  }

  function stagesFromStates(states: (Record<WorkflowStageId, StageState> & { preview?: StageState; apply?: StageState }) | undefined): PipelineStage[] {
    const legacyPreviewActive = states?.preview === "active";
    const legacyApplyActive = states?.apply === "active";
    return createDefaultStages().map((stage) => ({
      ...stage,
      state: stage.id === "slice" && (legacyPreviewActive || legacyApplyActive)
        ? "active"
        : states?.[stage.id] ?? stage.state,
    }));
  }

  function screenStatusLabel(status: ScreenStatus): string {
    return status === "done" ? "done" : "todo";
  }

  function screenListBadgeLabel(screen: UiScreen): string {
    return screen.id === selectedScreenId ? "当前" : screenStatusLabel(screen.status);
  }

  function screenDataFingerprint(screen: UiScreen): string {
    const first = screen.assets?.[0]?.assetId ?? "-";
    const overlay = screen.finalOverlay?.src || screen.referenceImageUrl ? "img" : "-";
    return `${screen.id} · assets=${screen.assets?.length ?? 0} · first=${first} · ${overlay}`;
  }

  function resolveWireframeReferenceSrc(screen: UiScreen): string {
    const explicit = String(screen.referenceImageUrl ?? "").trim();
    if (explicit) return explicit;
    const referenceImage = screen.references?.images?.find((image) => image.role === "layout" || image.role === "reference" || image.role === "style");
    const referenceSrc = String(referenceImage?.url ?? "").trim();
    if (referenceSrc) return referenceSrc;
    const overlaySrc = String(screen.finalOverlay?.src ?? "").trim();
    if (!overlaySrc) return "";
    const isRemoteImage = /^https?:\/\//.test(overlaySrc);
    const isLockedSource = Boolean(screen.finalOverlay?.locked);
    const hasFigmaSource = Boolean(screen.figmaUrl || figmaUrl || screen.assets?.some((asset) => asset.figma?.nodeId));
    return hasFigmaSource && (isRemoteImage || isLockedSource) ? overlaySrc : "";
  }

  function screenStatusFromStageStates(states: Record<WorkflowStageId, StageState>, fallback: unknown): ScreenStatus {
    if (states.slice === "done") return "done";
    return normalizeScreenStatus(fallback);
  }

  function uniqueScreenId(base: string): string {
    const cleanBase = sanitizeId(base, `screen_${Date.now().toString(36)}`);
    let id = cleanBase;
    let index = 2;
    while (uiScreens.some((screen) => screen.id === id)) {
      id = `${cleanBase}_${index}`;
      index += 1;
    }
    return id;
  }

  function uniqueScreenIdInSet(base: string, usedIds: Set<string>): string {
    return uniqueIdInSet(base, usedIds, `screen_${Date.now().toString(36)}`);
  }

  function currentScreenReferences(overlay: FinalOverlay = finalOverlay): ReferenceSet {
    const screen = uiScreens.find((item) => item.id === selectedScreenId) ?? selectedScreen;
    // 成稿图作为自动参考，但不再覆盖界面级手动管理的参考图（两者合并、去重）。
    const managed = (screen?.references?.images ?? []).filter((img) => img.id !== "screen_final" && img.url !== overlay.src);
    const images = overlay.src
      ? [{ id: "screen_final", url: overlay.src, label: "当前成稿", role: "reference" as const }, ...managed]
      : managed;
    return createReferenceSet({
      style: styleRef,
      layout: layoutRef,
      images,
      prompt: promptPack,
      negative: screen?.references?.negative ?? projectReferences.negative,
    });
  }

  // B2：参考资产管理 —— 读取/写入当前作用域（项目级 / 当前界面级）的参考集。
  function openReferenceAssets(): void {
    if (referenceScope === "screen" && !selectedScreen) referenceScope = "project";
    newReferenceUrl = "";
    newReferenceLabel = "";
    newReferenceRole = "reference";
    modalMode = "referenceAssets";
  }

  function scopeReferenceSet(): ReferenceSet {
    if (referenceScope === "screen" && selectedScreen) {
      return normalizeReferenceSet(selectedScreen.references, projectReferences);
    }
    return projectReferences;
  }

  function writeScopeReferenceSet(next: ReferenceSet): void {
    if (referenceScope === "screen" && selectedScreenId) {
      // layoutRef/promptPack 是界面级（切换界面时 hydrate 会按界面重置），可安全同步；
      // styleRef 是项目级全局变量、hydrate 不重置它，这里不写，避免界面级编辑污染项目风格并泄漏到其他界面。
      layoutRef = next.layout;
      promptPack = next.prompt;
      uiScreens = uiScreens.map((screen) => screen.id === selectedScreenId
        ? { ...screen, references: next, layoutRef: next.layout, promptPack: next.prompt }
        : screen);
      syncSelectedScreenFromWorkspace();
    } else {
      projectReferences = next;
      // style 是项目级，直接同步到画布『风格参考』输入框绑定的 styleRef，保证编辑即时生效。
      styleRef = next.style;
      persistWorkspaceDraft();
    }
  }

  function addReferenceImageFromInput(): void {
    const url = newReferenceUrl.trim();
    if (!url) {
      showToast("请先填写参考图 URL 或上传图片");
      return;
    }
    const set = scopeReferenceSet();
    const id = uniqueIdInSet(
      sanitizeId(newReferenceLabel.trim() || `reference_${set.images.length + 1}`, `reference_${set.images.length + 1}`),
      new Set(set.images.map((img) => img.id)),
    );
    const image: ReferenceImage = {
      id,
      url,
      label: newReferenceLabel.trim() || `参考图 ${set.images.length + 1}`,
      role: newReferenceRole,
    };
    writeScopeReferenceSet({ ...set, images: [...set.images, image] });
    newReferenceUrl = "";
    newReferenceLabel = "";
    showToast("参考图已添加");
  }

  async function uploadReferenceImage(event: Event): Promise<void> {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ""));
      reader.onerror = () => reject(reader.error ?? new Error("读取图片失败"));
      reader.readAsDataURL(file);
    }).catch(() => "");
    if (!dataUrl) {
      showToast("图片读取失败");
      return;
    }
    const set = scopeReferenceSet();
    const baseLabel = newReferenceLabel.trim() || file.name.replace(/\.[^.]+$/, "") || `参考图 ${set.images.length + 1}`;
    const id = uniqueIdInSet(
      sanitizeId(baseLabel, `reference_${set.images.length + 1}`),
      new Set(set.images.map((img) => img.id)),
    );
    writeScopeReferenceSet({
      ...set,
      images: [...set.images, { id, url: dataUrl, label: baseLabel, role: newReferenceRole }],
    });
    newReferenceLabel = "";
    showToast("本地图片已加入参考资产");
  }

  function updateReferenceImage(index: number, patch: Partial<ReferenceImage>): void {
    const set = scopeReferenceSet();
    if (index < 0 || index >= set.images.length) return;
    const images = set.images.map((img, i) => (i === index ? { ...img, ...patch } : img));
    writeScopeReferenceSet({ ...set, images });
  }

  function deleteReferenceImage(index: number): void {
    const set = scopeReferenceSet();
    writeScopeReferenceSet({ ...set, images: set.images.filter((_, i) => i !== index) });
  }

  function moveReferenceImage(index: number, delta: number): void {
    const set = scopeReferenceSet();
    const target = index + delta;
    if (target < 0 || target >= set.images.length) return;
    const images = [...set.images];
    const [moved] = images.splice(index, 1);
    images.splice(target, 0, moved);
    writeScopeReferenceSet({ ...set, images });
  }

  function updateReferenceField(field: "style" | "layout" | "prompt" | "negative", value: string): void {
    writeScopeReferenceSet({ ...scopeReferenceSet(), [field]: value });
  }

  function exportReferenceJson(download: boolean): void {
    const payload = buildReferenceExport({
      projectReferences,
      screenReferences: currentScreenReferences(),
      screenId: selectedScreenId,
      screenName: selectedScreen?.name ?? "",
      exportedAt: new Date().toISOString(),
    });
    const text = JSON.stringify(payload, null, 2);
    if (download) {
      const blob = new Blob([`${text}\n`], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectedScreen?.id ?? "artflow"}-references.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showToast("参考资产 JSON 已下载");
    } else {
      void navigator.clipboard?.writeText(text).then(
        () => showToast("参考资产 JSON 已复制"),
        () => showToast("复制失败，请手动复制"),
      );
    }
  }

  function rectFromPoints(start: { x: number; y: number }, end: { x: number; y: number }): Rect {
    const x1 = Math.max(0, Math.min(canvasWidth, start.x));
    const y1 = Math.max(0, Math.min(canvasHeight, start.y));
    const x2 = Math.max(0, Math.min(canvasWidth, end.x));
    const y2 = Math.max(0, Math.min(canvasHeight, end.y));
    return {
      x: snapValue(Math.min(x1, x2)),
      y: snapValue(Math.min(y1, y2)),
      w: snapValue(Math.abs(x2 - x1)),
      h: snapValue(Math.abs(y2 - y1)),
    };
  }

  function canvasPointFromClient(clientX: number, clientY: number): { x: number; y: number } | null {
    if (!phoneFrameElement) return null;
    const bounds = phoneFrameElement.getBoundingClientRect();
    if (clientX < bounds.left || clientX > bounds.right || clientY < bounds.top || clientY > bounds.bottom) return null;
    return {
      x: clampNumber(((clientX - bounds.left) / bounds.width) * canvasWidth, 0, canvasWidth),
      y: clampNumber(((clientY - bounds.top) / bounds.height) * canvasHeight, 0, canvasHeight),
    };
  }

  function normalizeWireframeAsset(item: any, index: number): ArtAsset {
    return normalizeAsset(item, index, { source: "wireframe-json", uniqueId: uniqueAssetId, selectedScreenId, outputRoot });
  }

  function importWireframeJson(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(String(reader.result)) as WireframeDocument;
        const rawNodes = data.nodes ?? data.assets ?? data.frames ?? [];
        if (!Array.isArray(rawNodes) || rawNodes.length === 0) {
          throw new Error("线框 JSON 缺少 nodes/assets/frames 数组");
        }
        if (!selectedScreen) {
          const name = String(data.screen?.name ?? file.name.replace(/\.[^.]+$/, "") ?? `新界面 ${uiScreens.length + 1}`);
          const id = uniqueScreenId(String(data.screen?.id ?? name));
          uiScreens = [...uiScreens, {
            id,
            name,
            category: String(data.screen?.category ?? "未分类界面"),
            width: Number(data.canvas?.width ?? canvasWidth),
            height: Number(data.canvas?.height ?? canvasHeight),
            figmaUrl,
            layoutRef,
            promptPack,
            references: normalizeReferenceSet(data.screen?.references, projectReferences),
            referenceImageUrl: data.screen?.referenceImageUrl ? String(data.screen.referenceImageUrl) : undefined,
            finalOverlay: normalizeFinalOverlay(data.screen?.finalOverlay, data.screen?.referenceImageUrl),
            status: "todo",
            stageStates: createDefaultStageStates("style"),
            semanticTree: data.screen?.semanticTree,
            assets: [],
          }];
          selectedScreenId = id;
        }
        if (data.canvas?.width) canvasWidth = Number(data.canvas.width);
        if (data.canvas?.height) canvasHeight = Number(data.canvas.height);
        if (data.screen?.name) {
          uiScreens = uiScreens.map((screen) => screen.id === selectedScreenId
            ? {
                ...screen,
                name: String(data.screen?.name),
                category: String(data.screen?.category ?? screen.category),
                semanticTree: data.screen?.semanticTree ?? screen.semanticTree,
                referenceImageUrl: data.screen?.referenceImageUrl ? String(data.screen.referenceImageUrl) : screen.referenceImageUrl,
                finalOverlay: data.screen?.finalOverlay ? normalizeFinalOverlay(data.screen.finalOverlay, data.screen.referenceImageUrl) : screen.finalOverlay,
              }
            : screen);
        }
        assets = rawNodes.map((item, index) => normalizeWireframeAsset(item, index));
        selectedAssetId = assets[0]?.assetId ?? "";
        wireframeImportError = "";
        completeStage("wireframe", "style");
        syncSelectedScreenFromWorkspace();
        showToast(`已导入 ${assets.length} 个线框节点`);
      } catch (error) {
        wireframeImportError = error instanceof Error ? error.message : "线框 JSON 导入失败";
        showToast(wireframeImportError);
      }
    };
    reader.readAsText(file);
    input.value = "";
  }

  function exportWireframeJson(): void {
    if (!selectedScreen) {
      showToast("请先新建或导入一个界面");
      return;
    }
    const payload = JSON.stringify(createWireframeDocument({
      selectedScreen,
      selectedScreenId,
      canvasWidth,
      canvasHeight,
      assets,
    }), null, 2);
    const blob = new Blob([payload], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedScreen.id}.wireframe.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    syncSelectedScreenFromWorkspace();
    showToast("线框 JSON 已导出");
  }

  function parseFigmaUrl(url: string): FigmaInfo {
    const keyMatch = url.match(/figma\.com\/(?:design|file)\/([^/?#]+)/);
    let nodeId = "";
    try {
      nodeId = new URL(url).searchParams.get("node-id") ?? "";
    } catch (_err) {
      nodeId = "";
    }
    return {
      fileKey: keyMatch?.[1] ?? "",
      nodeId,
      url,
    };
  }

  function persistFigmaUrl(): void {
    if (!figmaPersistenceReady) return;
    const url = figmaUrl.trim();
    if (url) {
      window.localStorage.setItem(figmaUrlStorageKey, url);
    } else {
      window.localStorage.removeItem(figmaUrlStorageKey);
    }
  }

  function persistFigmaImportState(): void {
    if (!figmaPersistenceReady) return;
    persistFigmaUrl();
    if (!figmaResult && !figmaFrames.length) {
      window.localStorage.removeItem(figmaImportStorageKey);
      return;
    }
    const snapshot: FigmaImportSnapshot = {
      version: 1,
      savedAt: new Date().toISOString(),
      url: figmaUrl,
      parseWholeFile: figmaParseWholeFile,
      showParsedOverlay: figmaShowParsedOverlay,
      result: figmaResult,
      frames: figmaFrames,
      selectedFrameIds: figmaSelectedFrameIds,
      previewFrameId: figmaPreviewFrameId,
      renderImages: figmaRenderImages,
      renderError: figmaRenderError,
      canvasZoom: figmaCanvasZoom,
      canvasPan: figmaCanvasPan,
    };
    try {
      window.localStorage.setItem(figmaImportStorageKey, JSON.stringify(snapshot));
    } catch (_error) {
      window.localStorage.removeItem(figmaImportStorageKey);
    }
  }

  function restoreFigmaImportState(): void {
    const savedUrl = window.localStorage.getItem(figmaUrlStorageKey);
    if (savedUrl) figmaUrl = savedUrl;
    const rawSnapshot = window.localStorage.getItem(figmaImportStorageKey);
    if (!rawSnapshot) return;
    try {
      const snapshot = JSON.parse(rawSnapshot) as Partial<FigmaImportSnapshot>;
      if (snapshot.version !== 1) return;
      if (typeof snapshot.url === "string" && snapshot.url.trim()) figmaUrl = snapshot.url;
      figmaParseWholeFile = snapshot.parseWholeFile === undefined ? true : Boolean(snapshot.parseWholeFile);
      figmaShowParsedOverlay = snapshot.showParsedOverlay ?? true;
      figmaResult = snapshot.result ?? null;
      figmaFrames = Array.isArray(snapshot.frames) ? snapshot.frames : [];
      figmaSelectedFrameIds = Array.isArray(snapshot.selectedFrameIds) ? snapshot.selectedFrameIds.filter(Boolean) : [];
      figmaPreviewFrameId = typeof snapshot.previewFrameId === "string" ? snapshot.previewFrameId : "";
      figmaRenderImages = snapshot.renderImages ?? {};
      figmaRenderError = typeof snapshot.renderError === "string" ? snapshot.renderError : "";
      figmaCanvasZoom = typeof snapshot.canvasZoom === "number" ? Math.max(0.25, Math.min(3, snapshot.canvasZoom)) : 1;
      figmaCanvasPan = snapshot.canvasPan && typeof snapshot.canvasPan.x === "number" && typeof snapshot.canvasPan.y === "number"
        ? { x: snapshot.canvasPan.x, y: snapshot.canvasPan.y }
        : { x: 96, y: 64 };
      figmaRenderLoading = false;
      figmaParsing = false;
    } catch (_error) {
      window.localStorage.removeItem(figmaImportStorageKey);
    }
  }

  function clearFigmaParseState(): void {
    figmaResult = null;
    figmaFrames = [];
    figmaSelectedFrameIds = [];
    figmaPreviewFrameId = "";
    figmaRenderImages = {};
    figmaRenderError = "";
    figmaRenderLoading = false;
    figmaParsing = false;
  }

  function resetFigmaParseState(): void {
    figmaParseEpoch += 1;
    clearFigmaParseState();
    persistFigmaImportState();
  }

  function clearStoredFigmaImport(): void {
    figmaParseEpoch += 1;
    clearFigmaParseState();
    window.localStorage.removeItem(figmaImportStorageKey);
    persistFigmaUrl();
    showToast("Figma 解析结果已清空");
  }

  function updateFigmaUrlInput(): void {
    resetFigmaParseState();
    figmaParseWholeFile = true;
    updateCurrentScreen();
    persistFigmaUrl();
  }

  function openFigmaImport(): void {
    modalMode = "figmaImport";
  }

  async function parseFigmaFile(): Promise<void> {
    if (!figmaInfo.fileKey) {
      resetFigmaParseState();
      showToast("无法识别 Figma file key");
      return;
    }
    const targetNodeId = figmaParseWholeFile ? "" : figmaInfo.nodeId;
    const requestUrl = `/api/figma/file?key=${encodeURIComponent(figmaInfo.fileKey)}${targetNodeId ? `&nodeId=${encodeURIComponent(targetNodeId)}` : ""}`;
    const epoch = figmaParseEpoch + 1;
    figmaParseEpoch = epoch;
    clearFigmaParseState();
    figmaParsing = true;
    try {
      const headers: Record<string, string> = {};
      if (figmaToken.trim()) headers["X-Figma-Token"] = figmaToken.trim();
      updateFigmaTokenInput();
      persistFigmaUrl();
      const response = await fetch(requestUrl, { headers });
      const data = await safeJson(response) as FigmaParseResult;
      if (epoch !== figmaParseEpoch) return;
      figmaResult = data;
      resetTaskStateForFigmaImport();
      if (!response.ok || !data.ok) {
        persistFigmaImportState();
        showToast(data.error ?? "Figma 解析失败");
        return;
      }
      figmaFrames = data.frames ?? [];
      const topFrames = figmaFrames.filter((frame) => frame.depth === 1 && frame.box);
      const firstPage = topFrames[0]?.page ?? "";
      figmaSelectedFrameIds = topFrames.filter((frame) => frame.page === firstPage).map((frame) => frame.id);
      figmaPreviewFrameId = figmaSelectedFrameIds[0] ?? figmaFrames.find((frame) => frame.box)?.id ?? "";
      resetFigmaCanvasView();
      persistFigmaImportState();
      void loadFigmaRenderImages(figmaSelectedFrameIds, epoch);
      setWorkflowStage("wireframe");
      showToast(`已解析 ${data.name ?? "Figma 文件"}，发现 ${figmaFrames.length} 个节点`);
    } catch (error) {
      if (epoch !== figmaParseEpoch) return;
      clearFigmaParseState();
      figmaResult = { ok: false, error: error instanceof Error ? error.message : "Figma 解析失败" };
      resetTaskStateForFigmaImport();
      persistFigmaImportState();
      showToast(figmaResult.error ?? "Figma 解析失败");
    } finally {
      if (epoch === figmaParseEpoch) figmaParsing = false;
    }
  }

  async function loadFigmaRenderImages(frameIds: string[], epoch = figmaParseEpoch): Promise<void> {
    const ids = frameIds.filter(Boolean).slice(0, 24);
    if (!figmaInfo.fileKey || !ids.length) return;
    figmaRenderLoading = true;
    figmaRenderError = "";
    try {
      const images = await fetchFigmaRenderImages(ids);
      if (epoch !== figmaParseEpoch) return;
      figmaRenderImages = { ...figmaRenderImages, ...images };
      figmaFrames = figmaFrames.map((frame) => ({ ...frame, renderUrl: images[frame.id] ?? frame.renderUrl }));
      persistFigmaImportState();
    } catch (error) {
      if (epoch !== figmaParseEpoch) return;
      figmaRenderError = error instanceof Error ? error.message : "Figma 官方渲染图获取失败";
      persistFigmaImportState();
    } finally {
      if (epoch === figmaParseEpoch) figmaRenderLoading = false;
    }
  }

  async function fetchFigmaRenderImages(frameIds: string[]): Promise<Record<string, string>> {
    const ids = frameIds.filter(Boolean).slice(0, 24);
    if (!figmaInfo.fileKey || !ids.length) return {};
    const headers: Record<string, string> = {};
    if (figmaToken.trim()) headers["X-Figma-Token"] = figmaToken.trim();
    const response = await fetch(`/api/figma/render?key=${encodeURIComponent(figmaInfo.fileKey)}&ids=${encodeURIComponent(ids.join(","))}&format=png&scale=1`, { headers });
    const data = await safeJson(response) as { ok: boolean; images?: Record<string, string>; error?: string };
    if (!response.ok || !data.ok) throw new Error(data.error ?? "Figma 官方渲染图获取失败");
    return data.images ?? {};
  }

  async function ensureFigmaRenderImage(frame: FigmaFrame): Promise<string> {
    const existing = figmaRenderImages[frame.id] ?? frame.renderUrl ?? "";
    if (existing) return existing;
    const images = await fetchFigmaRenderImages([frame.id]);
    const src = images[frame.id] ?? "";
    if (src) {
      figmaRenderImages = { ...figmaRenderImages, [frame.id]: src };
      figmaFrames = figmaFrames.map((item) => item.id === frame.id ? { ...item, renderUrl: src } : item);
      persistFigmaImportState();
    }
    return src;
  }

  async function loadAiProviders(): Promise<void> {
    aiProviderLoading = true;
    aiProviderError = "";
    try {
      const response = await fetch("/api/ai/providers");
      const data = await response.json() as AiProviderResult;
      if (!response.ok || !data.ok) throw new Error(data.error ?? "AI 适配器检测失败");
      aiProviders = data;
    } catch (error) {
      aiProviderError = error instanceof Error ? error.message : "AI 适配器检测失败";
    } finally {
      aiProviderLoading = false;
    }
  }

  async function loadCurrentUser(): Promise<void> {
    authLoading = true;
    try {
      const response = await fetch("/api/auth/me", { credentials: "include" });
      const data = await response.json() as { ok: boolean; user?: AuthUser | null };
      authUser = data.user ?? null;
      if (authUser) {
        await Promise.all([loadCloudProjects(), loadAiProviders()]);
      }
    } catch (_error) {
      authUser = null;
    } finally {
      authLoading = false;
    }
  }

  async function login(): Promise<void> {
    loginError = "";
    cloudStatus = "";
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: loginUsername.trim(), password: loginPassword }),
      });
      const data = await safeJson(response) as { ok: boolean; user?: AuthUser; error?: string };
      if (!response.ok || !data.ok || !data.user) throw new Error(data.error ?? "登录失败");
      authUser = data.user;
      loginPassword = "";
      await Promise.all([loadCloudProjects(), loadAiProviders()]);
      showToast(`已登录：${authUser.displayName}`);
    } catch (error) {
      loginError = error instanceof Error && error.message.includes("Unexpected end of JSON")
        ? "登录服务未启动，请使用线上地址或启动本地服务端"
        : error instanceof Error ? error.message : "登录失败";
    }
  }

  async function register(): Promise<void> {
    loginError = "";
    cloudStatus = "";
    if (registerPassword !== registerConfirmPassword) {
      loginError = "两次输入的密码不一致";
      return;
    }
    try {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: registerUsername.trim(),
          displayName: registerDisplayName.trim() || registerUsername.trim(),
          password: registerPassword,
        }),
      });
      const data = await safeJson(response) as { ok: boolean; user?: AuthUser; error?: string };
      if (!response.ok || !data.ok || !data.user) throw new Error(data.error ?? "注册失败");
      authUser = data.user;
      registerUsername = "";
      registerDisplayName = "";
      registerPassword = "";
      registerConfirmPassword = "";
      await Promise.all([loadCloudProjects(), loadAiProviders()]);
      showToast(`已注册：${authUser.displayName}`);
    } catch (error) {
      loginError = error instanceof Error ? error.message : "注册失败";
    }
  }

  async function safeJson(response: Response): Promise<any> {
    const text = await response.text();
    if (!text.trim()) throw new Error("服务器没有返回有效数据");
    try {
      return JSON.parse(text);
    } catch (_error) {
      const looksLikeHtml = text.trimStart().startsWith("<");
      throw new Error(looksLikeHtml
        ? "接口返回了 HTML 页面，不是 JSON。请确认后端服务已启动，并且当前地址不是纯 Vite 静态页面。"
        : "服务器返回的不是有效 JSON，请确认当前地址连接的是 Web 应用服务");
    }
  }

  async function logout(): Promise<void> {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" }).catch(() => undefined);
    authUser = null;
    cloudProjects = [];
    cloudStatus = "";
  }

  async function loadCloudProjects(): Promise<void> {
    cloudLoading = true;
    try {
      const response = await fetch("/api/projects", { credentials: "include" });
      const data = await response.json() as { ok: boolean; projects?: CloudProject[]; error?: string };
      if (!response.ok || !data.ok) throw new Error(data.error ?? "项目列表加载失败");
      cloudProjects = data.projects ?? [];
      selectedCloudProjectId = cloudProjects.some((item) => item.id === selectedCloudProjectId)
        ? selectedCloudProjectId
        : cloudProjects[0]?.id ?? "";
    } catch (error) {
      cloudStatus = error instanceof Error ? error.message : "项目列表加载失败";
    } finally {
      cloudLoading = false;
    }
  }

  async function openMemberAdmin(): Promise<void> {
    if (!selectedCloudProjectId) {
      showToast("请先创建或选择项目");
      return;
    }
    modalMode = "memberAdmin";
    await loadAdminUsers();
    const project = cloudProjects.find((item) => item.id === selectedCloudProjectId);
    selectedProjectMemberIds = [...(project?.members ?? [])];
  }

  async function loadAdminUsers(): Promise<void> {
    adminUserLoading = true;
    adminUserError = "";
    try {
      const response = await fetch("/api/users", { credentials: "include" });
      const data = await safeJson(response) as { ok: boolean; users?: AdminUser[]; error?: string };
      if (!response.ok || !data.ok) throw new Error(data.error ?? "人员列表加载失败");
      adminUsers = data.users ?? [];
    } catch (error) {
      adminUserError = error instanceof Error ? error.message : "人员列表加载失败";
    } finally {
      adminUserLoading = false;
    }
  }

  async function createAdminUser(): Promise<void> {
    adminUserError = "";
    try {
      const response = await fetch("/api/users", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: newUserName.trim(),
          displayName: newUserDisplayName.trim() || newUserName.trim(),
          password: newUserPassword,
          role: newUserRole,
        }),
      });
      const data = await safeJson(response) as { ok: boolean; user?: AdminUser; error?: string };
      if (!response.ok || !data.ok || !data.user) throw new Error(data.error ?? "添加人员失败");
      newUserName = "";
      newUserDisplayName = "";
      newUserPassword = "";
      await loadAdminUsers();
      showToast("人员已添加");
    } catch (error) {
      adminUserError = error instanceof Error ? error.message : "添加人员失败";
    }
  }

  function toggleProjectMember(userId: string): void {
    selectedProjectMemberIds = selectedProjectMemberIds.includes(userId)
      ? selectedProjectMemberIds.filter((id) => id !== userId)
      : [...selectedProjectMemberIds, userId];
  }

  async function saveProjectMembers(): Promise<void> {
    adminUserError = "";
    try {
      const response = await fetch(`/api/projects/${encodeURIComponent(selectedCloudProjectId)}/members`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ memberIds: selectedProjectMemberIds }),
      });
      const data = await safeJson(response) as { ok: boolean; error?: string };
      if (!response.ok || !data.ok) throw new Error(data.error ?? "项目人员保存失败");
      await loadCloudProjects();
      showToast("项目人员已保存");
    } catch (error) {
      adminUserError = error instanceof Error ? error.message : "项目人员保存失败";
    }
  }

  function applyManifestDocument(data: any, preferredScreenId = ""): void {
    const normalized = normalizeArtFlowDocument(data, {
      projectName,
      canvasWidth,
      canvasHeight,
      figmaUrl,
      outputRoot,
      projectReferences,
      styleRef,
      layoutRef,
      promptPack,
      selectedScreenId,
      currentScreens: uiScreens,
      currentAssets: assets,
      preferredScreenId,
    });
    projectName = normalized.projectName;
    canvasWidth = normalized.canvasWidth;
    canvasHeight = normalized.canvasHeight;
    figmaUrl = normalized.figmaUrl;
    outputRoot = normalized.outputRoot;
    projectConfig = normalized.projectConfig;
    projectReferences = normalized.projectReferences;
    styleRef = normalized.styleRef;
    layoutRef = normalized.layoutRef;
    promptPack = normalized.promptPack;
    uiScreens = normalized.uiScreens;
    selectedScreenId = normalized.selectedScreenId;
    assets = normalized.assets;
    hydrateWorkspaceFromSelectedScreen();
  }

  async function loadCloudProject(): Promise<void> {
    if (!selectedCloudProjectId) return;
    cloudLoading = true;
    try {
      const response = await fetch(`/api/projects/${encodeURIComponent(selectedCloudProjectId)}`, { credentials: "include" });
      const data = await response.json() as { ok: boolean; project?: CloudProject; error?: string };
      if (!response.ok || !data.ok || !data.project) throw new Error(data.error ?? "项目加载失败");
      if (data.project.document) applyManifestDocument(data.project.document);
      cloudStatus = data.project.document ? `已载入 ${data.project.name}` : "云端项目还没有保存内容";
      showToast(cloudStatus);
    } catch (error) {
      cloudStatus = error instanceof Error ? error.message : "项目加载失败";
    } finally {
      cloudLoading = false;
    }
  }

  async function handleCloudProjectChange(): Promise<void> {
    if (!selectedCloudProjectId) return;
    await loadCloudProject();
  }

  async function saveCloudProject(createNew = false): Promise<void> {
    if (!authUser) {
      showToast("请先登录");
      return;
    }
    if (!selectedCloudProjectId && !createNew) {
      modalMode = "projectSettings";
      showToast("请先创建自己的项目");
      return;
    }
    syncSelectedScreenFromWorkspace();
    const id = selectedCloudProjectId || sanitizeId(projectName, "project");
    cloudLoading = true;
    try {
      const response = await fetch(`/api/projects/${encodeURIComponent(id)}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: projectName, document: createManifest() }),
      });
      const data = await response.json() as { ok: boolean; project?: CloudProject; error?: string };
      if (!response.ok || !data.ok || !data.project) throw new Error(data.error ?? "项目保存失败");
      selectedCloudProjectId = data.project.id;
      cloudStatus = `已保存到云端：${data.project.name}`;
      await loadCloudProjects();
      showToast(cloudStatus);
    } catch (error) {
      cloudStatus = error instanceof Error ? error.message : "项目保存失败";
    } finally {
      cloudLoading = false;
    }
  }

  async function createCloudProject(): Promise<void> {
    const name = newProjectTitle.trim() || projectName.trim();
    if (!name) {
      showToast("请输入项目名称");
      return;
    }
    projectName = name;
    selectedCloudProjectId = sanitizeId(name, `project_${Date.now().toString(36)}`);
    newProjectTitle = "";
    await saveCloudProject(true);
  }

  function canManageCurrentProject(): boolean {
    const project = cloudProjects.find((item) => item.id === selectedCloudProjectId);
    return Boolean(authUser && (authUser.role === "owner" || authUser.role === "admin" || project?.ownerUserId === authUser.id));
  }

  function providerBadge(provider: AiProvider): string {
    return provider.available ? "badge good" : "badge warn";
  }

  async function copyWireframeTask(): Promise<void> {
    if (!selectedScreen) {
      showToast("请先新建或导入一个界面");
      return;
    }
    const payload = {
      task: "generate_wireframe",
      providerPolicy: "prefer local available CLI: codex, claude, claude-code",
      inputs: {
        projectName,
        screen: {
          id: selectedScreen.id,
          name: selectedScreen.name,
          category: selectedScreen.category,
        },
        canvas: { width: canvasWidth, height: canvasHeight, safeAreaProfile: projectConfig.safeAreaProfile },
        figma: figmaInfo,
        specFiles: [
          "docs/v1/规范/UI美术界面规范.md",
          "docs/v1/规范/ArtWorkbench全流程处理说明.html",
        ],
        references: {
          project: projectReferences,
          screen: currentScreenReferences(),
        },
        styleRef,
        layoutRef,
      },
      outputs: [
        "art_generation/workbench/wireframes/<screen>.wireframe.json",
        "art_generation/workbench/wireframes/<screen>.wireframe.svg",
        "art_generation/workbench/wireframes/<screen>.notes.md",
      ],
      constraints: [
        "节点必须有稳定 id、类型、x/y/w/h、层级、状态、可复用标记",
        "顶部 UI 必须避开微信胶囊和刘海安全区",
        "按钮文字由游戏渲染，线框只保留文本槽位",
      ],
    };
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    showToast("AI 线框任务已复制");
  }

  async function copyImageTask(): Promise<void> {
    if (!selectedScreen) {
      showToast("请先新建或导入一个界面");
      return;
    }
    updateCurrentScreen();
    const screenReferences = currentScreenReferences();
    const styleScreens = uiScreens.map((screen) => ({
      id: screen.id,
      name: screen.name,
      category: screen.category,
      status: screen.status,
    }));
    const screenForTask = uiScreens.find((screen) => screen.id === selectedScreenId) ?? selectedScreen;
    const taskAssets = imageTaskAssets(screenForTask);
    const layoutContract = imageTaskLayoutContract(screenForTask);
    const payload = [
      {
        id: "screen-current-final",
        out: "art_generation/outputs/workbench/screen-current-final.png",
        inputs: {
          projectName,
          screen: {
            id: screenForTask.id,
            name: screenForTask.name,
            category: screenForTask.category,
          },
          canvas: { width: canvasWidth, height: canvasHeight, safeAreaProfile: projectConfig.safeAreaProfile },
          layoutContract,
          semanticTree: taskAssets.semanticTree,
          reusableStyle: {
            references: {
              project: projectReferences,
              screen: screenReferences,
            },
            styleRef,
            promptPack,
            applyToScreens: styleScreens,
          },
          wireframeAssets: taskAssets,
          wireframeNodes: taskAssets.nodes,
        },
          prompt: [
          `Generation mode: ${imageGenerationModeLabel(imageGenerationMode)} (${imageGenerationMode}).`,
          imageGenerationMode === "button-atlas"
            ? "Generate a transparent UI button and icon-button-base sprite sheet / atlas. Use layout references only for placement and style references only for button material, outline, highlights and shadows. No text, icons, background, scene, annotations or full UI screen."
            : imageGenerationMode === "status-atlas"
              ? "Generate one solid-background UI status icon and status label sprite sheet. Empty areas must be flat chroma key color with no grid, texture, glow, shadow or variation. No text, labels, asset ids, full UI screen or background scene."
              : "Generate a high-fidelity mobile game UI mockup from the provided wireframe coordinates.",
          `Project: ${projectName}. Screen: ${screenForTask.name}. Canvas: ${canvasWidth}x${canvasHeight}.`,
          `Style reference: ${screenReferences.style}. Layout reference: ${screenReferences.layout}.`,
          `Style prompt: ${screenReferences.prompt}.`,
          `Negative prompt: ${screenReferences.negative}.`,
          "Treat this as a reusable project style direction that can be migrated to the other listed screens.",
          "Preserve the current wireframe structure, hierarchy, safe areas, and interaction regions.",
          "No baked text inside reusable button/panel skins. Keep component silhouettes clean for slicing.",
        ].join("\n"),
      },
    ];
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    showToast("生图 tasks JSON 已复制");
  }

  function createApiTaskStatus(): ApiTaskStatus {
    return { loading: false, files: [], command: "", message: "", error: "", prompt: "", provider: "", model: "", plan: null, geometryReport: null };
  }

  function createApiTaskStatuses(): Record<ApiTaskKey, ApiTaskStatus> {
    return {
      wireframe: createApiTaskStatus(),
      image: createApiTaskStatus(),
      export: createApiTaskStatus(),
    };
  }

  function setApiTaskStatus(key: ApiTaskKey, patch: Partial<ApiTaskStatus>): void {
    apiTasks = {
      ...apiTasks,
      [key]: {
        ...apiTasks[key],
        ...patch,
      },
    };
  }

  function resetTaskStateForFigmaImport(): void {
    apiTaskEpoch += 1;
    apiTasks = createApiTaskStatuses();
    wireframeImportError = "";
  }

  function createCanvasPayload() {
    return {
      width: canvasWidth,
      height: canvasHeight,
      safeAreaProfile: projectConfig.safeAreaProfile,
    };
  }

  function hydrateWorkspaceFromSelectedScreen(): void {
    const screen = uiScreens.find((item) => item.id === selectedScreenId);
    if (!screen) {
      selectedAssetId = assets[0]?.assetId ?? "";
      return;
    }
    hydratingScreen = true;
    try {
      figmaUrl = screen.figmaUrl;
      canvasWidth = screen.width;
      canvasHeight = screen.height;
      layoutRef = screen.layoutRef;
      promptPack = screen.promptPack;
      finalOverlay = normalizeFinalOverlay(screen.finalOverlay, screen.referenceImageUrl);
      const references = normalizeReferenceSet(screen.references, {
        style: styleRef,
        layout: screen.layoutRef,
        images: screen.referenceImageUrl ? [{ id: "screen_final", url: screen.referenceImageUrl, label: "当前成稿", role: "reference" }] : [],
        prompt: screen.promptPack,
        negative: projectReferences.negative,
      });
      layoutRef = references.layout;
      promptPack = references.prompt;
      assets = cloneAssets(screen.assets ?? []);
      selectedAssetId = assets.find((asset) => !asset.hidden)?.assetId ?? "";
      stages = stagesFromStates(screen.stageStates);
      canvasRenderKey += 1;
      const nextActive = stages.find((stage) => stage.state === "active")?.id ?? "wireframe";
      setWorkflowStage(nextActive, { sync: false });
    } finally {
      hydratingScreen = false;
    }
  }

  function syncSelectedScreenFromWorkspace(options: { persist?: boolean; semanticTree?: SemanticTree } = {}): void {
    if (!selectedScreenId) return;
    const shouldPersist = options.persist ?? true;
    const nextStageStates = stageStatesFromStages();
    const normalizedOverlay = normalizeFinalOverlay(finalOverlay);
    const references = currentScreenReferences(normalizedOverlay);
    const currentScreen = uiScreens.find((screen) => screen.id === selectedScreenId) ?? selectedScreen;
    const syncedSemanticTree = semanticTreeForAssets(currentScreen, assets, options.semanticTree);
    uiScreens = uiScreens.map((screen) => screen.id === selectedScreenId
      ? {
          ...screen,
          width: canvasWidth,
          height: canvasHeight,
          figmaUrl,
          layoutRef: references.layout,
          promptPack: references.prompt,
          references,
          referenceImageUrl: screen.referenceImageUrl,
          finalOverlay: normalizedOverlay,
          status: screenStatusFromStageStates(nextStageStates, screen.status),
          stageStates: nextStageStates,
          semanticTree: syncedSemanticTree,
          assets: cloneAssets(assets),
        }
      : screen);
    if (shouldPersist) persistWorkspaceDraft();
  }

  function selectScreen(screenId: string): void {
    const screen = uiScreens.find((item) => item.id === screenId);
    if (!screen) return;
    if (screen.id === selectedScreenId) {
      void centerCanvasInView();
      return;
    }
    syncSelectedScreenFromWorkspace({ persist: false });
    selectedScreenId = screen.id;
    hydrateWorkspaceFromSelectedScreen();
    persistWorkspaceDraft();
    void centerCanvasInView();
    showToast(`已切换到 ${screen.name}`);
  }

  function toggleScreenBatchSelection(screenId: string): void {
    selectedScreenBatchIds = selectedScreenBatchIds.includes(screenId)
      ? selectedScreenBatchIds.filter((id) => id !== screenId)
      : [...selectedScreenBatchIds, screenId];
  }

  function requestDeleteScreen(screenId: string): void {
    const screen = uiScreens.find((item) => item.id === screenId);
    if (!screen) return;
    pendingDeleteScreenIds = [screen.id];
    modalMode = "deleteScreen";
  }

  function requestDeleteSelectedScreens(): void {
    const ids = selectedScreenBatchIds.filter((id) => uiScreens.some((screen) => screen.id === id));
    if (!ids.length) {
      showToast("请先选择要删除的界面");
      return;
    }
    pendingDeleteScreenIds = ids;
    modalMode = "deleteScreen";
  }

  function confirmDeleteScreens(): void {
    const deletingIds = pendingDeleteScreenIds.filter((id) => uiScreens.some((screen) => screen.id === id));
    if (!deletingIds.length) {
      pendingDeleteScreenIds = [];
      modalMode = "none";
      return;
    }
    const deletingCurrent = deletingIds.includes(selectedScreenId);
    if (!deletingCurrent) {
      syncSelectedScreenFromWorkspace({ persist: false });
    }
    const firstDeletedIndex = uiScreens.findIndex((item) => deletingIds.includes(item.id));
    const remainingScreens = uiScreens.filter((item) => !deletingIds.includes(item.id));
    uiScreens = remainingScreens;
    selectedScreenBatchIds = selectedScreenBatchIds.filter((id) => !deletingIds.includes(id));

    if (deletingCurrent) {
      const nextScreen = remainingScreens[Math.min(Math.max(0, firstDeletedIndex), remainingScreens.length - 1)] ?? null;
      selectedScreenId = nextScreen?.id ?? "";
      if (nextScreen) {
        hydrateWorkspaceFromSelectedScreen();
      } else {
        assets = [];
        selectedAssetId = "";
        finalOverlay = createFinalOverlay();
        stages = createDefaultStages();
        canvasRenderKey += 1;
      }
      void centerCanvasInView();
    }

    persistWorkspaceDraft();
    pendingDeleteScreenIds = [];
    modalMode = "none";
    showToast(`已删除 ${deletingIds.length} 个界面`);
  }

  function updateCurrentScreen(): void {
    syncSelectedScreenFromWorkspace();
  }

  function updateProjectStyleRef(): void {
    projectReferences = { ...projectReferences, style: styleRef };
    updateCurrentScreen();
  }

  function applyProjectPreset(preset: ProjectPreset): void {
    projectConfig = createProjectConfigFromPreset(preset.id, projectConfig);
    outputRoot = projectConfig.outputRoot;
    canvasWidth = projectConfig.canvas.width;
    canvasHeight = projectConfig.canvas.height;
    if (!projectName || projectName === "Untitled Project") projectName = preset.name;
    uiScreens = uiScreens.map((screen) => ({
      ...screen,
      width: projectConfig.canvas.width,
      height: projectConfig.canvas.height,
    }));
    updateCurrentScreen();
    showToast(`已切换到 ${preset.name} 预设`);
  }

  function addScreen(): void {
    const name = newScreenName.trim() || `新界面 ${uiScreens.length + 1}`;
    const base = name.replace(/[^a-zA-Z0-9\u4e00-\u9fa5]+/g, "_").toLowerCase();
    const id = uniqueScreenId(base);
    syncSelectedScreenFromWorkspace();
    const screen: UiScreen = {
      id,
      name,
      category: "未分类界面",
      width: canvasWidth,
      height: canvasHeight,
      figmaUrl,
      layoutRef,
      promptPack,
      references: currentScreenReferences(),
      referenceImageUrl: "",
      finalOverlay: createFinalOverlay(),
      status: "todo",
      stageStates: createDefaultStageStates(),
      assets: [],
    };
    uiScreens = [...uiScreens, screen];
    newScreenName = "";
    selectScreen(screen.id);
  }

  function imageTaskAssets(screen: UiScreen | null = selectedScreen) {
    const semanticTree = compactSemanticTree(semanticTreeForAssets(screen, assets));
    const nodes = assets.map((asset) => ({
      assetId: asset.assetId,
      name: asset.name,
      type: asset.type,
      rect: asset.rect,
      sourceRect: asset.sourceRect,
      targetSize: asset.targetSize,
      status: asset.status,
      statusGroup: asset.statusGroup,
      atlasSlot: asset.atlasSlot,
      textPolicy: asset.textPolicy,
      hidden: asset.hidden,
      exportEnabled: asset.exportEnabled,
      locked: asset.locked,
      figma: asset.figma ? {
        nodeId: asset.figma.nodeId,
        nodeName: asset.figma.nodeName,
        type: asset.figma.type,
        page: asset.figma.page,
        parentId: asset.figma.parentId,
        parentIds: asset.figma.parentIds,
        path: asset.figma.path,
        text: asset.figma.text,
        renderUrl: asset.figma.renderUrl,
      } : undefined,
    }));
    return {
      nodes,
      semanticTree,
      missing: assets.filter((asset) => !asset.finalPath || asset.status.includes("missing")),
      candidates: assets.filter((asset) => ["candidate", "needs-review", "layout"].includes(asset.status)),
    };
  }

  function imageTaskLayoutContract(screen: UiScreen) {
    const semanticTree = compactSemanticTree(semanticTreeForAssets(screen, assets));
    const nodes = imageTaskAssets(screen).nodes.map((asset) => ({
      id: asset.assetId,
      name: asset.name,
      type: asset.type,
      rect: asset.rect,
      sourceRect: asset.sourceRect,
      targetSize: asset.targetSize,
      hidden: asset.hidden,
      exportEnabled: asset.exportEnabled !== false,
      locked: asset.locked,
      figmaNodeId: asset.figma?.nodeId ?? "",
      figmaPath: asset.figma?.path ?? [],
      textPolicy: asset.textPolicy ?? null,
    }));
    return {
      version: 2,
      sourceOfTruth: "wireframe-assets",
      tolerancePx: 0,
      canvas: { width: canvasWidth, height: canvasHeight },
      layoutReferenceImageUrl: screen.referenceImageUrl || finalOverlay.src || "",
      rootFigmaUrl: screen.figmaUrl || figmaUrl,
      semanticSourceOfTruth: semanticTree ? "figma-semantic-tree" : "wireframe-assets",
      semanticTree,
      nodeCount: nodes.length,
      nodes,
      rules: [
        "Generated image must keep every node bounding box position and size unchanged.",
        "Generated image must follow the supplied semantic tree hierarchy, roles, labels, component intent, and parent-child relationships.",
        "Generated image must not add, remove, reorder, or resize UI regions.",
        "Style may change fills, outlines, lighting, icon art, and material only inside existing node boxes.",
        "Text must follow textPolicy and should not be baked into reusable buttons unless explicitly requested.",
      ],
    };
  }

  function imageGenerationModeLabel(mode: ImageGenerationMode): string {
    if (mode === "button-atlas") return "按钮图集";
    if (mode === "status-atlas") return "状态图集";
    return "整屏成稿";
  }

  function createImagePromptPreview(): string {
    if (!selectedScreen) return "";
    const screenForPreview = uiScreens.find((screen) => screen.id === selectedScreenId) ?? selectedScreen;
    const taskAssets = imageTaskAssets(screenForPreview);
    const nodes = taskAssets.nodes.slice(0, 80).map((asset) => ({
      id: asset.assetId,
      type: asset.type,
      rect: asset.rect,
      sourceRect: asset.sourceRect,
      targetSize: asset.targetSize,
      status: asset.status,
      atlasSlot: asset.atlasSlot,
      textPolicy: asset.textPolicy,
      hidden: asset.hidden,
      exportEnabled: asset.exportEnabled !== false,
    }));
    return [
      `Provider: PomoAI / gemini-3.1-flash-image`,
      `Mode: ${imageGenerationModeLabel(imageGenerationMode)} (${imageGenerationMode})`,
      `Project: ${projectName}`,
      `Screen: ${selectedScreen.name} (${selectedScreen.id})`,
      `Canvas: ${canvasWidth}x${canvasHeight} / ${projectConfig.safeAreaProfile}`,
      "",
      "Hard constraints:",
      "- Wireframe geometry is the source of truth.",
      "- Preserve node rectangles, element types, relative positions, safe areas and interaction regions.",
      "- Apply style to the existing wireframe; do not invent a new layout.",
      "- Reusable button/panel skins should avoid baked text unless the selected text policy allows it.",
      "",
      `Style reference: ${styleRef || "(empty)"}`,
      `Layout reference: ${layoutRef || "(empty)"}`,
      "",
      "User prompt:",
      promptPack || "(empty)",
      "",
      `Wireframe nodes (${nodes.length}/${taskAssets.nodes.length}):`,
      JSON.stringify(nodes, null, 2),
    ].join("\n");
  }

  async function postApiTask(key: ApiTaskKey, url: string, payload: unknown, successText: string): Promise<ApiTaskResponse | null> {
    const epoch = apiTaskEpoch;
    setApiTaskStatus(key, { loading: true, files: [], command: "", message: "", error: "", prompt: "", provider: "", model: "", plan: null, geometryReport: null });
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await readApiTaskResponse(response, url);
      if (!response.ok || !data.ok) {
        if (epoch !== apiTaskEpoch) return null;
        setApiTaskStatus(key, {
          loading: false,
          files: data.files ?? [],
          command: data.command ?? "",
          message: data.message ?? "",
          error: data.error ?? `${url} 调用失败`,
          prompt: data.prompt ?? "",
          provider: data.provider ?? "",
          model: data.model ?? "",
          plan: data.plan ?? null,
          geometryReport: data.geometryReport ?? null,
        });
        showToast(data.error ?? "后端任务调用失败");
        return null;
      }
      if (epoch !== apiTaskEpoch) return null;
      setApiTaskStatus(key, {
        loading: false,
        files: data.files ?? [],
        command: data.command ?? "",
        message: data.message ?? "",
        error: "",
        prompt: data.prompt ?? "",
        provider: data.provider ?? "",
        model: data.model ?? "",
        plan: data.plan ?? null,
        geometryReport: data.geometryReport ?? null,
      });
      showToast(data.message ?? successText);
      return data;
    } catch (error) {
      if (epoch !== apiTaskEpoch) return null;
      setApiTaskStatus(key, {
        loading: false,
        files: [],
        command: "",
        message: "",
        prompt: "",
        provider: "",
        model: "",
        geometryReport: null,
        error: error instanceof Error ? error.message : `${url} 调用失败`,
      });
      showToast("后端任务调用失败");
      return null;
    }
  }

  async function submitWireframeTask(): Promise<void> {
    if (!selectedScreen) {
      showToast("请先新建或导入一个界面");
      return;
    }
    updateCurrentScreen();
    const data = await postApiTask("wireframe", "/api/wireframe/task", {
      project: projectName,
      screen: selectedScreen,
      canvas: createCanvasPayload(),
      figma: figmaInfo,
      styleRef,
      layoutRef,
      promptPack,
    }, "线框任务已提交");
    if (!data) return;
    const generatedNodes = data.nodes ?? data.assets ?? [];
    if (data.canvas?.width) canvasWidth = Number(data.canvas.width);
    if (data.canvas?.height) canvasHeight = Number(data.canvas.height);
    if (data.screen?.name || data.screen?.category) {
      uiScreens = uiScreens.map((screen) => screen.id === selectedScreenId
        ? {
            ...screen,
            name: String(data.screen?.name ?? screen.name),
            category: String(data.screen?.category ?? screen.category),
          }
        : screen);
    }
    if (Array.isArray(generatedNodes) && generatedNodes.length) {
      assets = generatedNodes.map((item, index) => normalizeAsset(item, index, { source: "wireframe-json", selectedScreenId, outputRoot }));
      selectedAssetId = assets[0]?.assetId ?? "";
      syncSelectedScreenFromWorkspace();
    }
    completeStage("wireframe", "style");
  }

  async function submitAllWireframeTasks(): Promise<void> {
    if (!uiScreens.length) {
      showToast("请先新建或导入界面");
      return;
    }
    syncSelectedScreenFromWorkspace();
    batchGeneratingWireframes = true;
    const epoch = apiTaskEpoch;
    setApiTaskStatus("wireframe", { loading: true, files: [], command: "", message: "", error: "" });
    const files: string[] = [];
    try {
      for (const screen of uiScreens) {
        const response = await fetch("/api/wireframe/task", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project: projectName,
            screen,
            canvas: { width: screen.width, height: screen.height, safeAreaProfile: projectConfig.safeAreaProfile },
            figma: parseFigmaUrl(screen.figmaUrl),
            styleRef,
            layoutRef: screen.layoutRef,
            promptPack: screen.promptPack,
          }),
        });
        const data = await readApiTaskResponse(response, "/api/wireframe/task");
        if (!response.ok || !data.ok) throw new Error(data.error ?? `${screen.name} 线框任务失败`);
        files.push(...(data.files ?? []));
      }
      if (epoch !== apiTaskEpoch) return;
      setApiTaskStatus("wireframe", {
        loading: false,
        files,
        command: "batch wireframe tasks",
        message: `已为 ${uiScreens.length} 个界面生成线框任务`,
        error: "",
      });
      showToast(`已生成 ${uiScreens.length} 个界面线框任务`);
    } catch (error) {
      if (epoch !== apiTaskEpoch) return;
      setApiTaskStatus("wireframe", {
        loading: false,
        files,
        command: "",
        message: "",
        error: error instanceof Error ? error.message : "批量线框任务失败",
      });
      showToast("批量线框任务失败");
    } finally {
      batchGeneratingWireframes = false;
    }
  }

  async function submitImageTask(): Promise<void> {
    if (!selectedScreen) {
      showToast("请先新建或导入一个界面");
      return;
    }
    updateCurrentScreen();
    const screenForTask = uiScreens.find((screen) => screen.id === selectedScreenId) ?? selectedScreen;
    const data = await postApiTask("image", "/api/image/task", {
      project: projectName,
      screen: {
        ...screenForTask,
        width: canvasWidth,
        height: canvasHeight,
        layoutRef,
        promptPack,
        finalOverlay,
        assets: cloneAssets(assets),
      },
      stage: activeStage,
      generationMode: imageGenerationMode,
      canvas: createCanvasPayload(),
      styleRef,
      layoutRef,
      promptPack,
      references: currentScreenReferences(),
      layoutContract: imageTaskLayoutContract(screenForTask),
      layoutReferenceImageUrl: screenForTask.referenceImageUrl || finalOverlay.src || "",
      assets: imageTaskAssets(screenForTask),
    }, "生图任务已提交");
    if (data?.imageDataUrl) {
      finalOverlay = createFinalOverlay(data.imageDataUrl, { ...finalOverlay, src: data.imageDataUrl, visible: true, fit: "fill", scale: 1, offsetX: 0, offsetY: 0 });
      syncSelectedScreenFromWorkspace();
    }
    if (data) setWorkflowStage("style");
  }

  async function submitExportPackage(): Promise<void> {
    updateCurrentScreen();
    if (blockingBuildIssues.length) {
      setApiTaskStatus("export", {
        loading: false,
        files: [],
        command: "",
        message: "",
        error: `存在 ${blockingBuildIssues.length} 个阻塞错误，修复后才能写入或同步。${blockingBuildSummary}`,
        plan: null,
      });
      modalMode = "buildReport";
      showToast("存在阻塞错误，已打开质量检查");
      return;
    }
    const manifest = createManifest();
    const data = await postApiTask("export", "/api/files/dry-run", {
      manifest,
      assets: manifest.assets,
      canvas: manifest.canvas,
      outputRoot: manifest.outputRoot,
      blockingIssues: blockingBuildIssues,
    }, "Dry-run 已生成");
    if (!data) return;
    if (data.plan?.summary.overwrite) {
      pendingDryRunPlan = data.plan;
      fileConflictConfirmed = false;
      setApiTaskStatus("export", {
        message: `发现 ${data.plan.summary.overwrite} 个覆盖冲突，确认后才允许后续写入。`,
        error: "",
      });
      modalMode = "fileConflict";
      showToast("发现覆盖冲突，等待确认");
      return;
    }
    pendingDryRunPlan = data.plan ?? null;
    fileConflictConfirmed = true;
    showToast("已生成写入预检清单");
  }

  async function writeStagingPackage(): Promise<void> {
    updateCurrentScreen();
    if (blockingBuildIssues.length) {
      setApiTaskStatus("export", {
        loading: false,
        files: [],
        command: "",
        message: "",
        error: `存在 ${blockingBuildIssues.length} 个阻塞错误，修复后才能输出。${blockingBuildSummary}`,
        plan: null,
      });
      modalMode = "buildReport";
      showToast("存在阻塞错误，已打开质量检查");
      return;
    }
    const manifest = createManifest();
    const data = await postApiTask("export", "/api/files/write-staging", {
      manifest,
      assets: manifest.assets,
      canvas: manifest.canvas,
      outputRoot: manifest.outputRoot,
      blockingIssues: blockingBuildIssues,
      extractionMasks: adoptedExtractionMasks,
    }, "Staging 输出已完成");
    if (!data) return;
    pendingDryRunPlan = data.plan ?? null;
    fileConflictConfirmed = false;
    showToast("真实文件已输出到 staging");
  }

  // D：assetIds 为空导出全部；传选中子集时只打包这些元素，且阻塞检查只看子集相关问题。
  async function downloadSliceZip(assetIds: string[] | null = null): Promise<void> {
    updateCurrentScreen();
    const wanted = assetIds?.filter(Boolean) ?? null;
    const relevantBlocking = wanted?.length
      ? blockingBuildIssues.filter((issue) => !issue.assetId || wanted.includes(issue.assetId))
      : blockingBuildIssues;
    if (relevantBlocking.length) {
      modalMode = "buildReport";
      showToast("存在阻塞错误，修复后再打包下载");
      return;
    }
    const manifest = createManifest();
    sliceZipLoading = true;
    try {
      const response = await fetch("/api/files/zip", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          manifest,
          assets: manifest.assets,
          canvas: manifest.canvas,
          outputRoot: manifest.outputRoot,
          blockingIssues: relevantBlocking,
          extractionMasks: adoptedExtractionMasks,
          assetIds: wanted?.length ? wanted : undefined,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({} as { error?: string }));
        showToast((data as { error?: string }).error ?? "切图打包失败");
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = wanted?.length
        ? `${selectedScreen?.id ?? "artflow"}-selected-${wanted.length === 1 ? wanted[0] : wanted.length}.zip`
        : `${selectedScreen?.id ?? "artflow"}-slices.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showToast("切图 ZIP 已下载");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "切图打包失败");
    } finally {
      sliceZipLoading = false;
    }
  }

  // D：单元素直接下载提取结果透明 PNG（客户端保存，零服务端调用）。
  function downloadEntryPng(assetId: string): void {
    if (!assetId) {
      showToast("先选中一个元素");
      return;
    }
    const src = extractEntries[assetId]?.cutoutDataUrl
      || assets.find((asset) => asset.assetId === assetId)?.dataUrl
      || "";
    if (!src) {
      showToast("该元素还没有提取结果，先做 AI 提取");
      return;
    }
    const link = document.createElement("a");
    link.href = src;
    link.download = `${assetId}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    showToast(`已下载 ${assetId}.png`);
  }

  // ---- AI 元素提取(切图阶段) -------------------------------------------------
  // 本地 SAM/LaMa 推理，零生成成本；唯一计费操作是「提示词二次处理」，必须显式确认费用。

  function createExtractPayload(): Record<string, unknown> {
    const manifest = createManifest();
    return {
      manifest,
      assets: manifest.assets,
      semanticTree: manifest.semanticTree,
      screenId: selectedScreenId || manifest.screen?.id || "",
    };
  }

  async function readExtractResponse(response: Response, url: string): Promise<ExtractScreenResponse> {
    const raw = await response.text();
    try {
      return JSON.parse(raw) as ExtractScreenResponse;
    } catch (_error) {
      throw new Error(`${url} 返回了无效 JSON：${raw.trim().slice(0, 80) || response.statusText}`);
    }
  }

  // 首次调用会自动拉起本地视觉 sidecar，可能需要 ~10 秒。
  async function ensureExtractHealth(): Promise<boolean> {
    if (extractHealthOk) return true;
    try {
      const response = await fetch("/api/extract/health");
      const data = await readExtractResponse(response, "/api/extract/health");
      if (!data.ok) {
        extractError = data.error ?? "本地视觉模型启动失败";
        return false;
      }
      extractHealthOk = true;
      return true;
    } catch (error) {
      extractError = error instanceof Error ? error.message : "提取服务不可用";
      return false;
    }
  }

  function mergeExtractEntries(entries: ExtractEntry[]): void {
    if (!entries.length) return;
    const next = { ...extractEntries };
    for (const entry of entries) next[entry.assetId] = entry;
    extractEntries = next;
  }

  async function runScreenExtraction(): Promise<void> {
    if (!selectedScreen) {
      showToast("请先新建或导入一个界面");
      return;
    }
    if (!hasFinalOverlay) {
      showToast("请先导入成稿源图，再做 AI 提取");
      return;
    }
    updateCurrentScreen();
    extractScreenLoading = true;
    extractError = "";
    extractMessage = "";
    try {
      if (!(await ensureExtractHealth())) {
        showToast(extractError || "提取服务不可用");
        return;
      }
      const response = await fetch("/api/extract/screen", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(createExtractPayload()),
      });
      const data = await readExtractResponse(response, "/api/extract/screen");
      if (!response.ok || !data.ok) {
        extractError = data.error ?? "整屏提取失败";
        showToast(extractError);
        return;
      }
      mergeExtractEntries(data.entries ?? []);
      extractNotes = data.notes ?? [];
      extractMessage = data.message ?? "整屏提取完成";
      showToast(extractMessage);
    } catch (error) {
      extractError = error instanceof Error ? error.message : "整屏提取失败";
      showToast(extractError);
    } finally {
      extractScreenLoading = false;
    }
  }

  // 重新提取单个元素；处于点修正模式时带上正/负点(成稿图像素坐标)。
  async function runSingleExtraction(assetId: string): Promise<void> {
    if (!hasFinalOverlay) {
      showToast("请先导入成稿源图，再做 AI 提取");
      return;
    }
    updateCurrentScreen();
    extractSingleLoadingId = assetId;
    extractError = "";
    try {
      if (!(await ensureExtractHealth())) {
        showToast(extractError || "提取服务不可用");
        return;
      }
      const payload = createExtractPayload();
      payload.assetId = assetId;
      if (pointEditAssetId === assetId && (pointEditPositive.length || pointEditNegative.length)) {
        payload.positivePoints = pointEditPositive;
        payload.negativePoints = pointEditNegative;
      }
      const response = await fetch("/api/extract/single", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await readExtractResponse(response, "/api/extract/single");
      if (!response.ok || !data.ok) {
        extractError = data.error ?? "元素提取失败";
        showToast(extractError);
        return;
      }
      mergeExtractEntries(data.entries ?? []);
      if (data.notes?.length) extractNotes = data.notes;
      const entry = (data.entries ?? []).find((item) => item.assetId === assetId);
      if (!entry) {
        showToast(`服务端没有返回 ${assetId} 的结果（背景层/纯文字元素不参与提取）`);
        return;
      }
      showToast(entry.ok ? `已重新提取 ${assetId}` : entry.error ?? `提取 ${assetId} 失败`);
    } catch (error) {
      extractError = error instanceof Error ? error.message : "元素提取失败";
      showToast(extractError);
    } finally {
      extractSingleLoadingId = "";
    }
  }

  async function ensureFinalOverlayNaturalSize(): Promise<{ w: number; h: number } | null> {
    const src = finalOverlay.src;
    if (!src) return null;
    if (finalOverlayNaturalSize && finalOverlayNaturalSrc === src) return finalOverlayNaturalSize;
    const image = await new Promise<HTMLImageElement | null>((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });
    if (!image?.naturalWidth || src !== finalOverlay.src) return null;
    finalOverlayNaturalSize = { w: image.naturalWidth, h: image.naturalHeight };
    finalOverlayNaturalSrc = src;
    return finalOverlayNaturalSize;
  }

  function startPointEdit(assetId: string): void {
    if (!hasFinalOverlay) {
      showToast("请先导入成稿源图，再做点修正");
      return;
    }
    pointEditAssetId = assetId;
    pointEditPolarity = "positive";
    pointEditPositive = [];
    pointEditNegative = [];
    selectAsset(assetId);
    void ensureFinalOverlayNaturalSize();
  }

  function exitPointEdit(): void {
    pointEditAssetId = "";
    pointEditPositive = [];
    pointEditNegative = [];
  }

  // 画布点击 → 画布坐标 → 成稿像素坐标(成稿像素 = 画布坐标 × 成稿宽/画布宽)。
  function handlePointEditPointerDown(event: PointerEvent): void {
    if (!pointEditAssetId || !phoneFrameElement) return;
    const natural = finalOverlayNaturalSize;
    if (!natural) {
      void ensureFinalOverlayNaturalSize();
      showToast("正在读取成稿图尺寸，请稍后再点");
      return;
    }
    const bounds = phoneFrameElement.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const canvasX = ((event.clientX - bounds.left) / bounds.width) * canvasWidth;
    const canvasY = ((event.clientY - bounds.top) / bounds.height) * canvasHeight;
    const px = Math.round(canvasX * (natural.w / Math.max(1, canvasWidth)));
    const py = Math.round(canvasY * (natural.h / Math.max(1, canvasHeight)));
    const point: [number, number] = [
      Math.max(0, Math.min(natural.w - 1, px)),
      Math.max(0, Math.min(natural.h - 1, py)),
    ];
    if (pointEditPolarity === "positive") {
      pointEditPositive = [...pointEditPositive, point];
    } else {
      pointEditNegative = [...pointEditNegative, point];
    }
  }

  // 成稿像素坐标 → 画布显示坐标，给点标记定位用。
  function pointMarkerStyle(point: [number, number], displayWidth: number, displayHeight: number, natural: { w: number; h: number } | null): string {
    if (!natural) return "display:none";
    const left = (point[0] / Math.max(1, natural.w)) * displayWidth;
    const top = (point[1] / Math.max(1, natural.h)) * displayHeight;
    return `left:${left}px;top:${top}px`;
  }

  // 采用当前提取结果：记录 mask(导出时通过 extractionMasks 附带，优先级最高)。
  function adoptExtractionMask(assetId: string): void {
    const entry = extractEntries[assetId];
    if (!entry?.maskDataUrl) {
      showToast("该元素还没有可采用的提取 mask");
      return;
    }
    adoptedExtractionMasks = { ...adoptedExtractionMasks, [assetId]: entry.maskDataUrl };
    patchAsset(assetId, { extractionMode: "segmentation-mask" });
    showToast(`已采用 ${assetId} 的提取结果，导出时生效`);
  }

  // 恢复硬切：放弃 AI 提取，导出时按矩形直接裁。
  function restoreRectExtraction(assetId: string): void {
    if (adoptedExtractionMasks[assetId]) {
      const next = { ...adoptedExtractionMasks };
      delete next[assetId];
      adoptedExtractionMasks = next;
    }
    patchAsset(assetId, { extractionMode: "rect" });
    if (pointEditAssetId === assetId) exitPointEdit();
    showToast(`${assetId} 已恢复为硬切`);
  }

  // C：切换提取目标类型(前景整体/本体去文字/仅文字层/背后背景)后立即重新提取。
  function setExtractionTarget(assetId: string, value: string): void {
    const target = (EXTRACTION_TARGETS as readonly string[]).includes(value)
      ? value as AssetExtractionTarget
      : "foreground";
    patchAsset(assetId, { extractionTarget: target === "foreground" ? undefined : target });
    if (hasFinalOverlay) void runSingleExtraction(assetId);
  }

  function toggleAssetBackground(assetId: string): void {
    const asset = assets.find((item) => item.assetId === assetId);
    if (!asset) return;
    const nextValue = !asset.isBackground;
    patchAsset(assetId, { isBackground: nextValue });
    showToast(nextValue ? `${assetId} 已标记为背景层` : `${assetId} 已取消背景层标记`);
  }

  async function runBackgroundPreview(): Promise<void> {
    if (!selectedScreen) {
      showToast("请先新建或导入一个界面");
      return;
    }
    if (!hasFinalOverlay) {
      showToast("请先导入成稿源图，再做背景补全");
      return;
    }
    updateCurrentScreen();
    backgroundPreviewLoading = true;
    extractError = "";
    try {
      if (!(await ensureExtractHealth())) {
        showToast(extractError || "提取服务不可用");
        return;
      }
      const response = await fetch("/api/extract/background", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(createExtractPayload()),
      });
      const raw = await response.text();
      let data: { ok?: boolean; backgroundDataUrl?: string; pipeline?: string; message?: string; error?: string };
      try {
        data = JSON.parse(raw);
      } catch (_error) {
        throw new Error(`/api/extract/background 返回了无效 JSON：${raw.trim().slice(0, 80)}`);
      }
      if (!response.ok || !data.ok || !data.backgroundDataUrl) {
        extractError = data.error ?? "背景补全失败";
        showToast(extractError);
        return;
      }
      backgroundPreviewUrl = data.backgroundDataUrl;
      backgroundPreviewPipeline = data.pipeline ?? "";
      showToast(data.message ?? "背景层补全完成");
    } catch (error) {
      extractError = error instanceof Error ? error.message : "背景补全失败";
      showToast(extractError);
    } finally {
      backgroundPreviewLoading = false;
    }
  }

  function openRefinePrompt(assetId: string): void {
    const entry = extractEntries[assetId];
    if (!entry?.cutoutDataUrl) {
      showToast("先提取该元素，才能做提示词二次处理");
      return;
    }
    refineTargetAssetId = assetId;
    refinePromptText = "";
    modalMode = "extractRefine";
  }

  // ⚠️ 唯一计费操作：云端 images/edits。只能从确认弹窗里触发。
  async function submitRefinePrompt(): Promise<void> {
    const entry = extractEntries[refineTargetAssetId];
    const prompt = refinePromptText.trim();
    if (!entry?.cutoutDataUrl || !prompt) {
      showToast("需要已提取的元素图和修改提示词");
      return;
    }
    refineLoading = true;
    try {
      const response = await fetch("/api/extract/refine-prompt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          imageDataUrl: entry.cutoutDataUrl,
          prompt,
          maskDataUrl: entry.maskDataUrl || undefined,
        }),
      });
      const raw = await response.text();
      let data: { ok?: boolean; imageDataUrl?: string; message?: string; error?: string };
      try {
        data = JSON.parse(raw);
      } catch (_error) {
        throw new Error(`/api/extract/refine-prompt 返回了无效 JSON：${raw.trim().slice(0, 80)}`);
      }
      if (!response.ok || !data.ok || !data.imageDataUrl) {
        showToast(data.error ?? "提示词二次处理失败");
        return;
      }
      extractEntries = {
        ...extractEntries,
        [refineTargetAssetId]: { ...entry, cutoutDataUrl: data.imageDataUrl, pipeline: "refine-prompt" },
      };
      // 结果存为 asset.dataUrl：导出时服务端直接用二次处理结果，不再从成稿裁切。
      patchAsset(refineTargetAssetId, { dataUrl: data.imageDataUrl });
      modalMode = "none";
      showToast(data.message ?? "二次处理完成，结果已存入该元素");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "提示词二次处理失败");
    } finally {
      refineLoading = false;
    }
  }

  // 置信度徽章：≥0.8 绿 / 0.6-0.8 黄 / <0.6 红。
  function extractionConfidenceBadge(confidence?: number): string {
    if (confidence === undefined || confidence === null || Number.isNaN(confidence)) return "badge";
    if (confidence >= 0.8) return "badge conf-high";
    if (confidence >= 0.6) return "badge conf-mid";
    return "badge conf-low";
  }

  function confirmFileConflicts(): void {
    if (!pendingDryRunPlan) {
      modalMode = "none";
      return;
    }
    fileConflictConfirmed = true;
    setApiTaskStatus("export", {
      message: `已确认 ${pendingDryRunPlan.summary.overwrite} 个覆盖冲突，后续写入接口可继续执行。`,
      error: "",
      plan: pendingDryRunPlan,
    });
    modalMode = "none";
    showToast("覆盖冲突已确认");
  }

  function cancelFileConflicts(): void {
    fileConflictConfirmed = false;
    setApiTaskStatus("export", {
      loading: false,
      files: [],
      command: "",
      message: "",
      error: "已取消覆盖确认，未写入任何文件。",
      plan: pendingDryRunPlan,
    });
    modalMode = "none";
    showToast("已取消写入确认");
  }

  // 同步到游戏：先 dry-run 预检冲突，确认后再真实写入游戏工程目录。
  async function previewGameSync(): Promise<void> {
    updateCurrentScreen();
    if (blockingBuildIssues.length) {
      modalMode = "buildReport";
      showToast("存在阻塞错误，修复后再同步到游戏");
      return;
    }
    const manifest = createManifest();
    gameSyncLoading = true;
    gameSyncError = "";
    gameSyncMessage = "";
    gameSyncDone = false;
    try {
      const data = await requestGameSync(manifest, true);
      if (!data.ok) {
        gameSyncError = data.error ?? "游戏资源同步预检失败";
        gameSyncPlan = null;
      } else {
        gameSyncPlan = (data.plan as GameSyncPlan) ?? null;
        gameSyncMessage = data.message ?? "";
      }
    } catch (error) {
      gameSyncError = error instanceof Error ? error.message : "游戏资源同步预检失败";
      gameSyncPlan = null;
    } finally {
      gameSyncLoading = false;
      modalMode = "gameSync";
    }
  }

  async function confirmGameSync(): Promise<void> {
    const manifest = createManifest();
    gameSyncLoading = true;
    gameSyncError = "";
    try {
      const data = await requestGameSync(manifest, false);
      if (!data.ok) {
        gameSyncError = data.error ?? "同步到游戏失败";
        return;
      }
      gameSyncPlan = (data.plan as GameSyncPlan) ?? gameSyncPlan;
      gameSyncMessage = data.message ?? "已同步到游戏";
      gameSyncDone = true;
      showToast("已同步到游戏工程");
    } catch (error) {
      gameSyncError = error instanceof Error ? error.message : "同步到游戏失败";
    } finally {
      gameSyncLoading = false;
    }
  }

  async function requestGameSync(manifest: ReturnType<typeof createManifest>, dryRun: boolean): Promise<{ ok: boolean; error?: string; message?: string; plan?: GameSyncPlan }> {
    const response = await fetch("/api/files/sync-game-assets", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        manifest,
        assets: manifest.assets,
        canvas: manifest.canvas,
        outputRoot: manifest.outputRoot,
        blockingIssues: blockingBuildIssues,
        dryRun,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok && !data.error) {
      return { ok: false, error: `同步接口返回 ${response.status}` };
    }
    return data;
  }

  function figmaFrameType(frame: FigmaFrame): AssetType {
    if (frame.type === "FRAME" || frame.type === "GROUP" || frame.type === "SECTION" || frame.type === "RECTANGLE") return "panel";
    if (frame.type === "TEXT") return "image";
    if (frame.name.toLowerCase().includes("button") || frame.name.includes("按钮")) return "button";
    if (frame.name.toLowerCase().includes("icon") || frame.name.includes("图标")) return "icon";
    return "image";
  }

  function figmaFrameToAsset(frame: FigmaFrame, origin: { x: number; y: number }, index = 0, usedIds?: Set<string>): ArtAsset {
    const box = frame.box ?? { x: 120, y: 240, w: 240, h: 120 };
    let safeId = uniqueAssetId(sanitizeId(frame.name, `figma_node_${index + 1}`));
    if (usedIds) {
      const base = safeId;
      let suffix = 2;
      while (usedIds.has(safeId)) {
        safeId = `${base}_${suffix}`;
        suffix += 1;
      }
      usedIds.add(safeId);
    }
    const rect = {
      x: Math.max(0, Math.round(box.x - origin.x)),
      y: Math.max(0, Math.round(box.y - origin.y)),
      w: Math.max(8, Math.round(box.w)),
      h: Math.max(8, Math.round(box.h)),
    };
    return {
      assetId: safeId,
      name: frame.name,
      type: figmaFrameType(frame),
      source: "figma",
      rect,
      sourceRect: { ...rect },
      targetSize: { w: Math.max(8, Math.round(box.w)), h: Math.max(8, Math.round(box.h)) },
      trim: zeroInsets(),
      padding: zeroInsets(),
      atlas: "figma_imported",
      finalPath: `figma/${safeId}.png`,
      status: "figma-imported",
      figma: {
        fileKey: figmaInfo.fileKey,
        fileUrl: figmaInfo.url,
        nodeId: frame.id,
        nodeName: frame.name,
        name: frame.name,
        type: frame.type,
        page: frame.page,
        parentId: frame.parentId,
        parentIds: frame.parentIds,
        path: frame.path,
        text: frame.text,
        style: frame.style,
        imageRef: frame.imageRef ?? frame.style?.imageRef ?? frame.style?.image?.imageRef,
        scaleMode: frame.scaleMode ?? frame.style?.scaleMode ?? frame.style?.image?.scaleMode,
        renderUrl: figmaRenderImages[frame.id] ?? frame.renderUrl,
      },
    };
  }

  function figmaFrameSemanticRole(frame: FigmaFrame): string {
    const name = frame.name.toLowerCase();
    if (frame.type === "TEXT") return "runtime_text";
    if (name.includes("button") || frame.name.includes("按钮")) return "button";
    if (name.includes("icon") || frame.name.includes("图标")) return "icon";
    if (["FRAME", "GROUP", "SECTION", "INSTANCE", "COMPONENT"].includes(frame.type)) return "container";
    return figmaFrameType(frame);
  }

  function figmaFrameToSemanticNode(frame: FigmaFrame, root: FigmaFrame, assetByNodeId: Map<string, ArtAsset>): SemanticTreeNode {
    const rootBox = root.box ?? { x: 0, y: 0, w: canvasWidth, h: canvasHeight };
    const box = frame.box;
    const rect = box ? {
      x: Math.max(0, Math.round(box.x - rootBox.x)),
      y: Math.max(0, Math.round(box.y - rootBox.y)),
      w: Math.max(0, Math.round(box.w)),
      h: Math.max(0, Math.round(box.h)),
    } : undefined;
    const asset = assetByNodeId.get(frame.id);
    const children = figmaFrames
      .filter((item) => item.parentId === frame.id && item.id !== frame.id)
      .map((child) => figmaFrameToSemanticNode(child, root, assetByNodeId));
    return {
      id: frame.id,
      name: frame.name,
      type: frame.type,
      role: figmaFrameSemanticRole(frame),
      rect,
      sourceRect: rect ? { ...rect } : undefined,
      assetId: asset?.assetId,
      parentId: frame.parentId,
      path: frame.path,
      text: frame.text,
      textPolicy: asset?.textPolicy ?? (frame.type === "TEXT" ? "separate-runtime-text" : undefined),
      figma: {
        nodeId: frame.id,
        nodeName: frame.name,
        type: frame.type,
        page: frame.page,
        style: frame.style,
        imageRef: frame.imageRef ?? frame.style?.imageRef ?? frame.style?.image?.imageRef,
        scaleMode: frame.scaleMode ?? frame.style?.scaleMode ?? frame.style?.image?.scaleMode,
        renderUrl: figmaRenderImages[frame.id] ?? frame.renderUrl,
      },
      children,
    };
  }

  function createFigmaSemanticTree(root: FigmaFrame, assetsForScreen: ArtAsset[]): SemanticTree {
    const box = root.box ?? { x: 0, y: 0, w: canvasWidth, h: canvasHeight };
    const assetByNodeId = new Map(assetsForScreen.map((asset) => [asset.figma?.nodeId ?? "", asset]).filter(([nodeId]) => nodeId));
    return {
      version: 1,
      source: "figma",
      rootId: root.id,
      rootName: root.name,
      canvas: { width: Math.max(320, Math.round(box.w)), height: Math.max(320, Math.round(box.h)) },
      nodes: [figmaFrameToSemanticNode(root, root, assetByNodeId)],
    };
  }

  function compactSemanticTree(tree: SemanticTree | undefined): SemanticTree | undefined {
    if (!tree) return undefined;
    const compactNode = (node: SemanticTreeNode): SemanticTreeNode | null => {
      const children = node.children.map(compactNode).filter(Boolean) as SemanticTreeNode[];
      const style = node.figma?.style as any;
      return {
        id: node.id,
        name: node.name,
        type: node.type,
        role: node.role,
        rect: node.rect,
        sourceRect: node.sourceRect,
        assetId: node.assetId,
        parentId: node.parentId,
        path: node.path,
        text: node.text,
        textPolicy: node.textPolicy,
        figma: {
          nodeId: node.figma?.nodeId,
          nodeName: node.figma?.nodeName,
          type: node.figma?.type,
          page: node.figma?.page,
          style: style ? {
            fill: style.fill,
            stroke: style.stroke,
            strokeWeight: style.strokeWeight,
            radius: style.radius,
            fontFamily: style.fontFamily,
            fontSize: style.fontSize,
            fontWeight: style.fontWeight,
            imageRef: style.imageRef ?? style.image?.imageRef,
            scaleMode: style.scaleMode ?? style.image?.scaleMode,
            effectsCss: style.effectsCss,
          } : undefined,
          imageRef: node.figma?.imageRef,
          scaleMode: node.figma?.scaleMode,
          renderUrl: node.figma?.renderUrl,
        },
        children,
      };
    };
    return {
      version: 1,
      source: tree.source,
      rootId: tree.rootId,
      rootName: tree.rootName,
      canvas: tree.canvas,
      nodes: tree.nodes.map(compactNode).filter(Boolean) as SemanticTreeNode[],
    };
  }

  function semanticTreeWithAssetState(tree: SemanticTree | undefined, sourceAssets: ArtAsset[]): SemanticTree | undefined {
    if (!tree) return undefined;
    const byNodeId = new Map<string, ArtAsset>();
    const byAssetId = new Map<string, ArtAsset>();
    for (const asset of sourceAssets) {
      if (asset.figma?.nodeId) byNodeId.set(asset.figma.nodeId, asset);
      if (asset.assetId) byAssetId.set(asset.assetId, asset);
    }
    const syncNode = (node: SemanticTreeNode): SemanticTreeNode => {
      const asset = byAssetId.get(node.assetId ?? "") || byNodeId.get(node.figma?.nodeId ?? node.id);
      const rect = asset ? { ...asset.rect } : node.rect;
      return {
        ...node,
        name: asset?.name || node.name,
        rect,
        sourceRect: asset ? { ...asset.sourceRect } : node.sourceRect,
        assetId: asset?.assetId ?? node.assetId,
        textPolicy: asset?.textPolicy ?? node.textPolicy,
        figma: node.figma ? {
          ...node.figma,
          nodeName: asset?.figma?.nodeName ?? node.figma.nodeName,
          renderUrl: asset?.figma?.renderUrl ?? node.figma.renderUrl,
        } : node.figma,
        children: node.children.map(syncNode),
      };
    };
    return {
      ...tree,
      canvas: { width: canvasWidth, height: canvasHeight },
      nodes: tree.nodes.map(syncNode),
    };
  }

  function semanticRoleForAsset(asset: ArtAsset): string {
    const figmaType = asset.figma?.type?.toUpperCase();
    if (figmaType === "TEXT") return "runtime_text";
    if (asset.type === "button") return "button";
    if (asset.type === "icon") return "icon";
    if (asset.type === "panel") return "container";
    return asset.type;
  }

  function createAssetSemanticTree(screen: UiScreen | null, sourceAssets: ArtAsset[]): SemanticTree | undefined {
    if (!screen || !sourceAssets.length) return undefined;
    const rootId = screen.id || selectedScreenId || "screen";
    return {
      version: 1,
      source: "artflow",
      rootId,
      rootName: screen.name || rootId,
      canvas: { width: canvasWidth, height: canvasHeight },
      nodes: sourceAssets.map((asset): SemanticTreeNode => ({
        id: asset.figma?.nodeId || asset.assetId,
        name: asset.name || asset.figma?.nodeName || asset.assetId,
        type: asset.figma?.type || asset.type,
        role: semanticRoleForAsset(asset),
        rect: { ...asset.rect },
        sourceRect: { ...asset.sourceRect },
        assetId: asset.assetId,
        parentId: asset.figma?.parentId,
        path: asset.figma?.path,
        text: asset.figma?.text,
        textPolicy: asset.textPolicy ?? (asset.figma?.type === "TEXT" ? "separate-runtime-text" : undefined),
        figma: asset.figma ? {
          nodeId: asset.figma.nodeId,
          nodeName: asset.figma.nodeName,
          type: asset.figma.type,
          page: asset.figma.page,
          style: asset.figma.style,
          imageRef: asset.figma.imageRef,
          scaleMode: asset.figma.scaleMode,
          renderUrl: asset.figma.renderUrl,
        } : undefined,
        children: [],
      })),
    };
  }

  function semanticTreeForAssets(screen: UiScreen | null, sourceAssets: ArtAsset[], preferredTree?: SemanticTree): SemanticTree | undefined {
    return semanticTreeWithAssetState(preferredTree ?? screen?.semanticTree, sourceAssets)
      ?? createAssetSemanticTree(screen, sourceAssets);
  }

  function selectedFigmaFrames(): FigmaFrame[] {
    const ids = new Set(figmaSelectedFrameIds);
    return figmaFrames.filter((frame) => ids.has(frame.id) && frame.box);
  }

  function figmaSelectionBounds(frames: FigmaFrame[]): { x: number; y: number; w: number; h: number } {
    const boxes = frames.map((frame) => frame.box).filter(Boolean) as Array<{ x: number; y: number; w: number; h: number }>;
    if (!boxes.length) return { x: 0, y: 0, w: canvasWidth, h: canvasHeight };
    const minX = Math.min(...boxes.map((box) => box.x));
    const minY = Math.min(...boxes.map((box) => box.y));
    const maxX = Math.max(...boxes.map((box) => box.x + box.w));
    const maxY = Math.max(...boxes.map((box) => box.y + box.h));
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }

  function toggleFigmaFrame(frameId: string): void {
    figmaPreviewFrameId = frameId;
    figmaSelectedFrameIds = figmaSelectedFrameIds.includes(frameId)
      ? figmaSelectedFrameIds.filter((id) => id !== frameId)
      : [...figmaSelectedFrameIds, frameId];
    persistFigmaImportState();
  }

  function selectFigmaFrame(frameId: string, additive = false): void {
    figmaPreviewFrameId = frameId;
    if (additive) {
      figmaSelectedFrameIds = figmaSelectedFrameIds.includes(frameId)
        ? figmaSelectedFrameIds.filter((id) => id !== frameId)
        : [...figmaSelectedFrameIds, frameId];
      persistFigmaImportState();
      return;
    }
    figmaSelectedFrameIds = [frameId];
    persistFigmaImportState();
  }

  function setFigmaPage(pageName: string): void {
    const pageTopFrames = figmaTopFrames.filter((frame) => frame.page === pageName);
    const nextFrame = pageTopFrames[0];
    if (!nextFrame) return;
    figmaPreviewFrameId = nextFrame.id;
    figmaSelectedFrameIds = pageTopFrames.map((frame) => frame.id);
    resetFigmaCanvasView();
  }

  function resetFigmaCanvasView(): void {
    figmaCanvasZoom = 1;
    figmaCanvasPan = { x: 96, y: 64 };
    persistFigmaImportState();
  }

  function figmaCanvasWorldStyle(panX: number, panY: number, zoom: number, boundsW: number, boundsH: number): string {
    const padding = 220;
    const width = Math.max(900, Math.round(boundsW * FIGMA_BOARD_SCALE + padding));
    const height = Math.max(720, Math.round(boundsH * FIGMA_BOARD_SCALE + padding));
    return `width:${width}px;height:${height}px;transform:translate(${Math.round(panX)}px, ${Math.round(panY)}px) scale(${zoom});`;
  }

  function figmaCanvasBoardStyle(frame: FigmaFrame): string {
    const box = frame.box;
    if (!box) return "";
    const x = Math.round((box.x - figmaImportCanvasBounds.x) * FIGMA_BOARD_SCALE + 80);
    const y = Math.round((box.y - figmaImportCanvasBounds.y) * FIGMA_BOARD_SCALE + 72);
    const width = Math.max(24, Math.round(box.w * FIGMA_BOARD_SCALE));
    const height = Math.max(24, Math.round(box.h * FIGMA_BOARD_SCALE));
    return `left:${x}px;top:${y}px;width:${width}px;height:${height}px;`;
  }

  function figmaCanvasNodeStyle(frame: FigmaFrame, root: FigmaFrame): string {
    return figmaRenderNodeStyleAtScale(frame, root, FIGMA_BOARD_SCALE);
  }

  function setFigmaCanvasZoom(nextZoom: number): void {
    figmaCanvasZoom = Math.max(0.25, Math.min(3, Number(nextZoom.toFixed(2))));
    persistFigmaImportState();
  }

  function defaultCanvasViewModeForStage(stageId: WorkflowStageId): CanvasViewMode {
    if (stageId === "wireframe") return "wireframe";
    if (stageId === "style") return "final";
    return "overlay";
  }

  function setCanvasViewMode(mode: CanvasViewMode): void {
    if (mode !== "wireframe" && !hasFinalOverlay) {
      showToast("当前界面还没有成稿图");
      return;
    }
    canvasViewMode = mode;
  }

  function zoomFigmaCanvas(event: WheelEvent): void {
    const delta = event.deltaY > 0 ? -0.08 : 0.08;
    setFigmaCanvasZoom(figmaCanvasZoom + delta);
  }

  function startFigmaCanvasPan(event: PointerEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target || target.closest("button,input,label,.figma-canvas-frame,.figma-preview-board")) return;
    if (target !== event.currentTarget && !target.classList.contains("figma-canvas-world")) return;
    figmaCanvasPanning = true;
    figmaCanvasLastPointer = { x: event.clientX, y: event.clientY };
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  function moveFigmaCanvasPan(event: PointerEvent): void {
    if (!figmaCanvasPanning) return;
    const dx = event.clientX - figmaCanvasLastPointer.x;
    const dy = event.clientY - figmaCanvasLastPointer.y;
    figmaCanvasPan = { x: figmaCanvasPan.x + dx, y: figmaCanvasPan.y + dy };
    figmaCanvasLastPointer = { x: event.clientX, y: event.clientY };
  }

  function stopFigmaCanvasPan(event: PointerEvent): void {
    if (!figmaCanvasPanning) return;
    figmaCanvasPanning = false;
    (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
    persistFigmaImportState();
  }

  function selectAllFigmaFrames(): void {
    figmaSelectedFrameIds = figmaTopFrames.map((frame) => frame.id);
    persistFigmaImportState();
  }

  function selectTopFigmaFrames(): void {
    figmaSelectedFrameIds = figmaPageTopFrames.map((frame) => frame.id);
    persistFigmaImportState();
  }

  function clearFigmaSelection(): void {
    figmaSelectedFrameIds = [];
    persistFigmaImportState();
  }

  function setFigmaParseWholeFile(nextValue: boolean): void {
    figmaParseWholeFile = nextValue;
    resetFigmaParseState();
  }

  function previewFigmaFrame(frameId: string): void {
    figmaPreviewFrameId = frameId;
    persistFigmaImportState();
  }

  function handleFigmaFrameKeydown(event: KeyboardEvent, frameId: string): void {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    if (event.key === " ") {
      toggleFigmaFrame(frameId);
      return;
    }
    previewFigmaFrame(frameId);
  }

  function figmaLayerTreeRows(root: FigmaFrame): FigmaLayerTreeRow[] {
    const rows: FigmaLayerTreeRow[] = [];
    const visit = (parentId: string, depth: number) => {
      for (const frame of figmaFrames.filter((item) => item.parentId === parentId)) {
        rows.push({ frame, depth });
        visit(frame.id, depth + 1);
      }
    };
    visit(root.id, 1);
    return rows;
  }

  function figmaLayerIcon(frame: FigmaFrame): string {
    if (frame.type === "TEXT") return "T";
    if (frame.type === "GROUP" || frame.type === "FRAME" || frame.type === "SECTION") return "⋯";
    return "▭";
  }

  async function importSelectedFigmaToCurrentCanvas(): Promise<void> {
    if (!selectedScreen) {
      showToast("请先新建界面，或使用“导入为界面”");
      return;
    }
    const frames = selectedFigmaFrames();
    if (!frames.length) {
      showToast("请先选择要导入的 Figma 节点");
      return;
    }
    const root = frames.find((frame) => frame.parentIds?.length === Math.min(...frames.map((item) => item.parentIds?.length ?? 0))) ?? frames[0];
    const rootBox = root.box ?? figmaSelectionBounds(frames);
    const importFrames = figmaDescendantsForScreen(root);
    const framesForAssets = importFrames.length ? importFrames : frames.filter((frame) => frame.id !== root.id);
    const bounds = { x: rootBox.x, y: rootBox.y, w: rootBox.w, h: rootBox.h };
    const usedIds = new Set<string>();
    assets = framesForAssets.map((frame, index) => ({
      ...figmaFrameToAsset(frame, bounds, index, usedIds),
      exportEnabled: figmaFrameExportEnabled(frame, root),
    }));
    const semanticTree = createFigmaSemanticTree(root, assets);
    selectedAssetId = assets.find((asset) => !asset.hidden)?.assetId ?? "";
    const referenceFrame = root;
    // 渲染底图只是预览叠层，属尽力而为；即使它失败也必须保住真实 assets + 语义树，
    // 否则一张预览图没就绪就会把真实几何数据整个丢掉，导致后续生图缺语义树被阻塞。
    let src = "";
    try {
      src = await ensureFigmaRenderImage(referenceFrame);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Figma 渲染底图获取失败，已仅导入线框结构");
    }
    if (src) {
      finalOverlay = createFinalOverlay(src, { ...finalOverlay, src, visible: true, fit: "fill", opacity: 1, scale: 1, offsetX: 0, offsetY: 0 });
    }
    canvasViewMode = src ? "final" : "wireframe";
    resetTaskStateForFigmaImport();
    completeStage("wireframe", "style");
    uiScreens = uiScreens.map((screen) => screen.id === selectedScreenId ? {
      ...screen,
      referenceImageUrl: src || screen.referenceImageUrl,
    } : screen);
    syncSelectedScreenFromWorkspace({ semanticTree });
    modalMode = "none";
    showToast(src
      ? `已导入 ${assets.length} 个 Figma 节点到当前画板`
      : `已导入 ${assets.length} 个 Figma 节点（线框结构），渲染底图未就绪可稍后重试`);
  }

  function figmaChildrenForScreen(root: FigmaFrame): FigmaFrame[] {
    const rootBox = root.box;
    const directChildren = figmaFrames.filter((frame) => frame.id !== root.id && frame.box && frame.parentId === root.id);
    if (!rootBox) return directChildren.length ? directChildren : [root];
    const isFullScreenContainer = (frame: FigmaFrame): boolean => {
      const box = frame.box;
      if (!box) return false;
      return box.w >= rootBox.w * 0.9 && box.h >= rootBox.h * 0.9 && Math.abs(box.x - rootBox.x) <= 4 && Math.abs(box.y - rootBox.y) <= 4;
    };
    const visibleDirect = directChildren.filter((frame) => !isFullScreenContainer(frame));
    if (visibleDirect.length >= 2) return visibleDirect;
    if (directChildren.length === 1 && ["FRAME", "GROUP", "SECTION", "INSTANCE", "COMPONENT"].includes(directChildren[0].type)) {
      const inner = figmaFrames.filter((frame) => frame.id !== directChildren[0].id && frame.box && frame.parentId === directChildren[0].id);
      return inner.length ? inner : directChildren;
    }
    return directChildren.length ? directChildren : [root];
  }

  function figmaDescendantsForScreen(root: FigmaFrame): FigmaFrame[] {
    const rootBox = root.box;
    if (!rootBox) return [];
    const isDescendant = (frame: FigmaFrame): boolean => frame.parentIds?.includes(root.id) || frame.parentId === root.id;
    const isFullScreenContainer = (frame: FigmaFrame): boolean => {
      const box = frame.box;
      if (!box) return false;
      return box.w >= rootBox.w * 0.94 && box.h >= rootBox.h * 0.94 && Math.abs(box.x - rootBox.x) <= 4 && Math.abs(box.y - rootBox.y) <= 4;
    };
    return figmaFrames
      .filter((frame) => frame.id !== root.id && frame.box && isDescendant(frame))
      .filter((frame) => !isFullScreenContainer(frame))
      .sort((a, b) => {
        const depthDelta = (a.parentIds?.length ?? 0) - (b.parentIds?.length ?? 0);
        if (depthDelta) return depthDelta;
        return (a.box?.y ?? 0) - (b.box?.y ?? 0) || (a.box?.x ?? 0) - (b.box?.x ?? 0);
      });
  }

  function figmaFrameExportEnabled(frame: FigmaFrame, root: FigmaFrame): boolean {
    if (frame.type === "TEXT") return true;
    const rootBox = root.box;
    const box = frame.box;
    if (!rootBox || !box) return false;
    const role = figmaFrameSemanticRole(frame);
    if (role === "container" && frame.childCount > 0 && box.w >= rootBox.w * 0.85 && box.h >= rootBox.h * 0.25) return false;
    return true;
  }

  async function importSelectedFigmaAsScreens(): Promise<void> {
    const selectedIds = new Set(figmaSelectedFrameIds);
    const roots = selectedFigmaFrames()
      .filter((frame) => frame.box)
      .filter((frame) => frame.depth === 1)
      .filter((frame) => !frame.parentIds?.some((parentId) => selectedIds.has(parentId)));
    if (!roots.length) {
      showToast("请先选择当前页顶层 Figma 画板，子图层不能作为界面导入");
      return;
    }
    syncSelectedScreenFromWorkspace();
    const usedScreenIds = new Set(uiScreens.map((screen) => screen.id));
    let rootRenderImages: Record<string, string> = {};
    try {
      rootRenderImages = await fetchFigmaRenderImages(roots.map((root) => root.id));
      figmaRenderImages = { ...figmaRenderImages, ...rootRenderImages };
      figmaFrames = figmaFrames.map((frame) => ({ ...frame, renderUrl: rootRenderImages[frame.id] ?? frame.renderUrl }));
      persistFigmaImportState();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Figma 官方渲染图获取失败");
      return;
    }
    const missingRoots = roots.filter((root) => !(rootRenderImages[root.id] ?? root.renderUrl));
    if (missingRoots.length) {
      showToast(`有 ${missingRoots.length} 个 Figma 画板缺少官方渲染图，已阻止导入，避免线框和风格页不一致`);
      return;
    }
    const importedScreens: UiScreen[] = roots.map((root) => {
      const box = root.box!;
      const screenId = uniqueScreenIdInSet(root.name, usedScreenIds);
      const childFrames = figmaDescendantsForScreen(root);
      const usedIds = new Set<string>();
      const screenAssets = childFrames.map((frame, index) => ({
        ...figmaFrameToAsset(frame, { x: box.x, y: box.y }, index, usedIds),
        hidden: true,
        exportEnabled: figmaFrameExportEnabled(frame, root),
      }));
      const renderSrc = rootRenderImages[root.id] ?? root.renderUrl ?? "";
      return {
        id: screenId,
        name: root.name,
        category: root.page,
        width: Math.max(320, Math.round(box.w)),
        height: Math.max(320, Math.round(box.h)),
        figmaUrl,
        layoutRef,
        promptPack,
        references: normalizeReferenceSet(undefined, {
          style: styleRef,
          layout: layoutRef,
          prompt: promptPack,
          negative: projectReferences.negative,
          images: renderSrc
            ? [{ id: "screen_final", url: renderSrc, label: root.name, role: "reference" }]
            : [],
        }),
        referenceImageUrl: renderSrc,
        finalOverlay: createFinalOverlay(renderSrc, {
          visible: true,
          locked: true,
          opacity: 1,
          fit: "fill",
          scale: 1,
          offsetX: 0,
          offsetY: 0,
        }),
        status: "todo",
        stageStates: createDefaultStageStates("style"),
        semanticTree: createFigmaSemanticTree(root, screenAssets),
        assets: screenAssets,
      };
    });
    uiScreens = [...uiScreens, ...importedScreens];
    resetTaskStateForFigmaImport();
    selectScreen(importedScreens[0].id);
    canvasViewMode = "final";
    modalMode = "none";
    showToast(`已从 Figma 导入 ${importedScreens.length} 个界面`);
  }

  function addAssetFromFigma(frame: FigmaFrame, event?: Event): void {
    event?.stopPropagation();
    if (!selectedScreen) {
      showToast("请先新建界面，或使用“导入为界面”");
      return;
    }
    const bounds = figmaSelectionBounds([frame]);
    const asset = figmaFrameToAsset(frame, bounds);
    assets = [...assets, asset];
    selectedAssetId = asset.assetId;
    resetTaskStateForFigmaImport();
    syncSelectedScreenFromWorkspace();
    showToast("已从 Figma 节点生成元素");
  }

  function selectAsset(assetId: string): void {
    selectedAssetId = assetId;
  }

  function toggleMarqueeMode(): void {
    if (!selectedScreen) {
      showToast("请先新建或导入一个界面");
      return;
    }
    marqueeMode = !marqueeMode;
    marqueeDrag = null;
    showToast(marqueeMode ? "框选模式已开启" : "框选模式已关闭");
  }

  function startMarquee(event: PointerEvent): void {
    if (!marqueeMode || event.target !== event.currentTarget) return;
    const point = canvasPointFromClient(event.clientX, event.clientY);
    if (!point) return;
    event.preventDefault();
    selectedAssetId = "";
    marqueeDrag = { start: point, current: point };
    ignoreCanvasClickUntil = Date.now() + 350;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  function selectCanvas(): void {
    if (Date.now() < ignoreCanvasClickUntil) {
      return;
    }
    selectedAssetId = "";
  }

  function handleCanvasClick(event: MouseEvent): void {
    if (event.target !== event.currentTarget) return;
    if (Date.now() < ignoreCanvasClickUntil) return;
  }

  function handleCanvasKeydown(event: KeyboardEvent): void {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    selectCanvas();
  }

  function setWorkflowStage(stageId: WorkflowStageId, options: { sync?: boolean } = {}): void {
    const nextStage: WorkflowStageId = stageId;
    activeStage = nextStage;
    currentView = nextStage === "slice" ? "atlas" : "screen";
    canvasViewMode = defaultCanvasViewModeForStage(nextStage);
    const activeIndex = stages.findIndex((stage) => stage.id === nextStage);
    stages = stages.map((stage, index) => ({
      ...stage,
      state: index < activeIndex ? "done" : index === activeIndex ? "active" : stage.state === "done" ? "done" : "todo",
    }));
    if ((options.sync ?? true) && !hydratingScreen) syncSelectedScreenFromWorkspace();
  }

  function completeStage(stageId: WorkflowStageId, nextStage?: WorkflowStageId): void {
    const target = nextStage ?? stageId;
    const targetIndex = stages.findIndex((stage) => stage.id === target);
    stages = stages.map((stage, index) => {
      if (stage.id === stageId && !nextStage) return { ...stage, state: "done" };
      if (nextStage) return { ...stage, state: index < targetIndex ? "done" : index === targetIndex ? "active" : stage.state === "done" ? "done" : "todo" };
      return stage;
    });
    if (nextStage) {
      activeStage = target;
      currentView = target === "slice" ? "atlas" : "screen";
      canvasViewMode = defaultCanvasViewModeForStage(target);
    }
    syncSelectedScreenFromWorkspace();
  }

  function assetClass(asset: ArtAsset, currentSelectedAssetId: string): string {
    return `node ${asset.type} ${asset.assetId === currentSelectedAssetId ? "selected" : ""} ${asset.locked ? "locked" : ""}`;
  }

  function statusClass(status: string): string {
    if (status === "extract-ready") return "badge good";
    if (status === "needs-review") return "badge warn";
    if (status === "candidate") return "badge blue";
    return "badge";
  }

  function assetDisplayName(asset: ArtAsset): string {
    return asset.name?.trim() || asset.figma?.name || asset.assetId.replace(/[_-]+/g, " ");
  }

  function assetLayerDepth(asset: ArtAsset): number {
    const figmaDepth = asset.figma?.parentIds?.length ?? asset.figma?.path?.length;
    if (typeof figmaDepth === "number" && Number.isFinite(figmaDepth)) return Math.max(0, Math.min(5, figmaDepth - 1));
    return 0;
  }

  function assetLayerLabel(asset: ArtAsset, index: number): string {
    const order = String(index + 1).padStart(2, "0");
    return `${order} · ${asset.type}`;
  }

  function toggleAssetHidden(assetId: string): void {
    assets = assets.map((asset) => asset.assetId === assetId ? { ...asset, hidden: !asset.hidden } : asset);
    if (selectedAssetId === assetId && assets.find((asset) => asset.assetId === assetId)?.hidden) selectedAssetId = "";
    syncSelectedScreenFromWorkspace();
  }

  function toggleSelectedExportEnabled(): void {
    if (!selectedAsset) return;
    assets = assets.map((asset) => asset.assetId === selectedAsset.assetId
      ? { ...asset, exportEnabled: !(asset.exportEnabled ?? true) }
      : asset);
    syncSelectedScreenFromWorkspace();
  }

  function toggleAssetLocked(assetId: string): void {
    assets = assets.map((asset) => asset.assetId === assetId ? { ...asset, locked: !asset.locked } : asset);
    syncSelectedScreenFromWorkspace();
  }

  function patchAsset(assetId: string, patch: Partial<ArtAsset>): void {
    assets = assets.map((asset) => asset.assetId === assetId ? { ...asset, ...patch } : asset);
    syncSelectedScreenFromWorkspace();
  }

  function renameSelectedAssetId(value: string): void {
    if (!selectedAsset) return;
    const nextId = value.trim();
    const currentId = selectedAsset.assetId;
    assets = assets.map((asset) => asset.assetId === currentId ? { ...asset, assetId: nextId } : asset);
    selectedAssetId = nextId;
    syncSelectedScreenFromWorkspace();
  }

  function nodeStyle(asset: ArtAsset, displayWidth: number, displayHeight: number): string {
    const sx = displayWidth / canvasWidth;
    const sy = displayHeight / canvasHeight;
    return [
      `left:${asset.rect.x * sx}px`,
      `top:${asset.rect.y * sy}px`,
      `width:${asset.rect.w * sx}px`,
      `height:${asset.rect.h * sy}px`,
    ].join(";");
  }

  function canvasDisplayWidth(): number {
    return canvasDisplayWidthValue;
  }

  function canvasDisplayHeight(): number {
    return canvasDisplayHeightValue;
  }

  function setCanvasZoom(nextZoom: number): void {
    canvasZoom = Math.max(0.2, Math.min(1.6, Number(nextZoom.toFixed(2))));
    if (canvasAutoCenter) void centerCanvasInView();
  }

  function zoomCanvas(event: WheelEvent): void {
    const direction = event.deltaY > 0 ? -1 : 1;
    const step = event.shiftKey ? 0.05 : 0.1;
    setCanvasZoom(canvasZoom + direction * step);
  }

  function fitCanvasToView(): void {
    if (!canvasWrapElement) {
      setCanvasZoom(1);
      return;
    }
    const padding = 56;
    const availableWidth = Math.max(240, canvasWrapElement.clientWidth - padding);
    const availableHeight = Math.max(320, canvasWrapElement.clientHeight - padding);
    const nextZoom = Math.min(1.6, Math.max(0.2, Math.min(availableWidth / canvasPreviewBaseWidthValue, availableHeight / canvasPreviewBaseHeightValue)));
    canvasZoom = Number(nextZoom.toFixed(2));
    if (canvasAutoCenter) void centerCanvasInView();
  }

  async function centerCanvasInView(): Promise<void> {
    await tick();
    const wrap = canvasWrapElement;
    if (!wrap) return;
    wrap.scrollTo({
      left: Math.max(0, (wrap.scrollWidth - wrap.clientWidth) / 2),
      top: Math.max(0, (wrap.scrollHeight - wrap.clientHeight) / 2),
      behavior: "auto",
    });
  }

  function toggleCanvasAutoCenter(): void {
    canvasAutoCenter = !canvasAutoCenter;
    if (canvasAutoCenter) void centerCanvasInView();
  }

  function createManifest() {
    return createArtFlowDocument({
      projectName,
      selectedScreenId,
      canvasWidth,
      canvasHeight,
      figmaInfo,
      projectConfig,
      projectReferences,
      styleRef,
      layoutRef,
      promptPack,
      outputRoot,
      uiScreens,
      assets,
    });
  }

  function createEmptyAsset(): ArtAsset {
    return {
      assetId: `asset_${Date.now().toString(36)}`,
      name: "新元素",
      type: "button",
      source: "manual",
      rect: { x: 160, y: 560, w: 180, h: 72 },
      sourceRect: { x: 160, y: 560, w: 180, h: 72 },
      targetSize: { w: 180, h: 72 },
      trim: zeroInsets(),
      padding: zeroInsets(),
      atlas: "physical_atlas_buttons_combined",
      finalPath: "common/buttons/asset_new.png",
      status: "layout",
      nineSlice: defaultNineSliceFor("button"),
      statusGroup: defaultStatusGroupFor("button"),
    };
  }

  function createAssetByType(type: AssetType): ArtAsset {
    const presets: Record<AssetType, { w: number; h: number; atlas: string; path: string }> = {
      panel: { w: 320, h: 220, atlas: "ui_panels", path: "panels" },
      button: { w: 220, h: 72, atlas: "ui_buttons", path: "buttons" },
      icon: { w: 96, h: 96, atlas: "ui_icons", path: "icons" },
      image: { w: 240, h: 160, atlas: "ui_images", path: "images" },
    };
    const preset = presets[type];
    const id = uniqueAssetId(`${screenPrefix(selectedScreenId || "screen")}_${type}`);
    const x = Math.max(0, Math.round((canvasWidth - preset.w) / 2));
    const y = Math.max(0, Math.round((canvasHeight - preset.h) / 2));
    const rect = { x, y, w: preset.w, h: preset.h };
    return {
      assetId: id,
      name: `${type}_${assets.length + 1}`,
      type,
      source: "manual",
      rect,
      sourceRect: { ...rect },
      targetSize: { w: preset.w, h: preset.h },
      trim: zeroInsets(),
      padding: zeroInsets(),
      atlas: preset.atlas,
      finalPath: `${screenPrefix(selectedScreenId || "screen")}/${preset.path}/${id}.png`,
      status: "layout",
      nineSlice: defaultNineSliceFor(type),
      statusGroup: defaultStatusGroupFor(type),
    };
  }

  function createAssetFromRect(type: MarqueeAssetType, rect: Rect): ArtAsset {
    const id = uniqueAssetId(`${screenPrefix(selectedScreenId || "screen")}_${type}`);
    const atlasByType: Record<MarqueeAssetType, string> = {
      panel: "ui_panels",
      button: "ui_buttons",
      icon: "ui_icons",
    };
    const pathByType: Record<MarqueeAssetType, string> = {
      panel: "panels",
      button: "buttons",
      icon: "icons",
    };
    return {
      assetId: id,
      name: `${type}_${assets.length + 1}`,
      type,
      source: "marquee",
      rect,
      sourceRect: { ...rect },
      targetSize: { w: rect.w, h: rect.h },
      trim: zeroInsets(),
      padding: zeroInsets(),
      atlas: atlasByType[type],
      finalPath: `${screenPrefix(selectedScreenId || "screen")}/${pathByType[type]}/${id}.png`,
      status: "layout",
      nineSlice: defaultNineSliceFor(type),
      statusGroup: defaultStatusGroupFor(type),
    };
  }

  function addQuickAsset(type: AssetType): void {
    if (!selectedScreen) {
      showToast("请先新建或导入一个界面");
      return;
    }
    const asset = createAssetByType(type);
    assets = [...assets, asset];
    selectedAssetId = asset.assetId;
    syncSelectedScreenFromWorkspace();
    showToast(`已新增 ${asset.assetId}`);
    // B：切图阶段新增元素即时走 SAM 语义提取。
    if (activeStage === "slice" && hasFinalOverlay) void runSingleExtraction(asset.assetId);
  }

  function clampAssetToCanvas(asset: ArtAsset): ArtAsset {
    let x = Math.max(0, Math.min(canvasWidth - asset.rect.w, asset.rect.x));
    let y = Math.max(0, Math.min(canvasHeight - asset.rect.h, asset.rect.y));
    if (snapToGrid) {
      x = Math.round(x / 4) * 4;
      y = Math.round(y / 4) * 4;
    }
    return { ...asset, rect: { ...asset.rect, x, y } };
  }

  function createAssetAtCanvasPoint(type: AssetType, clientX: number, clientY: number): ArtAsset | null {
    if (!phoneFrameElement) return null;
    const bounds = phoneFrameElement.getBoundingClientRect();
    if (clientX < bounds.left || clientX > bounds.right || clientY < bounds.top || clientY > bounds.bottom) return null;
    const asset = createAssetByType(type);
    const x = ((clientX - bounds.left) / bounds.width) * canvasWidth - asset.rect.w / 2;
    const y = ((clientY - bounds.top) / bounds.height) * canvasHeight - asset.rect.h / 2;
    return clampAssetToCanvas({ ...asset, rect: { ...asset.rect, x, y } });
  }

  function addAssetAtCanvasPoint(type: AssetType, clientX: number, clientY: number): boolean {
    if (!selectedScreen) {
      showToast("请先新建或导入一个界面");
      return false;
    }
    const asset = createAssetAtCanvasPoint(type, clientX, clientY);
    if (!asset) {
      showToast("拖到画布内创建元素");
      return false;
    }
    assets = [...assets, asset];
    selectedAssetId = asset.assetId;
    syncSelectedScreenFromWorkspace();
    showToast(`已放置 ${asset.assetId}`);
    // B：切图阶段拖放新增元素即时走 SAM 语义提取。
    if (activeStage === "slice" && hasFinalOverlay) void runSingleExtraction(asset.assetId);
    return true;
  }

  function startPaletteDrag(event: PointerEvent, type: AssetType): void {
    if (!selectedScreen) {
      showToast("请先新建或导入一个界面");
      return;
    }
    paletteDragType = type;
    paletteDragStart = { x: event.clientX, y: event.clientY };
    paletteDragMoved = false;
    suppressPaletteClick = false;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  function movePaletteDrag(event: PointerEvent): void {
    if (!paletteDragType) return;
    const distance = Math.hypot(event.clientX - paletteDragStart.x, event.clientY - paletteDragStart.y);
    if (distance > 6) paletteDragMoved = true;
  }

  function endPaletteDrag(event: PointerEvent): void {
    if (!paletteDragType) return;
    const type = paletteDragType;
    const moved = paletteDragMoved;
    paletteDragType = "";
    paletteDragMoved = false;
    if (moved) {
      suppressPaletteClick = true;
      addAssetAtCanvasPoint(type, event.clientX, event.clientY);
    }
  }

  function handlePaletteToolClick(type: AssetType): void {
    if (suppressPaletteClick) {
      suppressPaletteClick = false;
      return;
    }
    addQuickAsset(type);
  }

  function showToast(text: string): void {
    toast = text;
    window.setTimeout(() => {
      if (toast === text) toast = "";
    }, 1800);
  }

  function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ""));
      reader.onerror = () => reject(reader.error ?? new Error("图片读取失败"));
      reader.readAsDataURL(file);
    });
  }

  async function onImageImport(event: Event): Promise<void> {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (finalOverlay.locked) {
      input.value = "";
      showToast("成稿图已锁定");
      return;
    }
    try {
      const src = await readFileAsDataUrl(file);
      // 提取链路(cropAssetFromScreenRoot)只支持 fill/scale=1/offset=0 的成稿映射,
      // 导入时直接规整,否则 AI 提取会整体报「变换暂不支持」。
      finalOverlay = createFinalOverlay(src, { ...finalOverlay, src, visible: true, fit: "fill", scale: 1, offsetX: 0, offsetY: 0 });
      syncSelectedScreenFromWorkspace();
      modalMode = "none";
      showToast("成稿图已导入，可按线框提取元素");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "成稿图导入失败");
    } finally {
      input.value = "";
    }
  }

  function toggleFinalOverlayVisible(): void {
    if (!hasFinalOverlay) {
      showToast("请先导入成稿图");
      return;
    }
    finalOverlay = { ...finalOverlay, visible: !finalOverlay.visible };
    syncSelectedScreenFromWorkspace();
  }

  function toggleFinalOverlayLocked(): void {
    if (!hasFinalOverlay) {
      showToast("请先导入成稿图");
      return;
    }
    finalOverlay = { ...finalOverlay, locked: !finalOverlay.locked };
    syncSelectedScreenFromWorkspace();
  }

  function updateFinalOverlayOpacity(event: Event): void {
    if (finalOverlay.locked) {
      showToast("成稿图已锁定");
      return;
    }
    finalOverlay = { ...finalOverlay, opacity: clampNumber(Number((event.currentTarget as HTMLInputElement).value), 0, 1) };
    syncSelectedScreenFromWorkspace();
  }

  function updateFinalOverlayFit(event: Event): void {
    if (finalOverlay.locked) {
      showToast("成稿图已锁定");
      return;
    }
    const fit = String((event.currentTarget as HTMLSelectElement).value);
    finalOverlay = { ...finalOverlay, fit: ["cover", "contain", "fill"].includes(fit) ? fit as FinalOverlayFit : "cover" };
    syncSelectedScreenFromWorkspace();
  }

  function updateFinalOverlayNumber(field: "scale" | "offsetX" | "offsetY", value: number): void {
    if (finalOverlay.locked) {
      showToast("成稿图已锁定");
      return;
    }
    finalOverlay = {
      ...finalOverlay,
      [field]: field === "scale" ? clampNumber(value, 0.2, 3) : value,
    };
    syncSelectedScreenFromWorkspace();
  }

  function resetFinalOverlayAlignment(): void {
    if (finalOverlay.locked) {
      showToast("成稿图已锁定");
      return;
    }
    finalOverlay = { ...finalOverlay, fit: "fill", scale: 1, offsetX: 0, offsetY: 0 };
    syncSelectedScreenFromWorkspace();
    showToast("成稿图已按画布对齐");
  }

  function markAiFinal(): void {
    if (activeStage === "style") {
      completeStage("style", "slice");
      showToast("成稿源图已锁定，进入切图整理");
      return;
    }
    showToast("已进入元素提取阶段");
  }

  async function copyPromptPack(): Promise<void> {
    const screenReferences = currentScreenReferences();
    const payload = {
      references: {
        project: projectReferences,
        screen: screenReferences,
      },
      prompt: screenReferences.prompt,
      negative: screenReferences.negative,
    };
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    showToast("AI 参考包已复制");
  }

  async function copyTaskDiagnostics(status: ApiTaskStatus): Promise<void> {
    const lines = [
      `阶段：${activeStage}`,
      status.provider || status.model ? `Provider：${[status.provider, status.model].filter(Boolean).join(" · ")}` : "",
      status.command ? `Command：${status.command}` : "",
      status.error ? `Error：${status.error}` : "",
      status.message ? `Message：${status.message}` : "",
    ].filter(Boolean);
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      showToast("报错信息已复制");
    } catch (_error) {
      showToast("复制失败，请手动选择文本");
    }
  }

  function updateSelected(mutator: (asset: ArtAsset) => ArtAsset): void {
    if (!selectedAsset) return;
    assets = assets.map((asset) => asset.assetId === selectedAssetId ? mutator(asset) : asset);
    syncSelectedScreenFromWorkspace();
  }

  function toggleSelectedNineSlice(enabled: boolean): void {
    updateSelected((asset) => ({
      ...asset,
      nineSlice: enabled ? asset.nineSlice ?? defaultNineSliceFor(asset.type) ?? zeroInsets() : undefined,
    }));
  }

  function updateSelectedNineSlice(field: keyof NineSlice, value: number): void {
    updateSelected((asset) => ({
      ...asset,
      nineSlice: {
        ...(asset.nineSlice ?? defaultNineSliceFor(asset.type) ?? zeroInsets()),
        [field]: value,
      },
    }));
  }

  function toggleSelectedStatusGroup(enabled: boolean): void {
    updateSelected((asset) => ({
      ...asset,
      statusGroup: enabled ? asset.statusGroup ?? defaultStatusGroupFor(asset.type) ?? { group: "default_group", key: "default", states: ["normal"] } : undefined,
    }));
  }

  function updateSelectedStatusGroup(patch: Partial<StatusGroup>): void {
    updateSelected((asset) => ({
      ...asset,
      statusGroup: normalizeStatusGroup({ ...(asset.statusGroup ?? defaultStatusGroupFor(asset.type) ?? { group: "default_group", key: "default", states: ["normal"] }), ...patch }),
    }));
  }

  function updateSelectedTextPolicy(textPolicy: TextExportPolicy): void {
    updateSelected((asset) => ({ ...asset, textPolicy }));
  }

  function applySelected(): void {
    if (!selectedAsset) {
      showToast("请先选择一个元素");
      return;
    }
    assets = assets.map((asset, index) => asset.assetId === selectedAssetId ? normalizeAsset(asset, index, { selectedScreenId, outputRoot }) : asset);
    syncSelectedScreenFromWorkspace();
    showToast("元素参数已更新");
  }

  function markReady(): void {
    if (!selectedAsset) {
      showToast("请先选择一个元素");
      return;
    }
    updateSelected((asset) => ({ ...asset, status: "extract-ready" }));
    syncSelectedScreenFromWorkspace();
    showToast("已标记为可提取");
  }

  function validateAssets(assetList: ArtAsset[], width: number, height: number, screenId: string, screenName: string): BuildIssue[] {
    const issues: BuildIssue[] = [];
    const ids = new Set<string>();
    const pathPattern = /^[a-z0-9_./-]+$/;
    const assetIdPattern = /^[a-z0-9_/-]+$/;
    const addIssue = (severity: BuildIssueSeverity, asset: ArtAsset, field: string, message: string) => {
      issues.push({ severity, screenId, screenName, assetId: asset.assetId || "(empty)", field, message });
    };
    const semanticTree = semanticTreeForAssets(selectedScreen, assetList);
    const semanticByAssetId = new Map<string, SemanticTreeNode>();
    const semanticByNodeId = new Map<string, SemanticTreeNode>();
    const visitSemanticNode = (node: SemanticTreeNode) => {
      if (node.assetId) semanticByAssetId.set(node.assetId, node);
      semanticByNodeId.set(node.id, node);
      if (node.figma?.nodeId) semanticByNodeId.set(node.figma.nodeId, node);
      node.children.forEach(visitSemanticNode);
    };
    semanticTree?.nodes.forEach(visitSemanticNode);
    const sameRect = (left: Rect | undefined, right: Rect | undefined): boolean => Boolean(left && right
      && Math.round(left.x) === Math.round(right.x)
      && Math.round(left.y) === Math.round(right.y)
      && Math.round(left.w) === Math.round(right.w)
      && Math.round(left.h) === Math.round(right.h));
    for (const asset of assetList) {
      if (!asset.assetId.trim()) addIssue("error", asset, "assetId", "assetId 不能为空");
      if (!assetIdPattern.test(asset.assetId)) addIssue("error", asset, "assetId", "只能使用小写英文、数字、下划线、斜杠或连字符");
      if (ids.has(asset.assetId)) addIssue("error", asset, "assetId", "assetId 重复");
      ids.add(asset.assetId);
      if (asset.rect.x < 0 || asset.rect.y < 0 || asset.rect.x + asset.rect.w > width || asset.rect.y + asset.rect.h > height) {
        addIssue("error", asset, "rect", "超出画布范围");
      }
      if (asset.sourceRect.w <= 0 || asset.sourceRect.h <= 0) addIssue("error", asset, "sourceRect", "sourceRect 尺寸必须大于 0");
      if (asset.sourceRect.x < 0 || asset.sourceRect.y < 0) addIssue("error", asset, "sourceRect", "sourceRect 不能为负坐标");
      if (asset.targetSize.w <= 0 || asset.targetSize.h <= 0) addIssue("error", asset, "targetSize", "targetSize 尺寸必须大于 0");
      if (asset.trim.left + asset.trim.right >= asset.sourceRect.w || asset.trim.top + asset.trim.bottom >= asset.sourceRect.h) {
        addIssue("error", asset, "trim", "trim 不能超过 sourceRect 尺寸");
      }
      for (const [key, value] of Object.entries(asset.padding)) {
        if (value < 0) addIssue("error", asset, `padding.${key}`, "padding 不能为负数");
      }
      if (asset.nineSlice) {
        for (const [key, value] of Object.entries(asset.nineSlice)) {
          if (value < 0) addIssue("error", asset, `nineSlice.${key}`, "nineSlice 不能为负数");
        }
        if (asset.nineSlice.left + asset.nineSlice.right >= asset.sourceRect.w || asset.nineSlice.top + asset.nineSlice.bottom >= asset.sourceRect.h) {
          addIssue("error", asset, "nineSlice", "nineSlice 不能超过 sourceRect 尺寸");
        }
      }
      if (asset.statusGroup) {
        const tokens = [asset.statusGroup.group, asset.statusGroup.key, ...asset.statusGroup.states];
        for (const token of tokens) {
          if (!/^[a-z0-9_/-]+$/.test(token)) addIssue("error", asset, "statusGroup", "statusGroup 只能使用小写英文、数字、下划线、斜杠或连字符");
        }
        if (!asset.statusGroup.states.length) addIssue("error", asset, "statusGroup.states", "statusGroup 至少需要一个状态");
      }
      if (!asset.finalPath.trim()) addIssue("error", asset, "runtimeKey", "缺少 runtimeKey/finalPath");
      if (asset.finalPath.startsWith("/") || asset.finalPath.includes("..")) addIssue("error", asset, "runtimeKey", "runtimeKey 必须是安全相对路径");
      if (!pathPattern.test(asset.finalPath)) addIssue("error", asset, "runtimeKey", "runtimeKey 只能使用小写英文、数字、下划线、斜杠、点和连字符");
      if (/[A-Z]/.test(asset.finalPath)) addIssue("error", asset, "runtimeKey", "runtimeKey 不应包含大写字母");
      if (/\s/.test(asset.finalPath)) addIssue("error", asset, "runtimeKey", "runtimeKey 不应包含空格");
      if (!asset.finalPath.endsWith(".png")) addIssue("warning", asset, "runtimeKey", "建议输出 PNG");
      if (!asset.atlas.trim()) addIssue("error", asset, "atlas", "未入图集：atlas 不能为空");
      if (/\s/.test(asset.atlas) || /[A-Z]/.test(asset.atlas) || !/^[a-z0-9_/-]+$/.test(asset.atlas)) {
        addIssue("error", asset, "atlas", "atlas 只能使用小写英文、数字、下划线、斜杠或连字符");
      }
      if (asset.exportEnabled !== false && asset.sliceEnabled !== false) {
        if (!semanticTree) {
          addIssue("error", asset, "semanticTree", "缺少完整语义树，不能进入生图/切图输出");
        } else {
          const semanticNode = semanticByAssetId.get(asset.assetId) || semanticByNodeId.get(asset.figma?.nodeId ?? "");
          if (!semanticNode) {
            addIssue("error", asset, "semanticTree", "可导出元素没有匹配的语义节点");
          } else {
            if (!sameRect(asset.rect, semanticNode.rect)) addIssue("error", asset, "semanticTree.rect", "元素 rect 与语义树 rect 不一致");
            if (asset.textPolicy && semanticNode.textPolicy && asset.textPolicy !== semanticNode.textPolicy) {
              addIssue("error", asset, "semanticTree.textPolicy", "元素 textPolicy 与语义树 textPolicy 不一致");
            }
          }
        }
      }
    }
    return issues;
  }

  function buildBlockingIssueSummary(issues: BuildIssue[]): string {
    if (!issues.length) return "";
    const first = issues[0];
    const target = first.assetId && first.assetId !== "(empty)" ? first.assetId : first.screenName;
    const suffix = issues.length > 1 ? `，另有 ${issues.length - 1} 个阻塞问题` : "";
    return `${target} · ${first.field}：${first.message}${suffix}`;
  }

  function focusBuildIssue(issue: BuildIssue): void {
    if (issue.screenId && issue.screenId !== selectedScreenId) {
      selectScreen(issue.screenId);
    }
    if (issue.assetId && issue.assetId !== "(empty)") {
      selectedAssetId = issue.assetId;
    }
    currentView = "screen";
    modalMode = "none";
    showToast(`已定位 ${issue.assetId} · ${issue.field}`);
  }

  function addAsset(): void {
    if (!selectedScreen) {
      showToast("请先新建或导入一个界面");
      return;
    }
    if (!newAsset.assetId.trim()) {
      showToast("assetId 不能为空");
      return;
    }
    if (assets.some((asset) => asset.assetId === newAsset.assetId)) {
      showToast("assetId 已存在");
      return;
    }
    assets = [...assets, structuredClone(newAsset)];
    selectedAssetId = newAsset.assetId;
    newAsset = createEmptyAsset();
    modalMode = "none";
    syncSelectedScreenFromWorkspace();
    showToast("已新增元素");
  }

  function duplicateSelected(): void {
    if (!selectedAsset) {
      showToast("请先选择一个元素");
      return;
    }
    const source = structuredClone(selectedAsset);
    source.assetId = uniqueAssetId(`${source.assetId}_copy`);
    source.name = `${assetDisplayName(selectedAsset)} copy`;
    source.rect.x = Math.min(canvasWidth - source.rect.w, source.rect.x + 24);
    source.rect.y = Math.min(canvasHeight - source.rect.h, source.rect.y + 24);
    source.status = "layout";
    assets = [...assets, source];
    selectedAssetId = source.assetId;
    syncSelectedScreenFromWorkspace();
    showToast("已复制元素");
  }

  function uniqueAssetId(base: string): string {
    const cleanBase = sanitizeId(base, "asset");
    let id = cleanBase;
    let index = 2;
    while (assets.some((asset) => asset.assetId === id)) {
      id = `${cleanBase}_${index}`;
      index += 1;
    }
    return id;
  }

  function deleteSelected(): void {
    if (!selectedAsset) {
      showToast("请先选择一个元素");
      modalMode = "none";
      return;
    }
    assets = assets.filter((asset) => asset.assetId !== selectedAssetId);
    selectedAssetId = assets[0]?.assetId ?? "";
    modalMode = "none";
    syncSelectedScreenFromWorkspace();
    showToast("已删除元素");
  }

  function resizeHandlesFor(asset: ArtAsset): ResizeHandle[] {
    if (asset.locked || asset.assetId !== selectedAssetId) return [];
    return ["nw", "n", "ne", "e", "se", "s", "sw", "w"];
  }

  function startDrag(event: PointerEvent, assetId: string): void {
    event.preventDefault();
    event.stopPropagation();
    const asset = assets.find((item) => item.assetId === assetId);
    if (asset?.locked) {
      selectedAssetId = assetId;
      showToast("元素已锁定");
      return;
    }
    selectedAssetId = assetId;
    dragAssetId = assetId;
    dragLast = { x: event.clientX, y: event.clientY };
    ignoreCanvasClickUntil = Date.now() + 250;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  function startResize(event: PointerEvent, assetId: string, handle: ResizeHandle): void {
    event.preventDefault();
    event.stopPropagation();
    const asset = assets.find((item) => item.assetId === assetId);
    if (!asset) return;
    selectedAssetId = assetId;
    if (asset.locked) {
      showToast("元素已锁定");
      return;
    }
    dragAssetId = "";
    ignoreCanvasClickUntil = Date.now() + 350;
    resizeDrag = {
      assetId,
      handle,
      startRect: { ...asset.rect },
      startSourceRect: { ...asset.sourceRect },
      startTargetSize: { ...asset.targetSize },
      pointer: { x: event.clientX, y: event.clientY },
    };
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  function snapValue(value: number): number {
    return snapToGrid ? Math.round(value / 4) * 4 : value;
  }

  function resizeRectFromHandle(start: Rect, handle: ResizeHandle, dx: number, dy: number): Rect {
    const minSize = 8;
    const right = start.x + start.w;
    const bottom = start.y + start.h;
    let x = start.x;
    let y = start.y;
    let w = start.w;
    let h = start.h;

    if (handle.includes("e")) w = snapValue(start.w + dx);
    if (handle.includes("s")) h = snapValue(start.h + dy);
    if (handle.includes("w")) {
      x = snapValue(start.x + dx);
      x = Math.max(0, Math.min(right - minSize, x));
      w = right - x;
    }
    if (handle.includes("n")) {
      y = snapValue(start.y + dy);
      y = Math.max(0, Math.min(bottom - minSize, y));
      h = bottom - y;
    }

    w = Math.max(minSize, Math.min(canvasWidth - x, w));
    h = Math.max(minSize, Math.min(canvasHeight - y, h));
    return { x, y, w, h };
  }

  function moveDrag(event: PointerEvent): void {
    if (marqueeDrag) {
      const point = canvasPointFromClient(event.clientX, event.clientY);
      if (point) marqueeDrag = { ...marqueeDrag, current: point };
      return;
    }
    if (paletteDragType) {
      movePaletteDrag(event);
      return;
    }
    if (resizeDrag) {
      const sx = canvasDisplayWidth() / canvasWidth;
      const sy = canvasDisplayHeight() / canvasHeight;
      const dx = (event.clientX - resizeDrag.pointer.x) / sx;
      const dy = (event.clientY - resizeDrag.pointer.y) / sy;
      const nextRect = resizeRectFromHandle(resizeDrag.startRect, resizeDrag.handle, dx, dy);
      const widthDelta = nextRect.w - resizeDrag.startRect.w;
      const heightDelta = nextRect.h - resizeDrag.startRect.h;
      const sourceFollowedRect =
        resizeDrag.startSourceRect.x === resizeDrag.startRect.x &&
        resizeDrag.startSourceRect.y === resizeDrag.startRect.y &&
        resizeDrag.startSourceRect.w === resizeDrag.startRect.w &&
        resizeDrag.startSourceRect.h === resizeDrag.startRect.h;
      assets = assets.map((asset) => asset.assetId === resizeDrag?.assetId
        ? {
            ...asset,
            rect: nextRect,
            sourceRect: sourceFollowedRect ? { ...nextRect } : asset.sourceRect,
            targetSize: {
              w: Math.max(8, resizeDrag.startTargetSize.w + widthDelta),
              h: Math.max(8, resizeDrag.startTargetSize.h + heightDelta),
            },
          }
        : asset);
      return;
    }
    if (!dragAssetId) return;
    const sx = canvasDisplayWidth() / canvasWidth;
    const sy = canvasDisplayHeight() / canvasHeight;
    const dx = (event.clientX - dragLast.x) / sx;
    const dy = (event.clientY - dragLast.y) / sy;
    dragLast = { x: event.clientX, y: event.clientY };
    assets = assets.map((asset) => {
      if (asset.assetId !== dragAssetId) return asset;
      let x = asset.rect.x + dx;
      let y = asset.rect.y + dy;
      if (snapToGrid) {
        x = snapValue(x);
        y = snapValue(y);
      }
      x = Math.max(0, Math.min(canvasWidth - asset.rect.w, x));
      y = Math.max(0, Math.min(canvasHeight - asset.rect.h, y));
      return { ...asset, rect: { ...asset.rect, x, y } };
    });
  }

  function endDrag(event: PointerEvent): void {
    if (marqueeDrag) {
      const rect = rectFromPoints(marqueeDrag.start, marqueeDrag.current);
      marqueeDrag = null;
      ignoreCanvasClickUntil = Date.now() + 350;
      if (rect.w < 16 || rect.h < 16) {
        showToast("框选区域太小，已取消");
        return;
      }
      const marqueeAsset = createAssetFromRect(marqueeAssetType, rect);
      assets = [...assets, marqueeAsset];
      selectedAssetId = marqueeAsset.assetId;
      syncSelectedScreenFromWorkspace();
      showToast(`已框选生成 ${marqueeAsset.assetId}`);
      // B：框选即语义提取——以框为 box prompt 走 SAM，预览条直接出透明 PNG。
      if (hasFinalOverlay) void runSingleExtraction(marqueeAsset.assetId);
      return;
    }
    if (paletteDragType) {
      endPaletteDrag(event);
      return;
    }
    if (resizeDrag) {
      showToast("元素尺寸已更新");
      selectedAssetId = resizeDrag.assetId;
      resizeDrag = null;
      ignoreCanvasClickUntil = Date.now() + 350;
      syncSelectedScreenFromWorkspace();
      return;
    }
    if (dragAssetId) {
      selectedAssetId = dragAssetId;
      ignoreCanvasClickUntil = Date.now() + 350;
      showToast("元素坐标已更新");
    }
    dragAssetId = "";
  }

  function nudgeSelected(dx: number, dy: number): void {
    if (selectedAsset?.locked) {
      showToast("元素已锁定");
      return;
    }
    updateSelected((asset) => ({
      ...asset,
      rect: {
        ...asset.rect,
        x: Math.max(0, Math.min(canvasWidth - asset.rect.w, asset.rect.x + dx)),
        y: Math.max(0, Math.min(canvasHeight - asset.rect.h, asset.rect.y + dy)),
      },
    }));
  }

  function resizeSelected(dw: number, dh: number): void {
    if (selectedAsset?.locked) {
      showToast("元素已锁定");
      return;
    }
    updateSelected((asset) => {
      const nextRect = {
        ...asset.rect,
        w: Math.max(8, Math.min(canvasWidth - asset.rect.x, asset.rect.w + dw)),
        h: Math.max(8, Math.min(canvasHeight - asset.rect.y, asset.rect.h + dh)),
      };
      const sourceFollowedRect =
        asset.sourceRect.x === asset.rect.x &&
        asset.sourceRect.y === asset.rect.y &&
        asset.sourceRect.w === asset.rect.w &&
        asset.sourceRect.h === asset.rect.h;
      return {
        ...asset,
        rect: nextRect,
        sourceRect: sourceFollowedRect ? { ...nextRect } : asset.sourceRect,
        targetSize: {
          w: Math.max(8, asset.targetSize.w + dw),
          h: Math.max(8, asset.targetSize.h + dh),
        },
      };
    });
  }

  function importManifest(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(String(reader.result));
        if (!Array.isArray(data.assets)) throw new Error("manifest 缺少 assets 数组");
        applyManifestDocument(data);
        modalMode = "none";
        showToast("Manifest 已导入");
      } catch (error) {
        showToast(error instanceof Error ? error.message : "Manifest 导入失败");
      }
    };
    reader.readAsText(file);
    input.value = "";
  }

  function exportManifest(): void {
    syncSelectedScreenFromWorkspace();
    const blob = new Blob([JSON.stringify(createManifest(), null, 2)], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "art-workbench-manifest.json";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    showToast("已导出 manifest");
  }
</script>

<svelte:window on:pointermove={moveDrag} on:pointerup={endDrag} />

<svelte:head>
  <title>ArtFlow</title>
</svelte:head>

<div class:auth-mode={authLoading || !authUser} class:figma-mode={modalMode === "figmaImport"} class="app">
  {#if authLoading}
    <div class="login-page">
      <section class="login-card">
        <div class="login-mark">UI</div>
        <h1>ArtFlow</h1>
        <p>正在检查登录状态</p>
      </section>
    </div>
  {:else if !authUser}
    <div class="login-page">
      <section class="login-card">
        <div class="login-mark">UI</div>
        <h1>ArtFlow</h1>
        <p>统一管理游戏 UI、应用界面和美术资源的生成、校对与落地。</p>
        <div class="auth-switch" role="tablist" aria-label="账号入口">
          <button type="button" class:active={authMode === "login"} on:click={() => switchAuthMode("login")}>登录</button>
          <button type="button" class:active={authMode === "register"} on:click={() => switchAuthMode("register")}>注册账号</button>
        </div>
        {#key authMode}
          <div class="auth-fields" in:fly={{ y: 8, duration: 180 }} out:fade={{ duration: 90 }}>
            {#if authMode === "login"}
              <div class="field">
                <label for="login-username">用户名</label>
                <input id="login-username" bind:value={loginUsername} autocomplete="username">
              </div>
              <div class="field">
                <label for="login-password">密码</label>
                <input id="login-password" type="password" bind:value={loginPassword} autocomplete="current-password" on:keydown={(event) => { if (event.key === "Enter") void login(); }}>
              </div>
            {:else}
              <div class="field">
                <label for="register-username">用户名</label>
                <input id="register-username" bind:value={registerUsername} autocomplete="username" placeholder="3-32 位英文、数字或下划线">
              </div>
              <div class="field">
                <label for="register-display">显示名</label>
                <input id="register-display" bind:value={registerDisplayName} autocomplete="name" placeholder="用于协作记录">
              </div>
              <div class="field">
                <label for="register-password">密码</label>
                <input id="register-password" type="password" bind:value={registerPassword} autocomplete="new-password">
              </div>
              <div class="field">
                <label for="register-confirm">确认密码</label>
                <input id="register-confirm" type="password" bind:value={registerConfirmPassword} autocomplete="new-password" on:keydown={(event) => { if (event.key === "Enter") void register(); }}>
              </div>
            {/if}
          </div>
        {/key}
        {#if loginError}<p class="login-error">{loginError}</p>{/if}
        {#if authMode === "login"}
          <button type="button" class="primary login-button" on:click={login}>登录</button>
        {:else}
          <button type="button" class="primary login-button" on:click={register}>注册并进入</button>
        {/if}
      </section>
    </div>
  {:else if modalMode === "figmaImport"}
    <div class="figma-page">
      <div class="figma-workspace">
        <aside class="figma-rail">
          <div class="figma-logo">A</div>
          <button type="button" class="active" title="导入">I</button>
        </aside>
        <aside class="figma-left-panel">
          <header class="figma-file-head">
            <strong>Figma 导入</strong>
            <span>{figmaResult?.name ?? "未解析"}</span>
            <button type="button" on:click={() => modalMode = "none"} title="返回工作台">×</button>
          </header>
          <section class="figma-import-controls">
            <label for="figma-page-token">Figma Token</label>
            <input id="figma-page-token" type="password" placeholder="粘贴 Figma Personal Access Token" bind:value={figmaToken} on:input={updateFigmaTokenInput}>
            <div class="toolbar-row">
              <button type="button" on:click={saveFigmaToken}>保存</button>
              <button type="button" on:click={clearFigmaToken}>清除</button>
            </div>
            <label for="figma-page-url">文件链接</label>
            <input id="figma-page-url" placeholder="https://www.figma.com/design/..." bind:value={figmaUrl} on:input={updateFigmaUrlInput}>
            <div class="figma-scope-control" aria-label="Figma 解析范围">
              <button type="button" class:active={!figmaParseWholeFile} on:click={() => setFigmaParseWholeFile(false)} disabled={!figmaInfo.nodeId}>链接节点</button>
              <button type="button" class:active={figmaParseWholeFile || !figmaInfo.nodeId} on:click={() => setFigmaParseWholeFile(true)}>整文件</button>
              <span>{figmaInfo.nodeId && !figmaParseWholeFile ? `node-id ${figmaInfo.nodeId}` : "解析全部页面"}</span>
            </div>
            <div class="toolbar-row">
              <button type="button" class="primary" on:click={parseFigmaFile} disabled={figmaParsing}>{figmaParsing ? "解析中" : "解析文件"}</button>
              <button type="button" on:click={selectTopFigmaFrames} disabled={!figmaFrames.length}>当前页</button>
              <button type="button" on:click={selectAllFigmaFrames} disabled={!figmaFrames.length}>全选</button>
              <button type="button" on:click={clearFigmaSelection} disabled={!figmaFrames.length}>清空</button>
              <button type="button" on:click={clearStoredFigmaImport} disabled={!figmaResult && !figmaFrames.length}>清空解析</button>
            </div>
            {#if figmaResult?.error}
              <p class="figma-error">{figmaResult.error}</p>
            {/if}
          </section>
          <section class="figma-pages">
            <h3>页面</h3>
            {#if figmaParsedReady && figmaResult?.pages?.length}
              {#each figmaResult.pages as page}
                <button type="button" class:active={page.name === figmaActivePage} on:click={() => setFigmaPage(page.name)}>{page.name}</button>
              {/each}
            {:else}
              <p class="muted">解析后显示页面</p>
            {/if}
          </section>
          <section class="figma-layers">
            <h3>图层</h3>
            <div class="figma-layer-list">
              {#if figmaParsedReady && figmaLayerRoot}
                <button
                  type="button"
                  class:active={true}
                  style="--indent: 0px"
                  on:click={() => selectFigmaFrame(figmaLayerRoot.id)}
                >
                  <span>⌗</span>
                  <strong>{figmaLayerRoot.name}</strong>
                </button>
                {#each figmaLayerRows as row}
                  <div class="figma-layer-row">
                    <button
                      type="button"
                      class:active={figmaPreviewFrameId === row.frame.id}
                      style={`--indent:${row.depth * 16}px`}
                      on:mouseenter={() => previewFigmaFrame(row.frame.id)}
                      on:focus={() => previewFigmaFrame(row.frame.id)}
                      on:click={() => selectFigmaFrame(row.frame.id)}
                    >
                      <span>{figmaLayerIcon(row.frame)}</span>
                      <strong>{row.frame.name}</strong>
                    </button>
                    <button type="button" class="figma-layer-add" on:click={(event) => addAssetFromFigma(row.frame, event)} disabled={!selectedScreen}>加入</button>
                  </div>
                {/each}
              {:else}
                <p class="muted">解析后显示图层树</p>
              {/if}
            </div>
          </section>
        </aside>
        <main class="figma-canvas-area">
          <div class="figma-canvas-toolbar">
            <div>
              <strong>{figmaLayerRoot?.name ?? "Figma 导入"}</strong>
              <span>{figmaLayerRoot?.box ? `${figmaLayerRoot.box.w}x${figmaLayerRoot.box.h}` : "解析后显示画板"}</span>
            </div>
            <div class="figma-toolbar-actions">
              <label class="check figma-overlay-toggle">
                <input type="checkbox" bind:checked={figmaShowParsedOverlay} on:change={persistFigmaImportState}>
                解析层
              </label>
              <button type="button" on:click={() => setFigmaCanvasZoom(figmaCanvasZoom - 0.1)} disabled={!figmaFrames.length || figmaCanvasZoom <= 0.25}>-</button>
              <button type="button" on:click={resetFigmaCanvasView} disabled={!figmaFrames.length}>{Math.round(figmaCanvasZoom * 100)}%</button>
              <button type="button" on:click={() => setFigmaCanvasZoom(figmaCanvasZoom + 0.1)} disabled={!figmaFrames.length || figmaCanvasZoom >= 3}>+</button>
              <button type="button" on:click={importSelectedFigmaToCurrentCanvas} disabled={!figmaParsedReady || !figmaSelectedFrameIds.length}>导入当前画板</button>
              <button type="button" class="primary" on:click={importSelectedFigmaAsScreens} disabled={!figmaParsedReady || !figmaSelectedFrameIds.length}>导入为界面</button>
              <button type="button" on:click={() => modalMode = "none"}>返回工作台</button>
            </div>
          </div>
          <div
            class:dragging={figmaCanvasPanning}
            class="figma-import-canvas"
            role="application"
            aria-label="Figma 导入画布"
            on:wheel|preventDefault={zoomFigmaCanvas}
            on:pointerdown={startFigmaCanvasPan}
            on:pointermove={moveFigmaCanvasPan}
            on:pointerup={stopFigmaCanvasPan}
            on:pointercancel={stopFigmaCanvasPan}
          >
            {#if figmaParsing}
              <div class="figma-loading-preview" role="status" aria-live="polite">
                <strong>正在解析 Figma 文件</strong>
                <span>读取页面、画板和图层结构<span class="loading-dots"></span></span>
              </div>
            {:else if figmaPageTopFrames.length}
              <div class="figma-canvas-world" style={figmaCanvasWorldStyleValue}>
                {#each figmaPageTopFrames as frame}
                  {#if frame.box}
                    <div
                      class:selected={figmaSelectedFrameIds.includes(frame.id)}
                      class:active={figmaPreviewFrame?.id === frame.id}
                      class="figma-canvas-frame"
                      role="button"
                      tabindex="0"
                      aria-label={frame.name}
                      style={figmaCanvasBoardStyle(frame)}
                      on:mouseenter={() => previewFigmaFrame(frame.id)}
                      on:focusin={() => previewFigmaFrame(frame.id)}
                      on:keydown={(event) => handleFigmaFrameKeydown(event, frame.id)}
                      on:click|stopPropagation={() => previewFigmaFrame(frame.id)}
                    >
                      <label class="figma-frame-check">
                        <input type="checkbox" checked={figmaSelectedFrameIds.includes(frame.id)} on:pointerdown|stopPropagation on:click|stopPropagation on:change={() => toggleFigmaFrame(frame.id)}>
                        <span>{frame.name}</span>
                      </label>
                      <div class:show-overlay={figmaShowParsedOverlay} class="figma-preview-board figma-canvas-board">
                        {#if figmaRenderImages[frame.id]}
                          <img class="figma-official-render" src={figmaRenderImages[frame.id]} alt={`${frame.name} Figma 官方渲染`}>
                        {/if}
                        {#each figmaChildrenForScreen(frame).filter((child) => child.id !== frame.id) as child}
                          {#if child.type === "TEXT"}
                            <button type="button" class="figma-preview-node text" class:selected={figmaPreviewFrameId === child.id} style={figmaCanvasNodeStyle(child, frame)} title={child.name} on:click|stopPropagation={(event) => selectFigmaFrame(child.id, event.shiftKey || event.metaKey || event.ctrlKey)}>{child.text}</button>
                          {:else}
                            <button type="button" class={`figma-preview-node ${figmaFrameType(child)} ${child.type.toLowerCase()} ${figmaPreviewFrameId === child.id ? "selected" : ""}`} style={figmaCanvasNodeStyle(child, frame)} title={child.name} aria-label={child.name} on:click|stopPropagation={(event) => selectFigmaFrame(child.id, event.shiftKey || event.metaKey || event.ctrlKey)}></button>
                          {/if}
                        {/each}
                      </div>
                      <footer>
                        <span>{frame.box.w}x{frame.box.h}</span>
                        <span>{figmaChildrenForScreen(frame).length} 节点</span>
                      </footer>
                    </div>
                  {/if}
                {/each}
              </div>
            {:else}
              <div class="figma-empty-preview">解析后按页面显示全部画板，滚轮缩放，按住空白区域拖动。</div>
            {/if}
          </div>
        </main>
      </div>
    </div>
  {:else}
  <header class="topbar">
    <div class="brand">
      <strong>ArtFlow</strong>
      {#if cloudProjects.length}
        <select aria-label="当前项目" bind:value={selectedCloudProjectId} disabled={cloudLoading} on:change={handleCloudProjectChange}>
          {#each cloudProjects as project}
            <option value={project.id}>{project.name}</option>
          {/each}
        </select>
      {:else}
        <button type="button" on:click={() => modalMode = "projectSettings"}>创建项目</button>
      {/if}
    </div>
    <nav class="stage-tabs" aria-label="流水阶段">
      {#each stages as stage}
        <button type="button" class:active={activeStage === stage.id} on:click={() => setWorkflowStage(stage.id)}>{stage.title}</button>
      {/each}
    </nav>
    <div class="top-actions">
      <button type="button" on:click={loadCloudProject} disabled={cloudLoading || !selectedCloudProjectId}>载入云端</button>
      <button type="button" class="primary" on:click={saveCloudProject} disabled={cloudLoading}>{cloudLoading ? "同步中" : "保存云端"}</button>
      <div class="user-menu">
        <button type="button" class="user-menu-trigger" on:click={() => userMenuOpen = !userMenuOpen}>{authUser.displayName}</button>
        {#if userMenuOpen}
          <div class="user-menu-panel">
            <button type="button" on:click={() => { userMenuOpen = false; modalMode = "projectSettings"; }}>项目设置</button>
            {#if canManageCurrentProject()}
              <button type="button" on:click={() => { userMenuOpen = false; void openMemberAdmin(); }}>人员管理</button>
            {/if}
            <button type="button" on:click={() => { userMenuOpen = false; modalMode = "importMenu"; }}>导入资源</button>
            <button type="button" on:click={() => { userMenuOpen = false; exportManifest(); }}>下载清单</button>
            <button type="button" on:click={() => { userMenuOpen = false; void logout(); }}>退出登录</button>
          </div>
        {/if}
      </div>
    </div>
  </header>
  {#if cloudStatus}
    <div class="cloud-strip">{cloudStatus}</div>
  {/if}
  <div class="shell">
    <aside class="sidebar">
      <section class="section">
        <h2>界面列表</h2>
        {#if uiScreens.length}
          <div class="screen-batchbar">
            <span>已选 {selectedScreenBatchIds.length}</span>
            <button type="button" on:click={() => selectedScreenBatchIds = uiScreens.map((screen) => screen.id)}>全选</button>
            <button type="button" on:click={() => selectedScreenBatchIds = []} disabled={!selectedScreenBatchIds.length}>清空</button>
            <button type="button" class="warn" on:click={requestDeleteSelectedScreens} disabled={!selectedScreenBatchIds.length}>批量删除</button>
          </div>
        {/if}
        <div class="screen-list">
          {#if uiScreens.length}
            {#each uiScreens as screen}
              <div
                class:selected={screen.id === selectedScreenId}
                class="screen-card"
                aria-current={screen.id === selectedScreenId ? "page" : undefined}
              >
                <button
                  type="button"
                  class="screen-card-main"
                  on:click={() => selectScreen(screen.id)}
                >
                  <strong>{screen.name}</strong>
                  <span>{screen.category} · {screen.width}x{screen.height}</span>
                  <small>{screenDataFingerprint(screen)}</small>
                </button>
                <button
                  type="button"
                  class:active={selectedScreenBatchIds.includes(screen.id)}
                  class="screen-select"
                  aria-pressed={selectedScreenBatchIds.includes(screen.id)}
                  on:click={() => toggleScreenBatchSelection(screen.id)}
                >{selectedScreenBatchIds.includes(screen.id) ? "已选" : "选择"}</button>
                <button
                  type="button"
                  class="screen-delete"
                  title={`删除 ${screen.name}`}
                  aria-label={`删除 ${screen.name}`}
                  on:click={() => requestDeleteScreen(screen.id)}
                >删除</button>
                <span class:good={screen.status === "done"} class:current={screen.id === selectedScreenId} class="badge">{screenListBadgeLabel(screen)}</span>
              </div>
            {/each}
          {:else}
            <div class="empty-panel">当前项目还没有界面。</div>
          {/if}
        </div>
        <div class="screen-add">
          <input placeholder="新增界面名称" bind:value={newScreenName}>
          <button type="button" on:click={addScreen}>新增</button>
        </div>
        {#if uiScreens.length}
          <div class="toolbar-row">
            <button type="button" class="primary" on:click={submitAllWireframeTasks} disabled={batchGeneratingWireframes}>
              {batchGeneratingWireframes ? "生成中" : "批量生成线框任务"}
            </button>
          </div>
        {/if}
      </section>

      <section class="section stage-action-panel">
        <h2>{activeStageInfo.title}</h2>
        {#if !selectedScreen}
          <div class="quick-actions">
            <button type="button" class="primary" on:click={openFigmaImport}>导入 Figma</button>
            <label class="file-button">
              导入线框
              <input type="file" accept="application/json,.json" on:change={importWireframeJson}>
            </label>
          </div>
        {:else if activeStage === "wireframe"}
          <div class="toolbar-row">
            <button type="button" on:click={openFigmaImport}>导入 Figma</button>
            <label class="file-button compact">
              导入线框
              <input type="file" accept="application/json,.json" on:change={importWireframeJson}>
            </label>
            <button type="button" on:click={exportWireframeJson}>导出线框</button>
          </div>
        {:else if activeStage === "style"}
          <div class="toolbar-row">
            <button type="button" on:click={copyPromptPack}>复制提示词包</button>
            <button type="button" class="warn" on:click={markAiFinal}>锁定风格</button>
          </div>
        {:else if activeStage === "slice"}
          <div class="restore-grid compact">
            <div><strong>{assets.length}</strong><span>待切元素</span></div>
            <div><strong>{assets.filter((asset) => asset.status === "extract-ready").length}</strong><span>已确认</span></div>
          </div>
          <div class="toolbar-row">
            <label class="file-button compact" class:disabled={finalOverlay.locked} title={finalOverlay.locked ? "成稿图已锁定" : "导入或刷新当前界面的成稿源图"}>
              {hasFinalOverlay ? "刷新成稿源图" : "导入成稿源图"}
              <input type="file" accept="image/*" on:change={onImageImport} disabled={finalOverlay.locked}>
            </label>
            <button type="button" class="primary" on:click={markReady} disabled={!selectedAsset}>标记选中可提取</button>
            <button type="button" class="palette-tool" class:dragging={paletteDragType === "panel"} on:pointerdown={(event) => startPaletteDrag(event, "panel")} on:click={() => handlePaletteToolClick("panel")} title="点击新增，或拖到画布指定位置">面板</button>
            <button type="button" class="palette-tool" class:dragging={paletteDragType === "button"} on:pointerdown={(event) => startPaletteDrag(event, "button")} on:click={() => handlePaletteToolClick("button")} title="点击新增，或拖到画布指定位置">按钮</button>
            <button type="button" class="palette-tool" class:dragging={paletteDragType === "icon"} on:pointerdown={(event) => startPaletteDrag(event, "icon")} on:click={() => handlePaletteToolClick("icon")} title="点击新增，或拖到画布指定位置">图标</button>
            <button
              type="button"
              class="palette-tool"
              class:dragging={marqueeMode}
              on:click={toggleMarqueeMode}
              title="开启后在画布上拖一个框，松手即生成元素并立即 AI 语义提取"
            >
              {marqueeMode ? "框选中…" : "框选"}
            </button>
            {#if marqueeMode}
              <select bind:value={marqueeAssetType} title="框选生成的元素类型">
                <option value="panel">面板</option>
                <option value="button">按钮</option>
                <option value="icon">图标</option>
              </select>
            {/if}
            <button
              type="button"
              on:click={submitExportPackage}
              disabled={apiTasks.export.loading || blockingBuildIssues.length > 0}
              title={blockingBuildIssues.length ? "存在阻塞错误，修复后才能写入或同步" : "生成文件写入预检"}
            >
              {apiTasks.export.loading ? "预检中" : "写入预检"}
            </button>
            <button
              type="button"
              on:click={writeStagingPackage}
              disabled={apiTasks.export.loading || blockingBuildIssues.length > 0}
              title={blockingBuildIssues.length ? "存在阻塞错误，修复后才能输出" : "真实输出到 staging，不覆盖游戏工程"}
            >
              输出到 Staging
            </button>
            <button
              type="button"
              on:click={previewGameSync}
              disabled={gameSyncLoading || blockingBuildIssues.length > 0}
              title={blockingBuildIssues.length ? "存在阻塞错误，修复后才能同步" : "把 staging 输出同步到游戏工程目录（先预检冲突）"}
            >
              {gameSyncLoading ? "同步中" : "同步到游戏"}
            </button>
            <button
              type="button"
              class="primary"
              on:click={() => downloadSliceZip()}
              disabled={sliceZipLoading || blockingBuildIssues.length > 0}
              title="导出当前界面全部参与导出的元素（与画布选中无关）；只导选中元素请用切片预览条的「导出选中」"
            >
              {sliceZipLoading ? "打包中" : "导出全部 ZIP"}
            </button>
            <button type="button" on:click={exportManifest}>下载资源清单</button>
          </div>
          {#if blockingBuildIssues.length}
            <div class="api-result warn compact">
              <strong>写入已阻塞</strong>
              <p>{blockingBuildSummary}</p>
              <button type="button" on:click={() => modalMode = "buildReport"}>查看质量检查</button>
            </div>
          {/if}
        {/if}
      </section>

      <section class="section phase-panel">
        <h2>流程说明</h2>
        <p class="hint">{activeStageInfo.desc}</p>
        {#if activeStage === "wireframe"}
          {#if wireframeImportError}
            <div class="api-result warn compact">
              <strong>导入失败</strong>
              <p>{wireframeImportError}</p>
            </div>
          {/if}
        {:else if activeStage === "style"}
          <p class="hint">此阶段只生成同布局成稿源图。线框源图会保留为布局参考，不会被生成图覆盖。</p>
        {:else if activeStage === "slice"}
          {#if blockingBuildIssues.length}
            <div class="api-result warn compact">
              <strong>写入已阻塞</strong>
              <p>{blockingBuildSummary}</p>
              <button type="button" on:click={() => modalMode = "buildReport"}>查看质量检查</button>
            </div>
          {/if}
          <div class="toolbar-row">
          </div>
        {/if}
      </section>

    </aside>

    <main class="workspace">
      <div class="canvas-toolbar">
        <div>
          <strong>{selectedScreen?.name ?? "空画布"}</strong>
          <span>{selectedScreen ? `${canvasWidth}x${canvasHeight}` : "新建或导入后开始编辑"}</span>
        </div>
        <div class="canvas-controls">
          <div class="segmented-control" aria-label="画布视图模式">
            <button type="button" class:active={canvasViewMode === "wireframe"} on:click={() => setCanvasViewMode("wireframe")}>线框</button>
            <button type="button" class:active={canvasViewMode === "final"} on:click={() => setCanvasViewMode("final")} disabled={!hasFinalOverlay}>成稿</button>
            <button type="button" class:active={canvasViewMode === "overlay"} on:click={() => setCanvasViewMode("overlay")} disabled={!hasFinalOverlay}>叠加</button>
          </div>
          <button type="button" class:active={finalOverlay.locked} on:click={toggleFinalOverlayLocked} disabled={!hasFinalOverlay} title={hasFinalOverlay ? "锁定或解锁成稿图" : "未导入成稿图"} aria-label={finalOverlay.locked ? "解锁成稿" : "锁定成稿"}>{finalOverlay.locked ? "解锁成稿" : "锁定成稿"}</button>
          <button type="button" on:click={() => setCanvasZoom(canvasZoom - 0.1)} disabled={canvasZoom <= 0.2}>-</button>
          <button type="button" on:click={fitCanvasToView} title="适配视图">{Math.round(canvasZoom * 100)}%</button>
          <button type="button" class:active={canvasAutoCenter} on:click={toggleCanvasAutoCenter} title="居中画布">居中</button>
          <button type="button" on:click={() => setCanvasZoom(canvasZoom + 0.1)} disabled={canvasZoom >= 1.6}>+</button>
        </div>
      </div>
      <div class:canvas-centered={canvasAutoCenter} class="canvas-wrap" bind:this={canvasWrapElement} on:wheel|preventDefault={zoomCanvas}>
        {#if !selectedScreen}
          <div class="empty-canvas-surface main"></div>
        {:else if activeStage === "wireframe" || activeStage === "style" || activeStage === "slice"}
          {#key `${selectedScreenId}:${activeStage}:${canvasRenderKey}`}
          <div
            class="phone-frame"
            class:reference-backed={showWireframeReferenceInCanvas}
            class:palette-drop-target={Boolean(paletteDragType)}
            class:marquee-active={marqueeMode}
            bind:this={phoneFrameElement}
            style={phoneFrameCss}
            role="button"
            tabindex="0"
            aria-label="选择画布"
            on:pointerdown={startMarquee}
            on:click={handleCanvasClick}
            on:keydown={handleCanvasKeydown}
          >
            {#if showWireframeReferenceInCanvas}
              <img class="ui-image wireframe-reference" src={wireframeReferenceSrc} style={wireframeReferenceStyle} alt="Figma 导入线框源图">
            {/if}
            {#if showFinalOverlayInCanvas}
              <img class="ui-image" src={finalOverlay.src} style={finalOverlayStyle} alt="导入的 UI 成稿">
            {/if}
            {#if showCanvasGuides}
              <div class="safe-top"></div>
              <div class="capsule"></div>
            {/if}
            {#each visibleAssets as asset}
              <button
                type="button"
                class={assetClass(asset, selectedAssetId)}
                style={nodeStyle(asset, canvasDisplayWidthValue, canvasDisplayHeightValue)}
                on:pointerdown={(event) => startDrag(event, asset.assetId)}
                on:click|stopPropagation={() => selectAsset(asset.assetId)}
                aria-label={asset.assetId}
                title={assetDisplayName(asset)}
              >
                <span>{assetDisplayName(asset)}</span>
                {#each resizeHandlesFor(asset) as handle}
                  <span
                    class={`resize-handle ${handle}`}
                    aria-hidden="true"
                    on:pointerdown={(event) => startResize(event, asset.assetId, handle)}
                    on:click|stopPropagation
                  ></span>
                {/each}
              </button>
            {/each}
            {#if marqueeRect}
              <div class="marquee-rect" style={nodeStyle({ rect: marqueeRect } as ArtAsset, canvasDisplayWidthValue, canvasDisplayHeightValue)}></div>
            {/if}
            {#if activeStage === "slice" && pointEditAssetId}
              <!-- 点修正层：拦截画布点击，把点换算成成稿像素坐标，并渲染已放的正/负点 -->
              <div
                class="point-edit-layer"
                class:negative-mode={pointEditPolarity === "negative"}
                role="button"
                tabindex="0"
                aria-label={`点修正 ${pointEditAssetId}：点击添加${pointEditPolarity === "positive" ? "正点" : "负点"}`}
                on:pointerdown|stopPropagation={handlePointEditPointerDown}
                on:click|stopPropagation
                on:keydown|stopPropagation={(event) => { if (event.key === "Escape") exitPointEdit(); }}
              >
                {#each pointEditPositive as point, index (`p${index}-${point[0]}-${point[1]}`)}
                  <span class="point-marker positive" style={pointMarkerStyle(point, canvasDisplayWidthValue, canvasDisplayHeightValue, finalOverlayNaturalSize)} title={`正点 ${point[0]},${point[1]}`}>+</span>
                {/each}
                {#each pointEditNegative as point, index (`n${index}-${point[0]}-${point[1]}`)}
                  <span class="point-marker negative" style={pointMarkerStyle(point, canvasDisplayWidthValue, canvasDisplayHeightValue, finalOverlayNaturalSize)} title={`负点 ${point[0]},${point[1]}`}>-</span>
                {/each}
              </div>
            {/if}
          </div>
          {/key}
        {:else}
          <div class="empty-canvas-surface main"></div>
        {/if}
      </div>
      {#if activeStage === "slice"}
        <div class="slice-thumb-strip">
          <div class="slice-thumb-strip-head">
            <strong>切片预览</strong>
            <span>{visibleAssets.length} 张 · 棋盘格底=AI 语义提取结果，纯色底=矩形硬切回退 · 框选/工具栏可加更多</span>
            <button
              type="button"
              on:click={() => downloadEntryPng(selectedAssetId)}
              disabled={!selectedAsset || !(extractEntries[selectedAssetId]?.cutoutDataUrl || selectedAsset?.dataUrl)}
              title="把选中元素的提取结果透明 PNG 直接保存到本地"
            >
              下载选中 PNG
            </button>
            <button
              type="button"
              class="primary"
              on:click={() => downloadSliceZip(selectedAssetId ? [selectedAssetId] : null)}
              disabled={!selectedAsset || sliceZipLoading}
              title="只导出选中元素（切片 PNG + 组装清单 ZIP）"
            >
              导出选中
            </button>
          </div>
          <div class="slice-thumb-list">
            {#if !hasFinalOverlay}
              <div class="slice-thumb-hint">先在「2. 游戏风格」生成成稿，或用上方工具栏导入成稿源图，才能预览切片。</div>
            {:else if !visibleAssets.length}
              <div class="slice-thumb-hint">还没有切片元素，用上方工具栏的「面板/按钮/图标」或在画布上框选来新增。</div>
            {:else}
              {#each visibleAssets as asset (asset.assetId)}
                <button
                  type="button"
                  class="slice-thumb"
                  class:selected={asset.assetId === selectedAssetId}
                  on:click={() => selectAsset(asset.assetId)}
                  title={assetDisplayName(asset)}
                >
                  <div
                    class="slice-thumb-img"
                    class:alpha={Boolean(extractEntries[asset.assetId]?.ok && extractEntries[asset.assetId]?.cutoutDataUrl && asset.extractionMode !== "rect")}
                  >
                    {#if extractEntries[asset.assetId]?.ok && extractEntries[asset.assetId]?.cutoutDataUrl && asset.extractionMode !== "rect"}
                      <img src={extractEntries[asset.assetId].cutoutDataUrl} alt={asset.assetId}>
                    {:else if sliceThumbnails[asset.assetId]}
                      <img src={sliceThumbnails[asset.assetId]} alt={asset.assetId}>
                    {:else}
                      <span class="slice-thumb-pending">…</span>
                    {/if}
                  </div>
                  <span class="slice-thumb-name">{assetDisplayName(asset)}</span>
                  <span class="slice-thumb-size">{Math.round(asset.rect.w)}×{Math.round(asset.rect.h)}</span>
                </button>
              {/each}
            {/if}
          </div>
        </div>
      {/if}

    </main>

    <aside class="inspector">
      <section class="section">
        <h2>AI 协作</h2>
        {#if !selectedScreen}
          <div class="empty-panel">新建或导入界面后，AI 会基于当前画布和阶段生成任务。</div>
        {:else if activeStage === "wireframe"}
          <div class="field">
            <label for="ai-prompt">线框要求</label>
            <textarea id="ai-prompt" bind:value={promptPack} on:input={updateCurrentScreen} placeholder="描述当前界面的结构、元素和约束"></textarea>
          </div>
          <div class="toolbar-row">
            <button type="button" on:click={copyWireframeTask}>复制线框任务</button>
            <button type="button" class="primary" on:click={submitWireframeTask} disabled={apiTasks.wireframe.loading}>{apiTasks.wireframe.loading ? "提交中" : "提交线框任务"}</button>
          </div>
        {:else if activeStage === "style"}
          <div class="field">
            <label for="image-generation-mode">生图模式</label>
            <select id="image-generation-mode" bind:value={imageGenerationMode}>
              <option value="screen-mockup">整屏成稿</option>
              <option value="button-atlas">按钮图集</option>
              <option value="status-atlas">状态图集</option>
            </select>
          </div>
          <div class="field">
            <label for="ai-prompt">生成提示词</label>
            <textarea id="ai-prompt" bind:value={promptPack} on:input={updateCurrentScreen} placeholder="描述视觉风格、画面层级和输出限制"></textarea>
          </div>
          <div class="toolbar-row">
            <button type="button" on:click={copyImageTask}>复制生图任务</button>
            <button type="button" class="primary" on:click={submitImageTask} disabled={apiTasks.image.loading}>{apiTasks.image.loading ? "提交中" : "生成成稿源图"}</button>
          </div>
          <details class="prompt-audit" open>
            <summary>即将发送的提示词预览</summary>
            <pre>{imagePromptPreview}</pre>
          </details>
          {#if apiTasks.image.prompt}
            <details class="prompt-audit sent" open>
              <summary>后端实际发送给生图 API 的最终 Prompt</summary>
              <pre>{apiTasks.image.prompt}</pre>
            </details>
          {/if}
          {#if apiTasks.image.geometryReport}
            <details class:warn={!apiTasks.image.geometryReport.ok} class="prompt-audit sent" open>
              <summary>生成后几何校验</summary>
              <pre>{JSON.stringify(apiTasks.image.geometryReport, null, 2)}</pre>
            </details>
          {/if}
        {:else if activeStage === "slice"}
          <div class="extract-panel">
            <div class="extract-panel-head">
              <h3>AI 提取</h3>
              <span>本地 SAM/LaMa 推理 · 不计费</span>
            </div>
            <div class="toolbar-row">
              <button
                type="button"
                class="primary"
                on:click={runScreenExtraction}
                disabled={extractScreenLoading || Boolean(extractSingleLoadingId) || !hasFinalOverlay}
                title={hasFinalOverlay ? "对当前界面所有元素做 AI 提取（约 15 秒）" : "先导入成稿源图"}
              >
                {extractScreenLoading ? "整屏提取中…" : "一键整屏提取"}
              </button>
              <button
                type="button"
                on:click={runBackgroundPreview}
                disabled={backgroundPreviewLoading || extractScreenLoading || !hasFinalOverlay}
                title={hasFinalOverlay ? "把前景元素抠掉后补全背景（约 25 秒）" : "先导入成稿源图"}
              >
                {backgroundPreviewLoading ? "背景补全中…" : "背景补全预览"}
              </button>
            </div>
            {#if extractScreenLoading}
              <p class="hint">整屏提取约需 15 秒；首次调用会先启动本地视觉模型（约 10 秒），请耐心等待。</p>
            {:else if backgroundPreviewLoading}
              <p class="hint">背景补全约需 25 秒；首次调用会先启动本地视觉模型。</p>
            {:else if !hasFinalOverlay}
              <p class="hint">先在「2. 游戏风格」生成成稿，或用左侧工具栏导入成稿源图，才能做 AI 提取。</p>
            {/if}
            {#if extractError}
              <div class="api-result warn compact">
                <strong>提取失败</strong>
                <p>{extractError}</p>
              </div>
            {/if}
            {#if extractNotes.length}
              <div class="extract-notes">
                {#each extractNotes as note}
                  <p>{note}</p>
                {/each}
              </div>
            {/if}
            {#if extractMessage && !extractError}
              <p class="hint">{extractMessage}</p>
            {/if}
            {#if pointEditAssetId}
              <div class="api-result compact extract-point-edit">
                <strong>点修正：{pointEditAssetId}</strong>
                <p>在画布成稿上点击放点：正点=要保留的区域，负点=要剔除的区域。当前 正 {pointEditPositive.length} / 负 {pointEditNegative.length}。</p>
                <div class="toolbar-row">
                  <button type="button" class:active={pointEditPolarity === "positive"} on:click={() => pointEditPolarity = "positive"}>+ 正点</button>
                  <button type="button" class:active={pointEditPolarity === "negative"} on:click={() => pointEditPolarity = "negative"}>- 负点</button>
                  <button type="button" on:click={() => { pointEditPositive = []; pointEditNegative = []; }} disabled={!pointEditPositive.length && !pointEditNegative.length}>清空点</button>
                </div>
                <div class="toolbar-row">
                  <button
                    type="button"
                    class="primary"
                    on:click={() => runSingleExtraction(pointEditAssetId)}
                    disabled={extractSingleLoadingId === pointEditAssetId || (!pointEditPositive.length && !pointEditNegative.length)}
                  >
                    {extractSingleLoadingId === pointEditAssetId ? "提取中…" : "按点重新提取"}
                  </button>
                  <button type="button" on:click={exitPointEdit}>退出点修正</button>
                </div>
              </div>
            {/if}
            {#if backgroundPreviewUrl}
              <div class="extract-background-preview">
                <div class="extract-panel-head">
                  <strong>背景补全预览</strong>
                  {#if backgroundPreviewPipeline}<span>{backgroundPreviewPipeline}</span>{/if}
                </div>
                <div class="extract-cutout wide">
                  <img src={backgroundPreviewUrl} alt="背景补全预览">
                </div>
              </div>
            {/if}
            {#if extractCardRows.length}
              <div class="extract-grid">
                {#each extractCardRows as row (row.asset.assetId)}
                  <div class="extract-card" class:selected={row.asset.assetId === selectedAssetId}>
                    <button type="button" class="extract-cutout" on:click={() => selectAsset(row.asset.assetId)} title={assetDisplayName(row.asset)}>
                      {#if row.entry?.cutoutDataUrl}
                        <img src={row.entry.cutoutDataUrl} alt={row.asset.assetId}>
                      {:else}
                        <span class="extract-cutout-empty">{row.entry && !row.entry.ok ? "提取失败" : "未提取"}</span>
                      {/if}
                    </button>
                    <div class="extract-card-head">
                      <strong>{assetDisplayName(row.asset)}</strong>
                      {#if row.entry?.ok && row.entry.confidence !== undefined}
                        <span class={extractionConfidenceBadge(row.entry.confidence)}>{Math.round((row.entry.confidence ?? 0) * 100)}%</span>
                      {:else if row.entry && !row.entry.ok}
                        <span class="badge conf-low">失败</span>
                      {/if}
                      {#if row.asset.isBackground}<span class="badge blue">背景层</span>{/if}
                      {#if row.asset.extractionTarget && row.asset.extractionTarget !== "foreground"}<span class="badge blue">{extractionTargetLabels[row.asset.extractionTarget]}</span>{/if}
                      {#if row.asset.extractionMode === "rect"}<span class="badge">硬切</span>{/if}
                      {#if adoptedExtractionMasks[row.asset.assetId]}<span class="badge good">已采用</span>{/if}
                      {#if row.asset.dataUrl}<span class="badge blue">二次处理</span>{/if}
                    </div>
                    {#if row.entry?.textContent}
                      <span class="extract-pipeline">识别文字：{row.entry.textContent}</span>
                    {/if}
                    {#if row.entry?.pipeline}
                      <span class="extract-pipeline">{row.entry.pipeline}{row.entry.reason ? ` · ${row.entry.reason}` : ""}</span>
                    {/if}
                    {#if row.entry && !row.entry.ok && row.entry.error}
                      <p class="extract-error">{row.entry.error}</p>
                    {/if}
                    <div class="extract-card-actions">
                      <select
                        class="extract-target-select"
                        value={row.asset.extractionTarget ?? "foreground"}
                        on:change={(event) => setExtractionTarget(row.asset.assetId, event.currentTarget.value)}
                        disabled={extractScreenLoading || Boolean(extractSingleLoadingId) || !hasFinalOverlay}
                        title="提取目标：前景整体 / 本体去文字 / 仅文字层 / 元素背后的背景（选择后自动重新提取）"
                      >
                        <option value="foreground">前景整体</option>
                        <option value="body-no-text">本体去文字</option>
                        <option value="text-layer">仅文字层</option>
                        <option value="background-behind">背后背景</option>
                      </select>
                      <button
                        type="button"
                        on:click={() => runSingleExtraction(row.asset.assetId)}
                        disabled={extractScreenLoading || Boolean(extractSingleLoadingId) || !hasFinalOverlay}
                      >
                        {extractSingleLoadingId === row.asset.assetId ? "提取中…" : "重新提取"}
                      </button>
                      <button
                        type="button"
                        on:click={() => downloadEntryPng(row.asset.assetId)}
                        disabled={!row.entry?.cutoutDataUrl && !row.asset.dataUrl}
                        title="把当前提取结果透明 PNG 保存到本地"
                      >
                        下载 PNG
                      </button>
                      <button
                        type="button"
                        class:active={pointEditAssetId === row.asset.assetId}
                        on:click={() => pointEditAssetId === row.asset.assetId ? exitPointEdit() : startPointEdit(row.asset.assetId)}
                        title="在成稿上点正/负点修正分割范围"
                      >
                        点修正
                      </button>
                      <button type="button" on:click={() => adoptExtractionMask(row.asset.assetId)} disabled={!row.entry?.maskDataUrl} title="采用当前提取 mask，导出时优先生效">采用</button>
                      <button type="button" on:click={() => restoreRectExtraction(row.asset.assetId)} title="放弃 AI 提取，导出时按矩形硬切">恢复硬切</button>
                      <button type="button" on:click={() => openRefinePrompt(row.asset.assetId)} disabled={!row.entry?.cutoutDataUrl} title="用提示词对提取结果做云端重绘（计费）">二次处理</button>
                      <button
                        type="button"
                        class:active={Boolean(row.asset.isBackground)}
                        on:click={() => toggleAssetBackground(row.asset.assetId)}
                        title="背景层不参与元素提取，由背景补全负责"
                      >
                        背景层
                      </button>
                    </div>
                  </div>
                {/each}
              </div>
            {:else}
              <div class="empty-panel">当前界面还没有参与导出的元素，先在画布上框选或用工具栏新增。</div>
            {/if}
          </div>
        {/if}
        {#if currentApiTaskStatus.error}
          <div class="api-result warn">
            <strong>任务错误</strong>
            <p class="api-result-text">{currentApiTaskStatus.error}</p>
            <button type="button" class="copy-diagnostics" on:click={() => copyTaskDiagnostics(currentApiTaskStatus)}>一键复制报错</button>
          </div>
        {:else if currentApiTaskStatus.message}
          <div class="api-result">
            <strong>任务结果</strong>
            {#if currentApiTaskStatus.provider || currentApiTaskStatus.model}
              <code>{[currentApiTaskStatus.provider, currentApiTaskStatus.model].filter(Boolean).join(" · ")}</code>
            {/if}
            <p>{currentApiTaskStatus.message}</p>
          </div>
        {/if}
        {#if aiProviderLoading}
          <p class="hint">正在检测 AI 适配器。</p>
        {:else if aiProviderError}
          <p class="hint">{aiProviderError}</p>
        {:else if aiProviders}
          <div class="provider-block compact">
            {#each [...aiProviders.wireframe, ...aiProviders.image] as provider}
              <div class="provider-row">
                <div>
                  <strong>{provider.label}</strong>
                  <span>{provider.role}</span>
                </div>
                <span class={providerBadge(provider)}>{provider.available ? "可用" : "未配置"}</span>
              </div>
            {/each}
          </div>
        {/if}
      </section>

      <section class="section">
        <h2>属性</h2>
        {#if activeStage === "slice"}
          <div class="field">
            <label for="slice-output-root-right">资源输出路径</label>
            <input id="slice-output-root-right" bind:value={outputRoot}>
          </div>
          {#if apiTasks.export.files.length || apiTasks.export.error || apiTasks.export.message || apiTasks.export.command}
            <div class:warn={apiTasks.export.error} class="api-result wide">
              {#if apiTasks.export.error}
                <strong>预检失败</strong>
                <p>{apiTasks.export.error}</p>
              {:else}
                <strong>写入预检</strong>
                {#if apiTasks.export.message}<p>{apiTasks.export.message}</p>{/if}
                {#if apiTasks.export.command}<code>{apiTasks.export.command}</code>{/if}
                {#if apiTasks.export.plan}
                  <div class="plan-summary">
                    <div><strong>{apiTasks.export.plan.summary.write}</strong><span>将写入</span></div>
                    <div><strong>{apiTasks.export.plan.summary.overwrite}</strong><span>将覆盖</span></div>
                    <div><strong>{apiTasks.export.plan.summary.skip}</strong><span>跳过</span></div>
                  </div>
                  {#if apiTasks.export.plan.summary.overwrite}
                    <p>{fileConflictConfirmed ? "覆盖冲突已确认。" : "覆盖冲突未确认，不会执行写入。"}</p>
                    <button type="button" on:click={() => { pendingDryRunPlan = apiTasks.export.plan; modalMode = "fileConflict"; }}>
                      {fileConflictConfirmed ? "查看覆盖冲突" : "确认覆盖冲突"}
                    </button>
                  {/if}
                {/if}
                {#if apiTasks.export.files.length}
                  <ul>
                    {#each apiTasks.export.files as file}
                      <li><code>{file}</code></li>
                    {/each}
                  </ul>
                {/if}
              {/if}
            </div>
          {/if}
          <div class="atlas-groups">
            <div class="atlas-groups-head">
              <h3>图集分组</h3>
              <span>{atlasGroups.length} 组 · {atlasGroups.reduce((sum, group) => sum + group.items.length, 0)} 张</span>
            </div>
            <p class="hint">按 atlas 字段聚合，显示每组资源数、占用面积和最大切片尺寸。点资源可在画布中定位。改资源的「资源分组」字段即可重新分组。</p>
            {#if !atlasGroups.length}
              <div class="empty-panel">还没有元素可分组。用工具栏新增或框选元素后会按 atlas 自动归组。</div>
            {:else}
              {#each atlasGroups as group (group.atlas)}
                <details class="atlas-group" open>
                  <summary>
                    <strong>{group.atlas}</strong>
                    <span>{group.items.length} 张 · 最大 {group.maxW}×{group.maxH} · 占用 {(group.area / 1000).toFixed(1)}k px²</span>
                  </summary>
                  <ul class="atlas-group-list">
                    {#each group.items as item (item.assetId)}
                      <li>
                        <button
                          type="button"
                          class="atlas-group-item"
                          class:selected={item.assetId === selectedAssetId}
                          on:click={() => selectAsset(item.assetId)}
                          title={item.finalPath || item.runtimeKey || item.assetId}
                        >
                          <strong>{assetDisplayName(item)}</strong>
                          <span>{Math.round(item.targetSize?.w ?? item.rect.w)}×{Math.round(item.targetSize?.h ?? item.rect.h)} · {item.finalPath || item.runtimeKey || item.assetId}</span>
                        </button>
                      </li>
                    {/each}
                  </ul>
                </details>
              {/each}
            {/if}
          </div>
        {:else if selectedAsset}
          <div class="field">
            <label for="asset-name">图层名称</label>
            <input
              id="asset-name"
              value={selectedAsset.name ?? ""}
              placeholder={selectedAsset.figma?.name ?? selectedAsset.assetId}
              on:input={(event) => patchAsset(selectedAsset.assetId, { name: (event.currentTarget as HTMLInputElement).value })}
            >
          </div>
          <div class="field">
            <label for="asset-id">元素标识</label>
            <input
              id="asset-id"
              value={selectedAsset.assetId}
              on:input={(event) => renameSelectedAssetId((event.currentTarget as HTMLInputElement).value)}
            >
          </div>
          <div class="field">
            <label for="asset-final-path">切片文件名（输出路径）</label>
            <input
              id="asset-final-path"
              value={selectedAsset.finalPath ?? ""}
              placeholder="例如 main/nav/btn_shop.png"
              on:input={(event) => patchAsset(selectedAsset.assetId, { finalPath: (event.currentTarget as HTMLInputElement).value })}
            >
            <p class="hint">切出来的 PNG 文件名，相对「资源输出路径」。建议小写英文/数字/下划线/斜杠，以 .png 结尾。</p>
          </div>
          <div class="layer-state-row">
            <label class="check">
              <input type="checkbox" checked={Boolean(selectedAsset.locked)} on:change={() => toggleAssetLocked(selectedAsset.assetId)}>
              锁定
            </label>
            <label class="check">
              <input type="checkbox" checked={Boolean(selectedAsset.hidden)} on:change={() => toggleAssetHidden(selectedAsset.assetId)}>
              画布隐藏
            </label>
            <label class="check">
              <input type="checkbox" checked={selectedAsset.exportEnabled ?? true} on:change={() => toggleSelectedExportEnabled()}>
              参与分离/导出
            </label>
          </div>
          <div class="split">
            <div class="field"><label for="x">横向位置</label><input id="x" type="number" bind:value={selectedAsset.rect.x} disabled={selectedAsset.locked}></div>
            <div class="field"><label for="y">纵向位置</label><input id="y" type="number" bind:value={selectedAsset.rect.y} disabled={selectedAsset.locked}></div>
            <div class="field"><label for="w">宽度</label><input id="w" type="number" bind:value={selectedAsset.rect.w} disabled={selectedAsset.locked}></div>
            <div class="field"><label for="h">高度</label><input id="h" type="number" bind:value={selectedAsset.rect.h} disabled={selectedAsset.locked}></div>
          </div>
          <div class="split">
            <div class="field"><label for="tw">输出宽</label><input id="tw" type="number" bind:value={selectedAsset.targetSize.w}></div>
            <div class="field"><label for="th">输出高</label><input id="th" type="number" bind:value={selectedAsset.targetSize.h}></div>
          </div>
          <div class="field-group">
            <strong>源裁切 sourceRect</strong>
            <div class="split">
              <div class="field"><label for="source-x">源 x</label><input id="source-x" type="number" min="0" bind:value={selectedAsset.sourceRect.x}></div>
              <div class="field"><label for="source-y">源 y</label><input id="source-y" type="number" min="0" bind:value={selectedAsset.sourceRect.y}></div>
              <div class="field"><label for="source-w">源宽</label><input id="source-w" type="number" min="1" bind:value={selectedAsset.sourceRect.w}></div>
              <div class="field"><label for="source-h">源高</label><input id="source-h" type="number" min="1" bind:value={selectedAsset.sourceRect.h}></div>
            </div>
          </div>
          <div class="field-group">
            <strong>裁切 trim</strong>
            <div class="split">
              <div class="field"><label for="trim-left">左</label><input id="trim-left" type="number" min="0" bind:value={selectedAsset.trim.left}></div>
              <div class="field"><label for="trim-top">上</label><input id="trim-top" type="number" min="0" bind:value={selectedAsset.trim.top}></div>
              <div class="field"><label for="trim-right">右</label><input id="trim-right" type="number" min="0" bind:value={selectedAsset.trim.right}></div>
              <div class="field"><label for="trim-bottom">下</label><input id="trim-bottom" type="number" min="0" bind:value={selectedAsset.trim.bottom}></div>
            </div>
          </div>
          <div class="field-group">
            <strong>留白 padding</strong>
            <div class="split">
              <div class="field"><label for="padding-left">左</label><input id="padding-left" type="number" min="0" bind:value={selectedAsset.padding.left}></div>
              <div class="field"><label for="padding-top">上</label><input id="padding-top" type="number" min="0" bind:value={selectedAsset.padding.top}></div>
              <div class="field"><label for="padding-right">右</label><input id="padding-right" type="number" min="0" bind:value={selectedAsset.padding.right}></div>
              <div class="field"><label for="padding-bottom">下</label><input id="padding-bottom" type="number" min="0" bind:value={selectedAsset.padding.bottom}></div>
            </div>
          </div>
          {#if selectedAsset.type === "panel" || selectedAsset.type === "button"}
            <div class="field-group">
              <label class="check">
                <input type="checkbox" checked={Boolean(selectedAsset.nineSlice)} on:change={(event) => toggleSelectedNineSlice((event.currentTarget as HTMLInputElement).checked)}>
                启用九宫格 nineSlice
              </label>
              {#if selectedAsset.nineSlice}
                <div class="split">
                  <div class="field">
                    <label for="nine-left">左</label>
                    <input id="nine-left" type="number" min="0" value={selectedAsset.nineSlice.left} on:input={(event) => updateSelectedNineSlice("left", Number((event.currentTarget as HTMLInputElement).value))}>
                  </div>
                  <div class="field">
                    <label for="nine-top">上</label>
                    <input id="nine-top" type="number" min="0" value={selectedAsset.nineSlice.top} on:input={(event) => updateSelectedNineSlice("top", Number((event.currentTarget as HTMLInputElement).value))}>
                  </div>
                  <div class="field">
                    <label for="nine-right">右</label>
                    <input id="nine-right" type="number" min="0" value={selectedAsset.nineSlice.right} on:input={(event) => updateSelectedNineSlice("right", Number((event.currentTarget as HTMLInputElement).value))}>
                  </div>
                  <div class="field">
                    <label for="nine-bottom">下</label>
                    <input id="nine-bottom" type="number" min="0" value={selectedAsset.nineSlice.bottom} on:input={(event) => updateSelectedNineSlice("bottom", Number((event.currentTarget as HTMLInputElement).value))}>
                  </div>
                </div>
              {/if}
            </div>
          {/if}
          <div class="field-group">
            <label class="check">
              <input type="checkbox" checked={Boolean(selectedAsset.statusGroup)} on:change={(event) => toggleSelectedStatusGroup((event.currentTarget as HTMLInputElement).checked)}>
              启用状态组 statusGroup
            </label>
            {#if selectedAsset.statusGroup}
              <div class="split">
                <div class="field">
                  <label for="status-group">状态组</label>
                  <input id="status-group" value={selectedAsset.statusGroup.group} on:input={(event) => updateSelectedStatusGroup({ group: (event.currentTarget as HTMLInputElement).value })}>
                </div>
                <div class="field">
                  <label for="status-key">状态键</label>
                  <input id="status-key" value={selectedAsset.statusGroup.key} on:input={(event) => updateSelectedStatusGroup({ key: (event.currentTarget as HTMLInputElement).value })}>
                </div>
              </div>
              <div class="field">
                <label for="status-states">状态列表</label>
                <input id="status-states" value={selectedAsset.statusGroup.states.join(", ")} on:input={(event) => updateSelectedStatusGroup({ states: (event.currentTarget as HTMLInputElement).value.split(/[,\s]+/).filter(Boolean) })}>
              </div>
            {/if}
          </div>
          <div class="field-group">
            <strong>文字导出策略</strong>
            <div class="field">
              <label for="text-export-policy">按钮/标签文字处理</label>
              <select
                id="text-export-policy"
                value={selectedAsset.textPolicy ?? "separate-runtime-text"}
                on:change={(event) => updateSelectedTextPolicy((event.currentTarget as HTMLSelectElement).value as TextExportPolicy)}
              >
                <option value="separate-runtime-text">文字和底图分离</option>
                <option value="bake-with-art">文字随图片一起导出</option>
                <option value="discard-text">丢弃图中文字</option>
              </select>
            </div>
            <p class="hint">多状态按钮会按 statusGroup.states 导出；文字策略决定每个状态图是否保留、分离或丢弃文字。</p>
          </div>
          <div class="field">
            <label for="atlas">资源分组</label>
            <input id="atlas" bind:value={selectedAsset.atlas}>
          </div>
          <div class="field">
            <label for="final-path">输出位置</label>
            <input id="final-path" bind:value={selectedAsset.finalPath}>
          </div>
          <div class="toolbar-row">
            <button type="button" class="good" on:click={applySelected}>应用</button>
            <button type="button" on:click={markReady}>标记可提取</button>
            <label class="check">
              <input type="checkbox" bind:checked={snapToGrid}>
              4px 吸附
            </label>
          </div>
          <div class="nudge-pad">
            <button type="button" on:click={() => nudgeSelected(0, -4)}>上</button>
            <button type="button" on:click={() => nudgeSelected(-4, 0)}>左</button>
            <button type="button" on:click={() => nudgeSelected(4, 0)}>右</button>
            <button type="button" on:click={() => nudgeSelected(0, 4)}>下</button>
            <button type="button" on:click={() => resizeSelected(-4, -4)}>缩小</button>
            <button type="button" on:click={() => resizeSelected(4, 4)}>放大</button>
          </div>
          {#if selectedAsset.figma}
            <div class="figma-meta-card">
              <strong>Figma 节点</strong>
              <code>{selectedAsset.figma.nodeId}</code>
              <span>{selectedAsset.figma.name} · {selectedAsset.figma.type}</span>
              {#if selectedAsset.figma.text}<p>{selectedAsset.figma.text}</p>{/if}
            </div>
          {/if}
        {:else if selectedScreen}
          <div class="field">
            <label for="canvas-screen-name">界面名称</label>
            <input id="canvas-screen-name" bind:value={selectedScreen.name} on:input={updateCurrentScreen}>
          </div>
          <div class="field">
            <label for="canvas-screen-category">界面分类</label>
            <input id="canvas-screen-category" bind:value={selectedScreen.category} on:input={updateCurrentScreen}>
          </div>
          <div class="split">
            <div class="field">
              <label for="canvas-screen-width">画布宽</label>
              <input id="canvas-screen-width" type="number" min="320" max="4096" bind:value={canvasWidth} on:input={updateCurrentScreen}>
            </div>
            <div class="field">
              <label for="canvas-screen-height">画布高</label>
              <input id="canvas-screen-height" type="number" min="320" max="4096" bind:value={canvasHeight} on:input={updateCurrentScreen}>
            </div>
          </div>
          {#if activeStage === "wireframe"}
            <div class="field">
              <label for="canvas-layout-ref">线框来源</label>
              <input id="canvas-layout-ref" bind:value={layoutRef} on:input={updateCurrentScreen}>
            </div>
          {:else if activeStage === "style"}
            <div class="field">
              <label for="canvas-style-ref">风格参考</label>
              <input id="canvas-style-ref" bind:value={styleRef} on:input={updateProjectStyleRef}>
            </div>
          {/if}
          <div class="field">
            <strong class="field-label">参考资产</strong>
            <div class="toolbar-row">
              <button type="button" on:click={openReferenceAssets}>管理参考资产</button>
              <button type="button" on:click={() => exportReferenceJson(true)} title="导出可交给 AI 的参考资产 JSON（不含运行时字段）">导出参考 JSON</button>
            </div>
            <p class="hint">管理风格/线框/参考图与提示词，供 AI 线框和生图任务使用。</p>
          </div>
          <div class="overlay-panel">
            <div class="overlay-panel-head">
              <strong>画布视图</strong>
              <span>{canvasViewMode === "wireframe" ? "只看线框" : canvasViewMode === "final" ? "只看成稿" : "线框 + 成稿"}</span>
            </div>
            <div class="segmented-control wide" aria-label="画布视图模式">
              <button type="button" class:active={canvasViewMode === "wireframe"} on:click={() => setCanvasViewMode("wireframe")}>线框</button>
              <button type="button" class:active={canvasViewMode === "final"} on:click={() => setCanvasViewMode("final")} disabled={!hasFinalOverlay}>成稿</button>
              <button type="button" class:active={canvasViewMode === "overlay"} on:click={() => setCanvasViewMode("overlay")} disabled={!hasFinalOverlay}>叠加</button>
            </div>
            <div class="toolbar-row">
              <button type="button" class:active={finalOverlay.locked} on:click={toggleFinalOverlayLocked} disabled={!hasFinalOverlay}>{finalOverlay.locked ? "解锁成稿" : "锁定成稿"}</button>
              <button type="button" on:click={resetFinalOverlayAlignment} disabled={!hasFinalOverlay || finalOverlay.locked}>对齐画布</button>
            </div>
            <div class="field">
              <label for="final-overlay-opacity">成稿透明度 · {Math.round(finalOverlay.opacity * 100)}%</label>
              <input
                id="final-overlay-opacity"
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={finalOverlay.opacity}
                disabled={!hasFinalOverlay || finalOverlay.locked}
                on:input={updateFinalOverlayOpacity}
              >
            </div>
            <div class="field">
              <label for="final-overlay-fit">适配方式</label>
              <select id="final-overlay-fit" value={finalOverlay.fit} disabled={!hasFinalOverlay || finalOverlay.locked} on:change={updateFinalOverlayFit}>
                <option value="fill">填满画布</option>
                <option value="contain">完整显示</option>
                <option value="cover">覆盖画布</option>
              </select>
            </div>
            <div class="split">
              <div class="field">
                <label for="final-overlay-scale">缩放</label>
                <input
                  id="final-overlay-scale"
                  type="number"
                  min="0.2"
                  max="3"
                  step="0.01"
                  value={finalOverlay.scale}
                  disabled={!hasFinalOverlay || finalOverlay.locked}
                  on:input={(event) => updateFinalOverlayNumber("scale", Number((event.currentTarget as HTMLInputElement).value))}
                >
              </div>
              <div class="field">
                <label for="final-overlay-offset-x">横向偏移</label>
                <input
                  id="final-overlay-offset-x"
                  type="number"
                  step="1"
                  value={finalOverlay.offsetX}
                  disabled={!hasFinalOverlay || finalOverlay.locked}
                  on:input={(event) => updateFinalOverlayNumber("offsetX", Number((event.currentTarget as HTMLInputElement).value))}
                >
              </div>
              <div class="field">
                <label for="final-overlay-offset-y">纵向偏移</label>
                <input
                  id="final-overlay-offset-y"
                  type="number"
                  step="1"
                  value={finalOverlay.offsetY}
                  disabled={!hasFinalOverlay || finalOverlay.locked}
                  on:input={(event) => updateFinalOverlayNumber("offsetY", Number((event.currentTarget as HTMLInputElement).value))}
                >
              </div>
            </div>
          </div>
          <div class="toolbar-row">
            <button type="button" class="palette-tool" class:dragging={paletteDragType === "panel"} on:pointerdown={(event) => startPaletteDrag(event, "panel")} on:click={() => handlePaletteToolClick("panel")} title="点击新增，或拖到画布指定位置">面板</button>
            <button type="button" class="palette-tool" class:dragging={paletteDragType === "button"} on:pointerdown={(event) => startPaletteDrag(event, "button")} on:click={() => handlePaletteToolClick("button")} title="点击新增，或拖到画布指定位置">按钮</button>
            <button type="button" class="palette-tool" class:dragging={paletteDragType === "icon"} on:pointerdown={(event) => startPaletteDrag(event, "icon")} on:click={() => handlePaletteToolClick("icon")} title="点击新增，或拖到画布指定位置">图标</button>
            <button type="button" on:click={() => { newAsset = createEmptyAsset(); modalMode = "addAsset"; }}>自定义</button>
            <button type="button" on:click={() => modalMode = "buildReport"}>质量检查</button>
          </div>
        {:else}
          <div class="empty-panel">未选中界面。</div>
        {/if}
      </section>

      <section class="section">
        <h2>图层</h2>
        <div class="asset-list layer-list">
          {#if assets.length}
            {#each layerRows as row}
              <div
                class:selected={row.asset.assetId === selectedAssetId}
                class:hidden-layer={row.asset.hidden}
                class:locked-layer={row.asset.locked}
                class="layer-row"
                style={`--depth:${row.depth}`}
              >
                <button type="button" class="layer-select" on:click={() => selectAsset(row.asset.assetId)}>
                  <span class="layer-index">{row.index + 1}</span>
                  <span class="layer-main">
                    <strong>{assetDisplayName(row.asset)}</strong>
                    <span>{row.asset.assetId}</span>
                  </span>
                  <span class="layer-meta">{assetLayerLabel(row.asset, row.index)}</span>
                </button>
                <div class="layer-actions" aria-label={`${row.asset.assetId} 状态`}>
                  <button
                    type="button"
                    class:active={row.asset.hidden}
                    on:click={() => toggleAssetHidden(row.asset.assetId)}
                    title={row.asset.hidden ? "显示图层" : "隐藏图层"}
                    aria-label={row.asset.hidden ? "显示图层" : "隐藏图层"}
                  >{row.asset.hidden ? "显" : "隐"}</button>
                  <button
                    type="button"
                    class:active={row.asset.locked}
                    on:click={() => toggleAssetLocked(row.asset.assetId)}
                    title={row.asset.locked ? "解锁图层" : "锁定图层"}
                    aria-label={row.asset.locked ? "解锁图层" : "锁定图层"}
                  >{row.asset.locked ? "解" : "锁"}</button>
                </div>
              </div>
            {/each}
          {:else}
            <div class="empty-panel">当前界面还没有元素。</div>
          {/if}
        </div>
        {#if selectedScreen}
          <div class="toolbar-row">
            <button type="button" class="palette-tool" class:dragging={paletteDragType === "panel"} on:pointerdown={(event) => startPaletteDrag(event, "panel")} on:click={() => handlePaletteToolClick("panel")} title="点击新增，或拖到画布指定位置">面板</button>
            <button type="button" class="palette-tool" class:dragging={paletteDragType === "button"} on:pointerdown={(event) => startPaletteDrag(event, "button")} on:click={() => handlePaletteToolClick("button")} title="点击新增，或拖到画布指定位置">按钮</button>
            <button type="button" class="palette-tool" class:dragging={paletteDragType === "icon"} on:pointerdown={(event) => startPaletteDrag(event, "icon")} on:click={() => handlePaletteToolClick("icon")} title="点击新增，或拖到画布指定位置">图标</button>
            <button type="button" class="palette-tool" class:dragging={paletteDragType === "image"} on:pointerdown={(event) => startPaletteDrag(event, "image")} on:click={() => handlePaletteToolClick("image")} title="点击新增，或拖到画布指定位置">图片</button>
            <button type="button" on:click={duplicateSelected} disabled={!selectedAsset}>复制</button>
            <button type="button" class="warn" on:click={() => modalMode = "deleteAsset"} disabled={!selectedAsset}>删除</button>
          </div>
        {/if}
      </section>

      <section class="section">
        <h2>当前状态</h2>
          <div class="restore-grid compact">
          <div><strong>{assets.length}</strong><span>元素</span></div>
          <div><strong>{blockingBuildIssues.length}</strong><span>阻塞</span></div>
        </div>
        <div class="toolbar-row">
          <button type="button" on:click={() => modalMode = "buildReport"}>质量检查</button>
        </div>
      </section>
    </aside>
  </div>

  <footer class="status-bar" aria-label="当前状态">
    <span>{projectName}</span>
    <span>{selectedScreen ? selectedScreen.name : "未选择界面"}</span>
    <span>{activeStageInfo.title}</span>
    <span>{screenDebugSummary}</span>
    {#if cloudStatus}<span>{cloudStatus}</span>{/if}
  </footer>

  {#if modalMode !== "none"}
    <div class="modal-backdrop" role="presentation">
      <section class:wide-modal={modalMode === "figmaImport"} class="modal" role="dialog" aria-modal="true">
        {#if modalMode === "importMenu"}
          <header>
            <h2>导入</h2>
            <button type="button" on:click={() => modalMode = "none"}>关闭</button>
          </header>
          <div class="modal-body">
            <div class="import-choice-list">
              <button type="button" class="import-choice" on:click={openFigmaImport}>
                <strong>Figma 导入</strong>
                <span>填写 Token 和链接，解析后预览全部页面与节点，再选择部分或全部导入。</span>
              </button>
              <label class="import-choice file-choice">
                <strong>导入项目备份</strong>
                <span>恢复 ArtFlow 保存的项目、界面列表、元素和输出设置。</span>
                <input type="file" accept="application/json,.json" on:change={importManifest}>
              </label>
              <label class="import-choice file-choice">
                <strong>导入成稿图</strong>
                <span>把当前界面的 AI 成稿或参考图叠到画布上，用于后续切图。</span>
                <input type="file" accept="image/*" on:change={onImageImport}>
              </label>
            </div>
          </div>
          <footer><button type="button" class="primary" on:click={() => modalMode = "none"}>完成</button></footer>
        {:else if modalMode === "figmaImport"}
          <div class="figma-workspace">
            <aside class="figma-rail">
              <div class="figma-logo">A</div>
              <button type="button" class="active" title="导入">I</button>
            </aside>
            <aside class="figma-left-panel">
              <header class="figma-file-head">
                <strong>Figma 导入</strong>
                <span>{figmaResult?.name ?? "未解析"}</span>
                <button type="button" on:click={() => modalMode = "none"} title="关闭">×</button>
              </header>
              <section class="figma-import-controls">
                <label for="figma-dialog-token">Figma Token</label>
                <input id="figma-dialog-token" type="password" placeholder="粘贴 Figma Personal Access Token" bind:value={figmaToken} on:input={updateFigmaTokenInput}>
                <div class="toolbar-row">
                  <button type="button" on:click={saveFigmaToken}>保存</button>
                  <button type="button" on:click={clearFigmaToken}>清除</button>
                </div>
                <label for="figma-dialog-url">文件链接</label>
                <input id="figma-dialog-url" placeholder="https://www.figma.com/design/..." bind:value={figmaUrl} on:input={updateFigmaUrlInput}>
                <div class="figma-scope-control" aria-label="Figma 解析范围">
                  <button type="button" class:active={!figmaParseWholeFile} on:click={() => setFigmaParseWholeFile(false)} disabled={!figmaInfo.nodeId}>链接节点</button>
                  <button type="button" class:active={figmaParseWholeFile || !figmaInfo.nodeId} on:click={() => setFigmaParseWholeFile(true)}>整文件</button>
                  <span>{figmaInfo.nodeId && !figmaParseWholeFile ? `node-id ${figmaInfo.nodeId}` : "解析全部页面"}</span>
                </div>
                <div class="toolbar-row">
                  <button type="button" class="primary" on:click={parseFigmaFile} disabled={figmaParsing}>{figmaParsing ? "解析中" : "解析文件"}</button>
                  <button type="button" on:click={selectTopFigmaFrames} disabled={!figmaFrames.length}>顶层</button>
                  <button type="button" on:click={selectAllFigmaFrames} disabled={!figmaFrames.length}>全选</button>
                  <button type="button" on:click={clearFigmaSelection} disabled={!figmaFrames.length}>清空</button>
                  <button type="button" on:click={clearStoredFigmaImport} disabled={!figmaResult && !figmaFrames.length}>清空解析</button>
                </div>
                {#if figmaResult?.error}
                  <p class="figma-error">{figmaResult.error}</p>
                {/if}
              </section>
              <section class="figma-pages">
                <h3>页面</h3>
                {#if figmaParsedReady && figmaResult?.pages?.length}
                  {#each figmaResult.pages as page}
                    <button type="button" class:active={page.name === figmaActivePage} on:click={() => setFigmaPage(page.name)}>{page.name}</button>
                  {/each}
                {:else}
                  <p class="muted">解析后显示页面</p>
                {/if}
              </section>
              <section class="figma-layers">
                <h3>图层</h3>
                <div class="figma-layer-list">
                  {#if figmaParsedReady && figmaPreviewFrame}
                    <button
                      type="button"
                      class:active={true}
                      style="--indent: 0px"
                      on:click={() => selectFigmaFrame(figmaPreviewFrame.id)}
                    >
                      <span>⌗</span>
                      <strong>{figmaPreviewFrame.name}</strong>
                    </button>
                    {#each figmaLayerTreeRows(figmaPreviewFrame) as row}
                      <div class="figma-layer-row">
                        <button
                          type="button"
                          class:active={figmaPreviewFrameId === row.frame.id}
                          style={`--indent:${row.depth * 16}px`}
                          on:mouseenter={() => previewFigmaFrame(row.frame.id)}
                          on:focus={() => previewFigmaFrame(row.frame.id)}
                          on:click={() => selectFigmaFrame(row.frame.id)}
                        >
                          <span>{figmaLayerIcon(row.frame)}</span>
                          <strong>{row.frame.name}</strong>
                        </button>
                        <button type="button" class="figma-layer-add" on:click={(event) => addAssetFromFigma(row.frame, event)} disabled={!selectedScreen}>加入</button>
                      </div>
                    {/each}
                  {:else}
                    <p class="muted">解析后显示图层树</p>
                  {/if}
                </div>
              </section>
            </aside>
            <main class="figma-canvas-area">
              <div class="figma-canvas-toolbar">
                <div>
                  <strong>{figmaPreviewFrame?.name ?? "Figma 导入"}</strong>
                  <span>{figmaPreviewFrame?.box ? `${figmaPreviewFrame.box.w}x${figmaPreviewFrame.box.h}` : "解析后显示画板"}</span>
                </div>
                <div class="figma-toolbar-actions">
                  <label class="check figma-overlay-toggle">
                    <input type="checkbox" bind:checked={figmaShowParsedOverlay} on:change={persistFigmaImportState}>
                    解析层
                  </label>
                  <button type="button" on:click={() => setFigmaCanvasZoom(figmaCanvasZoom - 0.1)} disabled={!figmaFrames.length || figmaCanvasZoom <= 0.25}>-</button>
                  <button type="button" on:click={resetFigmaCanvasView} disabled={!figmaFrames.length}>{Math.round(figmaCanvasZoom * 100)}%</button>
                  <button type="button" on:click={() => setFigmaCanvasZoom(figmaCanvasZoom + 0.1)} disabled={!figmaFrames.length || figmaCanvasZoom >= 3}>+</button>
                  <button type="button" on:click={importSelectedFigmaToCurrentCanvas} disabled={!figmaParsedReady || !figmaSelectedFrameIds.length}>导入当前画板</button>
                  <button type="button" class="primary" on:click={importSelectedFigmaAsScreens} disabled={!figmaParsedReady || !figmaSelectedFrameIds.length}>导入为界面</button>
                </div>
              </div>
              <div
                class:dragging={figmaCanvasPanning}
                class="figma-import-canvas"
                role="application"
                aria-label="Figma 导入画布"
                on:wheel|preventDefault={zoomFigmaCanvas}
                on:pointerdown={startFigmaCanvasPan}
                on:pointermove={moveFigmaCanvasPan}
                on:pointerup={stopFigmaCanvasPan}
                on:pointercancel={stopFigmaCanvasPan}
              >
                {#if figmaParsing}
                  <div class="figma-loading-preview" role="status" aria-live="polite">
                    <strong>正在解析 Figma 文件</strong>
                    <span>读取页面、画板和图层结构<span class="loading-dots"></span></span>
                  </div>
                {:else if figmaPageTopFrames.length}
                  <div class="figma-canvas-world" style={figmaCanvasWorldStyleValue}>
                    {#each figmaPageTopFrames as frame}
                      {#if frame.box}
                        <div
                          class:selected={figmaSelectedFrameIds.includes(frame.id)}
                          class:active={figmaPreviewFrame?.id === frame.id}
                          class="figma-canvas-frame"
                          role="button"
                          tabindex="0"
                          aria-label={frame.name}
                          style={figmaCanvasBoardStyle(frame)}
                          on:mouseenter={() => previewFigmaFrame(frame.id)}
                          on:focusin={() => previewFigmaFrame(frame.id)}
                          on:keydown={(event) => handleFigmaFrameKeydown(event, frame.id)}
                          on:click|stopPropagation={() => previewFigmaFrame(frame.id)}
                        >
                          <label class="figma-frame-check">
                            <input type="checkbox" checked={figmaSelectedFrameIds.includes(frame.id)} on:pointerdown|stopPropagation on:click|stopPropagation on:change={() => toggleFigmaFrame(frame.id)}>
                            <span>{frame.name}</span>
                          </label>
                          <div class:show-overlay={figmaShowParsedOverlay} class="figma-preview-board figma-canvas-board">
                            {#if figmaRenderImages[frame.id]}
                              <img class="figma-official-render" src={figmaRenderImages[frame.id]} alt={`${frame.name} Figma 官方渲染`}>
                            {/if}
                            {#each figmaChildrenForScreen(frame).filter((child) => child.id !== frame.id) as child}
                              {#if child.type === "TEXT"}
                                <button type="button" class="figma-preview-node text" class:selected={figmaPreviewFrameId === child.id} style={figmaCanvasNodeStyle(child, frame)} title={child.name} on:click|stopPropagation={(event) => selectFigmaFrame(child.id, event.shiftKey || event.metaKey || event.ctrlKey)}>{child.text}</button>
                              {:else}
                                <button type="button" class={`figma-preview-node ${figmaFrameType(child)} ${child.type.toLowerCase()} ${figmaPreviewFrameId === child.id ? "selected" : ""}`} style={figmaCanvasNodeStyle(child, frame)} title={child.name} aria-label={child.name} on:click|stopPropagation={(event) => selectFigmaFrame(child.id, event.shiftKey || event.metaKey || event.ctrlKey)}></button>
                              {/if}
                            {/each}
                          </div>
                          <footer>
                            <span>{frame.box.w}x{frame.box.h}</span>
                            <span>{figmaChildrenForScreen(frame).length} 节点</span>
                          </footer>
                        </div>
                      {/if}
                    {/each}
                  </div>
                {:else}
                  <div class="figma-empty-preview">解析后按页面显示全部画板，滚轮缩放，按住空白区域拖动。</div>
                {/if}
              </div>
            </main>
          </div>
        {:else if modalMode === "projectSettings"}
          <header>
            <h2>项目设置</h2>
            <button type="button" on:click={() => modalMode = "none"}>关闭</button>
          </header>
          <div class="modal-body">
            <div class="field">
              <label for="project-settings-name">目标项目</label>
              <input id="project-settings-name" bind:value={projectName} placeholder="输入项目名称">
            </div>
            {#if !selectedCloudProjectId}
              <div class="field">
                <label for="project-create-name">新建独立项目</label>
                <input id="project-create-name" bind:value={newProjectTitle} placeholder="例如：自然国度 UI 资源链">
              </div>
              <div class="toolbar-row">
                <button type="button" class="primary" on:click={createCloudProject} disabled={cloudLoading}>创建并保存项目</button>
              </div>
            {/if}
            <div class="field">
              <label for="project-settings-output">游戏资源输出路径</label>
              <input id="project-settings-output" bind:value={outputRoot}>
            </div>
            <div class="field">
              <strong class="field-label">项目预设</strong>
              <div class="preset-list">
                {#each projectPresets as preset}
                  <button
                    type="button"
                    class:active={projectConfig.preset === preset.id}
                    on:click={() => applyProjectPreset(preset)}
                  >
                    <strong>{preset.name}</strong>
                    <span>{preset.description}</span>
                    <small>{preset.outputRoot}</small>
                  </button>
                {/each}
              </div>
            </div>
            <div class="field">
              <label for="project-settings-style">项目默认风格参考</label>
              <input id="project-settings-style" bind:value={styleRef} on:input={updateProjectStyleRef}>
            </div>
            <div class="field">
              <label for="project-settings-note">项目说明</label>
              <p id="project-settings-note" class="hint">这里维护全局默认值；每个 UI 界面的尺寸、线框来源和提示词仍在界面列表中单独维护。</p>
            </div>
            <div class="toolbar-row">
              <button type="button" on:click={() => modalMode = "help"}>工作流说明</button>
              <button type="button" on:click={() => modalMode = "buildReport"}>构建报告</button>
            </div>
          </div>
          <footer>
            <button type="button" on:click={() => modalMode = "none"}>关闭</button>
            <button type="button" class="primary" on:click={() => { void saveCloudProject(); modalMode = "none"; }}>保存项目</button>
          </footer>
        {:else if modalMode === "memberAdmin"}
          <header>
            <h2>人员管理</h2>
            <button type="button" on:click={() => modalMode = "none"}>关闭</button>
          </header>
          <div class="modal-body member-admin">
            {#if adminUserError}<p class="login-error">{adminUserError}</p>{/if}
            {#if authUser.role === "owner" || authUser.role === "admin"}
            <section>
              <h3>添加人员</h3>
              <div class="split">
                <div class="field"><label for="new-user-name">用户名</label><input id="new-user-name" bind:value={newUserName}></div>
                <div class="field"><label for="new-user-display">显示名</label><input id="new-user-display" bind:value={newUserDisplayName}></div>
              </div>
              <div class="split">
                <div class="field"><label for="new-user-password">初始密码</label><input id="new-user-password" type="password" bind:value={newUserPassword}></div>
                <div class="field"><label for="new-user-role">角色</label><select id="new-user-role" bind:value={newUserRole}><option value="member">成员</option><option value="admin">管理员</option></select></div>
              </div>
              <div class="toolbar-row"><button type="button" class="primary" on:click={createAdminUser} disabled={adminUserLoading}>添加人员</button></div>
            </section>
            {/if}
            <section>
              <h3>项目协作人员</h3>
              <p class="hint">项目：{cloudProjects.find((item) => item.id === selectedCloudProjectId)?.name ?? selectedCloudProjectId}</p>
              <div class="member-list">
                {#each adminUsers as item}
                  <label class="member-row">
                    <input type="checkbox" checked={selectedProjectMemberIds.includes(item.id)} on:change={() => toggleProjectMember(item.id)}>
                    <span><strong>{item.displayName}</strong><small>{item.username} · {item.role}</small></span>
                  </label>
                {/each}
              </div>
            </section>
          </div>
          <footer>
            <button type="button" on:click={() => modalMode = "none"}>关闭</button>
            <button type="button" class="primary" on:click={saveProjectMembers} disabled={adminUserLoading || !selectedCloudProjectId}>保存项目人员</button>
          </footer>
        {:else if modalMode === "addAsset"}
          <header>
            <h2>新增元素</h2>
            <button type="button" on:click={() => modalMode = "none"}>关闭</button>
          </header>
          <div class="modal-body">
            <div class="field"><label for="new-id">assetId</label><input id="new-id" bind:value={newAsset.assetId}></div>
            <div class="split">
              <div class="field"><label for="new-type">类型</label><select id="new-type" bind:value={newAsset.type}><option>panel</option><option>button</option><option>icon</option><option>image</option></select></div>
              <div class="field"><label for="new-status">状态</label><input id="new-status" bind:value={newAsset.status}></div>
            </div>
            <div class="split">
              <div class="field"><label for="new-x">x</label><input id="new-x" type="number" bind:value={newAsset.rect.x}></div>
              <div class="field"><label for="new-y">y</label><input id="new-y" type="number" bind:value={newAsset.rect.y}></div>
              <div class="field"><label for="new-w">w</label><input id="new-w" type="number" bind:value={newAsset.rect.w}></div>
              <div class="field"><label for="new-h">h</label><input id="new-h" type="number" bind:value={newAsset.rect.h}></div>
            </div>
            <div class="field"><label for="new-atlas">atlas</label><input id="new-atlas" bind:value={newAsset.atlas}></div>
            <div class="field"><label for="new-path">finalPath</label><input id="new-path" bind:value={newAsset.finalPath}></div>
          </div>
          <footer>
            <button type="button" on:click={() => modalMode = "none"}>取消</button>
            <button type="button" class="primary" on:click={addAsset}>新增</button>
          </footer>
        {:else if modalMode === "deleteAsset"}
          <header>
            <h2>确认删除</h2>
            <button type="button" on:click={() => modalMode = "none"}>关闭</button>
          </header>
          <div class="modal-body">
            <p>{selectedAsset ? `将删除元素 ${selectedAsset.assetId}。这只会修改当前 Web 工作台状态，不会删除项目文件。` : "当前没有选中元素。"}</p>
          </div>
          <footer>
            <button type="button" on:click={() => modalMode = "none"}>取消</button>
            <button type="button" class="warn" on:click={deleteSelected}>确认删除</button>
          </footer>
        {:else if modalMode === "deleteScreen"}
          <header>
            <h2>确认删除界面</h2>
            <button type="button" on:click={() => { pendingDeleteScreenIds = []; modalMode = "none"; }}>关闭</button>
          </header>
          <div class="modal-body">
            {#if pendingDeleteScreens.length === 1}
              <p>将删除界面“{pendingDeleteScreens[0].name}”。这只会从当前 ArtFlow 项目中移除，不会改动游戏工程。</p>
            {:else if pendingDeleteScreens.length > 1}
              <p>将批量删除 {pendingDeleteScreens.length} 个界面。这只会从当前 ArtFlow 项目中移除，不会改动游戏工程。</p>
              <div class="delete-screen-list">
                {#each pendingDeleteScreens as screen}
                  <span>{screen.name}</span>
                {/each}
              </div>
            {:else}
              <p>当前没有选中界面。</p>
            {/if}
          </div>
          <footer>
            <button type="button" on:click={() => { pendingDeleteScreenIds = []; modalMode = "none"; }}>取消</button>
            <button type="button" class="warn" on:click={confirmDeleteScreens} disabled={!pendingDeleteScreens.length}>确认删除</button>
          </footer>
        {:else if modalMode === "extractRefine"}
          <header>
            <h2>提示词二次处理</h2>
            <button type="button" on:click={() => modalMode = "none"} disabled={refineLoading}>关闭</button>
          </header>
          <div class="modal-body">
            <p>对元素 <code>{refineTargetAssetId}</code> 当前的提取结果做云端重绘，结果会替换该元素预览并直接用于导出。</p>
            {#if extractEntries[refineTargetAssetId]?.cutoutDataUrl}
              <div class="extract-cutout wide">
                <img src={extractEntries[refineTargetAssetId].cutoutDataUrl} alt={refineTargetAssetId}>
              </div>
            {/if}
            <div class="field">
              <label for="refine-prompt-text">修改提示词</label>
              <textarea
                id="refine-prompt-text"
                bind:value={refinePromptText}
                placeholder="例如：把边缘毛刺修干净，保持原有配色、高光和透明背景"
              ></textarea>
            </div>
            <div class="api-result warn compact">
              <strong>计费提示</strong>
              <p>本次调用将产生 AI 生成费用（云端 images/edits 接口）。点「确认费用并处理」后才会发起请求。</p>
            </div>
          </div>
          <footer>
            <button type="button" on:click={() => modalMode = "none"} disabled={refineLoading}>取消</button>
            <button type="button" class="warn" on:click={submitRefinePrompt} disabled={refineLoading || !refinePromptText.trim()}>
              {refineLoading ? "处理中…" : "确认费用并处理"}
            </button>
          </footer>
        {:else if modalMode === "fileConflict"}
          <header>
            <h2>覆盖冲突确认</h2>
            <button type="button" on:click={cancelFileConflicts}>关闭</button>
          </header>
          <div class="modal-body">
            {#if pendingDryRunPlan && dryRunConflictEntries.length}
              <div class="plan-summary">
                <div><strong>{pendingDryRunPlan.summary.write}</strong><span>将写入</span></div>
                <div><strong>{pendingDryRunPlan.summary.overwrite}</strong><span>将覆盖</span></div>
                <div><strong>{pendingDryRunPlan.summary.skip}</strong><span>跳过</span></div>
              </div>
              <p>以下文件已存在。取消不会写入任何文件；确认后仅记录覆盖许可，真实写入将在后续写入步骤中执行。</p>
              <ul class="conflict-list">
                {#each dryRunConflictEntries as entry}
                  <li>
                    <strong>{entry.path}</strong>
                    <span>{entry.assetId || entry.kind}{entry.size ? ` · ${entry.size} bytes` : ""}</span>
                  </li>
                {/each}
              </ul>
            {:else}
              <p>当前预检没有覆盖冲突。</p>
            {/if}
          </div>
          <footer>
            <button type="button" on:click={cancelFileConflicts}>取消写入</button>
            <button type="button" class="warn" on:click={confirmFileConflicts} disabled={!pendingDryRunPlan || !dryRunConflictEntries.length}>确认覆盖</button>
          </footer>
        {:else if modalMode === "gameSync"}
          <header>
            <h2>同步到游戏</h2>
            <button type="button" on:click={() => modalMode = "none"}>关闭</button>
          </header>
          <div class="modal-body">
            {#if gameSyncError}
              <div class="api-result warn compact">
                <strong>{gameSyncDone ? "同步失败" : "无法同步"}</strong>
                <p>{gameSyncError}</p>
              </div>
            {/if}
            {#if gameSyncPlan}
              <div class="plan-summary">
                {#if gameSyncDone}
                  <div><strong>{gameSyncPlan.summary.written ?? 0}</strong><span>已同步</span></div>
                  <div><strong>{gameSyncPlan.summary.skipped ?? 0}</strong><span>跳过</span></div>
                {:else}
                  <div><strong>{gameSyncPlan.summary.write ?? 0}</strong><span>将写入</span></div>
                  <div><strong>{gameSyncPlan.summary.overwrite ?? 0}</strong><span>将覆盖</span></div>
                  <div><strong>{gameSyncPlan.summary.skip ?? 0}</strong><span>跳过</span></div>
                {/if}
              </div>
              {#if gameSyncPlan.gameRoot}
                <p class="hint">目标游戏目录：<code>{gameSyncPlan.gameRoot}</code></p>
              {/if}
              {#if gameSyncMessage}
                <p>{gameSyncMessage}</p>
              {/if}
              {#if !gameSyncDone}
                <p>这会把 staging 输出复制到真实游戏工程目录。覆盖项会替换游戏侧已有文件，请确认后再执行。真实写入需要后端开启 <code>ARTFLOW_ALLOW_GAME_WRITE=1</code>。</p>
              {/if}
              <ul class="conflict-list">
                {#each gameSyncPlan.entries as entry}
                  <li>
                    <strong>{entry.path} → {entry.targetPath || "-"}</strong>
                    <span>
                      {entry.action === "overwrite" ? "覆盖" : entry.action === "write" ? "写入" : entry.action === "written" ? "已写入" : "跳过"}{entry.assetId ? ` · ${entry.assetId}` : entry.kind ? ` · ${entry.kind}` : ""}{entry.reason ? ` · ${entry.reason}` : ""}{entry.size ? ` · ${entry.size} bytes` : ""}
                    </span>
                  </li>
                {/each}
              </ul>
            {:else if !gameSyncError}
              <p>没有可同步的文件。请先「输出到 Staging」。</p>
            {/if}
          </div>
          <footer>
            {#if gameSyncDone}
              <button type="button" class="primary" on:click={() => modalMode = "none"}>完成</button>
            {:else}
              <button type="button" on:click={() => modalMode = "none"}>取消</button>
              <button
                type="button"
                class="warn"
                on:click={confirmGameSync}
                disabled={gameSyncLoading || !gameSyncPlan || ((gameSyncPlan.summary.write ?? 0) + (gameSyncPlan.summary.overwrite ?? 0)) === 0}
              >
                {gameSyncLoading ? "同步中" : "确认同步到游戏"}
              </button>
            {/if}
          </footer>
        {:else if modalMode === "referenceAssets"}
          <header>
            <h2>参考资产管理</h2>
            <button type="button" on:click={() => modalMode = "none"}>关闭</button>
          </header>
          <div class="modal-body">
            <div class="segmented-control wide" aria-label="参考资产作用域">
              <button type="button" class:active={referenceScope === "project"} on:click={() => referenceScope = "project"}>项目级</button>
              <button type="button" class:active={referenceScope === "screen"} on:click={() => { if (selectedScreen) referenceScope = "screen"; }} disabled={!selectedScreen}>当前界面</button>
            </div>
            <p class="hint">
              {referenceScope === "project"
                ? "项目级参考资产是所有界面的默认值。"
                : `当前界面「${selectedScreen?.name ?? ""}」的参考资产，会覆盖项目级；成稿图会自动作为参考图。`}
            </p>

            <div class="ref-text-grid">
              <div class="field">
                <label for="ref-style">风格参考</label>
                <input id="ref-style" value={activeReferenceSet.style} on:input={(e) => updateReferenceField("style", (e.currentTarget as HTMLInputElement).value)} placeholder="风格描述或参考来源">
              </div>
              <div class="field">
                <label for="ref-layout">线框参考</label>
                <input id="ref-layout" value={activeReferenceSet.layout} on:input={(e) => updateReferenceField("layout", (e.currentTarget as HTMLInputElement).value)} placeholder="布局来源">
              </div>
            </div>
            <div class="field">
              <label for="ref-prompt">提示词</label>
              <textarea id="ref-prompt" rows="2" value={activeReferenceSet.prompt} on:input={(e) => updateReferenceField("prompt", (e.currentTarget as HTMLTextAreaElement).value)} placeholder="给 AI 的正向提示词"></textarea>
            </div>
            <div class="field">
              <label for="ref-negative">负面提示词</label>
              <textarea id="ref-negative" rows="2" value={activeReferenceSet.negative} on:input={(e) => updateReferenceField("negative", (e.currentTarget as HTMLTextAreaElement).value)} placeholder="不希望出现的内容"></textarea>
            </div>

            <div class="ref-images">
              <div class="ref-images-head">
                <strong>参考图（{activeReferenceSet.images.length}）</strong>
                <span>顺序即优先级，可上移/下移</span>
              </div>
              {#if !activeReferenceSet.images.length}
                <div class="empty-panel">还没有参考图。用下方添加 URL 或上传本地图片。</div>
              {:else}
                <ul class="ref-image-list">
                  {#each activeReferenceSet.images as image, index (image.id)}
                    <li class="ref-image-item">
                      <div class="ref-image-thumb">
                        {#if image.url}<img src={image.url} alt={image.label} on:error={(e) => (e.currentTarget as HTMLImageElement).style.opacity = "0.2"}>{/if}
                      </div>
                      <div class="ref-image-fields">
                        <input class="ref-image-label" value={image.label} on:input={(e) => updateReferenceImage(index, { label: (e.currentTarget as HTMLInputElement).value })} placeholder="标签">
                        <select value={image.role} on:change={(e) => updateReferenceImage(index, { role: (e.currentTarget as HTMLSelectElement).value as ReferenceImage["role"] })}>
                          <option value="reference">参考</option>
                          <option value="style">风格</option>
                          <option value="layout">布局</option>
                        </select>
                      </div>
                      <div class="ref-image-actions">
                        <button type="button" on:click={() => moveReferenceImage(index, -1)} disabled={index === 0} title="上移">↑</button>
                        <button type="button" on:click={() => moveReferenceImage(index, 1)} disabled={index === activeReferenceSet.images.length - 1} title="下移">↓</button>
                        <button type="button" class="warn" on:click={() => deleteReferenceImage(index)} title="删除">✕</button>
                      </div>
                    </li>
                  {/each}
                </ul>
              {/if}
              <div class="ref-add-row">
                <input bind:value={newReferenceUrl} placeholder="参考图 URL（https:// 或 data:）">
                <input class="ref-add-label" bind:value={newReferenceLabel} placeholder="标签（可选）">
                <select bind:value={newReferenceRole}>
                  <option value="reference">参考</option>
                  <option value="style">风格</option>
                  <option value="layout">布局</option>
                </select>
                <button type="button" class="primary" on:click={addReferenceImageFromInput}>添加</button>
                <label class="file-button compact">
                  上传
                  <input type="file" accept="image/*" on:change={uploadReferenceImage}>
                </label>
              </div>
            </div>
          </div>
          <footer>
            <button type="button" on:click={() => exportReferenceJson(false)}>复制参考 JSON</button>
            <button type="button" on:click={() => exportReferenceJson(true)}>下载参考 JSON</button>
            <button type="button" class="primary" on:click={() => modalMode = "none"}>完成</button>
          </footer>
        {:else if modalMode === "buildReport"}
          <header>
            <h2>质量检查</h2>
            <button type="button" on:click={() => modalMode = "none"}>关闭</button>
          </header>
          <div class="modal-body">
            <div class="issue-summary">
              <div><strong>{buildIssues.length}</strong><span>全部问题</span></div>
              <div><strong>{blockingBuildIssues.length}</strong><span>阻塞错误</span></div>
              <div><strong>{warningBuildIssues.length}</strong><span>提示</span></div>
            </div>
            {#if buildIssues.length === 0}
              <p class="success-text">当前资源命名、尺寸和路径校验通过。</p>
            {:else}
              {#if blockingBuildIssues.length}
                <section class="issue-section" aria-label="阻塞错误">
                  <h3>阻塞错误</h3>
                  <ul class="issue-list">
                    {#each blockingBuildIssues as issue}
                      <li class="error-issue">
                        <button type="button" on:click={() => focusBuildIssue(issue)}>
                          <span>阻塞</span>
                          <strong>{issue.assetId}</strong>
                          <code>{issue.field}</code>
                          <small>{issue.screenName}</small>
                          <em>{issue.message}</em>
                        </button>
                      </li>
                    {/each}
                  </ul>
                </section>
              {/if}
              {#if warningBuildIssues.length}
                <section class="issue-section" aria-label="提示">
                  <h3>提示</h3>
                  <ul class="issue-list">
                    {#each warningBuildIssues as issue}
                      <li class="warning-issue">
                        <button type="button" on:click={() => focusBuildIssue(issue)}>
                          <span>提示</span>
                          <strong>{issue.assetId}</strong>
                          <code>{issue.field}</code>
                          <small>{issue.screenName}</small>
                          <em>{issue.message}</em>
                        </button>
                      </li>
                    {/each}
                  </ul>
                </section>
              {/if}
            {/if}
          </div>
          <footer><button type="button" class="primary" on:click={() => modalMode = "none"}>知道了</button></footer>
        {:else}
          <header>
            <h2>工作流说明</h2>
            <button type="button" on:click={() => modalMode = "none"}>关闭</button>
          </header>
          <div class="modal-body">
            <p>ArtFlow 将线框、AI 生成、美术切图、资源管理和项目协作串联成一条清晰的生产流水线。</p>
            <p>每个阶段只处理当前最重要的任务：先确定结构，再统一风格，随后提取资源，最后交付到项目中使用。</p>
          </div>
          <footer><button type="button" class="primary" on:click={() => modalMode = "none"}>开始使用</button></footer>
        {/if}
      </section>
    </div>
  {/if}
  {/if}
  {#if toast}
    <div class="toast">{toast}</div>
  {/if}
</div>
